/**
 * Playtest engine — spec-aligned phase machine, play handlers, combat
 * resolution, priority continuation, counter, tokens, and helpers.
 *
 * This is the new engine that drives the playtest UI per
 * PLAYTEST_EXTRACTION_SPEC.md §4-9. It coexists with the legacy `engine.ts`
 * (which the older tests/UI still call) and uses the dual-layer state model
 * defined in `src/types/game.ts`.
 *
 * Naming convention — this file uses descriptive names rather than the
 * minified bundle names (`gi`, `kl`, `eo`, `hf`, `Js`, `no`, …). A short
 * comment alongside each function references the original symbol.
 */
import type { Card } from "@/types/card";
import type { Deck } from "@/types/deck";
import type {
  GameState,
  PlayerState,
  ZoneCard,
  CombatSlot,
  AdMode,
  Buff,
  BuffEffect,
  BuffTrigger,
  PendingPlay,
  PlaytestPhase,
  PendingPrompt,
} from "@/types/game";
import { uid, safeNum } from "@/lib/utils";
import { setupPlayer as buildPlayerLegacy } from "./engine";
import { trace } from "./engineTrace";
import { evalMechanics, type MechanicResult } from "./rules/mechanics";
import { getCardHooks, getChampionHooksFor, getAnyHooksByName, type CardHookCtx, type CardHookResult } from "./cards/registry";
import { canonicalName } from "./nameMatcher";
import { hasKeyword, reflectAmount, disarmCount, perryPayload } from "./rules/keywords";
import { applyCounterGrants } from "./rules/counters";
import {
  applyEffectRow,
  emptyResult,
  isPopupTarget,
  isThisTarget,
  type EffectCtx,
  type EffectResult,
} from "./rules/effects";
import type {
  CounterRule,
  EffectRow,
  EffectTarget,
  ReactionTrigger,
} from "@/types/card";
import type { ActionChainEntry, ActionChainState, AttackPlaySnapshot, DeferredEffect } from "@/types/game";

// ─── helpers ──────────────────────────────────────────────────────────────

const opp = (n: 1 | 2): 1 | 2 => (n === 1 ? 2 : 1);

/**
 * Honor No Time-style attack-keyword silencing on a combat slot. If the
 * slot has `silenceAllAttackKeywords` ALL printed attack keywords on the
 * attacker's card are treated as absent (slot-granted keywords are NOT
 * silenced — those came from the player's own buffs/counters). Otherwise
 * an individual keyword may be in `silencedAttackKeywords`.
 */
function isAttackKeywordSilenced(
  slot: { silenceAllAttackKeywords?: boolean; silencedAttackKeywords?: string[] } | null | undefined,
  kw: string,
): boolean {
  if (!slot) return false;
  if (slot.silenceAllAttackKeywords) return true;
  const list = slot.silencedAttackKeywords ?? [];
  const lc = kw.toLowerCase();
  return list.some((x) => x.toLowerCase() === lc);
}

/**
 * True iff `c` carries the Quick keyword for attack purposes. Quick may live
 * in any of: generic `keywords`, the canonical `attackKeywords`, the legacy
 * `attackType` strings on A/D / equipment / sig / token cards, or in
 * slot-granted keywords from fired buffs (e.g. gain-quick). Centralizing the
 * lookup keeps Quick-gated effects (Ignite Blade, next-quick-attack triggers)
 * consistent with the Ignite Blade hand precondition check in playSig.
 */
function cardIsQuick(c: Card | null | undefined, slotGranted: string[] = []): boolean {
  if (!c) return false;
  if ((c.keywords ?? []).some((k) => k.toLowerCase() === "quick")) return true;
  if (hasKeyword(c.attackKeywords, "Quick")) return true;
  const at =
    (c.attackDefendFields?.attackType ?? "") +
    " " +
    (c.equipmentFields?.attackType ?? "") +
    " " +
    (c.signatureMoveFields?.attackType ?? "") +
    " " +
    (c.tokenFields?.attackType ?? "");
  if (/quick/i.test(at)) return true;
  if (slotGranted.some((k) => k.toLowerCase() === "quick")) return true;
  return false;
}

/**
 * Build the effective keyword list for buff-firing purposes. Returns the
 * card's generic `keywords`, plus "Quick" when `cardIsQuick` recognizes the
 * card via any of the legacy attackKeywords / attackType paths. This is what
 * `firePendingBuffs` should see so next-quick-attack triggers (Tensed Core,
 * Ignite Blade, etc.) reliably fire regardless of where Quick is stored.
 */
function effectiveKeywordsForBuffFire(c: Card | null | undefined): string[] {
  const base = c?.keywords ?? [];
  if (cardIsQuick(c) && !base.some((k) => k.toLowerCase() === "quick")) {
    return [...base, "Quick"];
  }
  return base;
}

export function pushLog(gs: GameState, player: string, message: string): GameState {
  return {
    ...gs,
    log: [...gs.log, { round: gs.round, player, message, ts: Date.now() }],
  };
}

// ─── Action Chain (general-purpose reaction window) ────────────────────

/** Empty action-chain state (chain inactive). */
function emptyActionChain(): ActionChainState {
  // originalActor is a placeholder when the chain is empty; pushActionChain
  // sets it to the real actor when the first entry is added.
  return { entries: [], reactor: null, originalActor: 1, consecutivePasses: 0 };
}

/** Read the current action chain (or an empty one if missing). */
function getActionChain(gs: GameState): ActionChainState {
  return gs.actionChain ?? emptyActionChain();
}

function setActionChain(gs: GameState, ac: ActionChainState | null): GameState {
  return { ...gs, actionChain: ac };
}

/**
 * For a given trigger event emitted by `actorNum`, scan `opponent`'s hand and
 * in-play utility zone for cards eligible to react. A card is eligible if:
 *   - It is a Utility with utilityType "Reaction", AND
 *   - Its `reactionTriggers` contains the event (or "any-priority-action").
 *
 * Phase 1 only inspects hand utilities. Future phases may also surface in-play
 * reaction-typed abilities and counter activations on AD cards in hand.
 */
function findEligibleReactors(
  gs: GameState,
  reactorNum: 1 | 2,
  event: ReactionTrigger,
  cardMap: Map<string, Card>,
): { instanceId: string; cardId: string }[] {
  const out: { instanceId: string; cardId: string }[] = [];
  const reactor = gs.players[reactorNum];
  for (const inst of reactor.handInstances) {
    const card = cardMap.get(inst.cardId);
    if (!card) continue;
    if (card.cardType !== "Utility") continue;
    const sub = (card.utilityFields?.utilityType ?? "").toLowerCase();
    if (sub !== "reaction") continue;
    const triggers = card.utilityFields?.reactionTriggers ?? [];
    if (
      triggers.includes(event) ||
      triggers.includes("any-priority-action")
    ) {
      out.push({ instanceId: inst.id, cardId: inst.cardId });
    }
  }
  return out;
}

/**
 * Push a reactable action onto the action chain and — if the opponent has any
 * eligible reactor cards — open the reaction window for them. If no eligible
 * reactor exists, the chain stays closed (entries persist for descriptive
 * logging until the chain naturally closes).
 *
 * Call this AFTER the action's effect has been applied to game state.
 */
export function pushActionChain(
  gs: GameState,
  entry: Omit<ActionChainEntry, "id">,
  event: ReactionTrigger,
  cardMap: Map<string, Card>,
): GameState {
  const chain = getActionChain(gs);
  const fullEntry: ActionChainEntry = {
    id: uid("ac_"),
    ...entry,
  };
  // Window ALWAYS opens for the opponent of the actor — we no longer gate
  // on eligible reactor cards. Opponent can pass to decline, and any new push
  // (LIFO chained reaction) resets the pass counter and bounces priority back.
  const reactorNum = opp(entry.playerNum);
  const eligibles = findEligibleReactors(gs, reactorNum, event, cardMap);
  const newEntries = [...chain.entries, fullEntry];
  const isFirstPush = chain.entries.length === 0;
  let next: GameState = setActionChain(gs, {
    entries: newEntries,
    reactor: reactorNum,
    // Track the bottom-of-stack actor for pass-resolution semantics.
    originalActor: isFirstPush ? entry.playerNum : chain.originalActor,
    // Any new push resets consecutive-pass count — reactions can chain.
    consecutivePasses: 0,
  });
  trace.emit({
    category: isFirstPush ? "chain.open" : "chain.event",
    label: event,
    summary: `Action Chain ${isFirstPush ? "open" : "event"}: ${entry.description} (reactor=P${reactorNum})`,
    round: gs.round,
    activePlayer: entry.playerNum,
    details: {
      actor: entry.playerNum,
      reactor: reactorNum,
      eligibleReactorCount: eligibles.length,
      event,
      entryDescription: entry.description,
      entryCardId: entry.cardId,
      chainLength: newEntries.length,
      reason: "pushActionChain",
      windowAlwaysOpen: true,
    },
  });
  next = pushLog(
    next,
    "system",
    `Action Chain — ${gs.players[reactorNum].name} may react to ${entry.description}`,
  );
  return next;
}

/**
 * Phase 2 — emit a combat / state-change event onto the Action Chain.
 *
 * Unlike `pushActionChain` (which assumes reactor = opp(actor)), this helper
 * lets the caller specify the reactor explicitly because trigger semantics
 * vary by event:
 *   - self-received-damage         → reactor is the damaged player
 *   - my-attack-dealt-damage       → reactor is the attacker
 *   - my-defend-full-blocked       → reactor is the defender
 *   - self-armor-gains-wear        → reactor is the player whose armor wore
 *   - opponent-armor-gains-wear    → reactor is the OPPOSITE player of the
 *                                    armor-bearer (they 'see' opponent armor wear)
 *   - opponent-draws-card          → reactor is the non-drawer
 *   - opponent-gains-stamina/life  → reactor is the non-gainer
 *
 * Eligibility is also widened: any card listing `any-priority-action` matches.
 */
export function emitActionChainEvent(
  gs: GameState,
  params: {
    actorNum: 1 | 2;
    reactorNum: 1 | 2;
    event: ReactionTrigger;
    description: string;
    cardId?: string;
    instanceId?: string;
  },
  cardMap: Map<string, Card>,
): GameState {
  // Don't emit if the game is over (resolveCombat may emit several events;
  // a fatal-damage attack should not open a chain on a finished game).
  if (gs.status === "finished" || gs.winner) return gs;

  const { actorNum, reactorNum, event, description, cardId, instanceId } = params;
  const reactor = gs.players[reactorNum];
  const eligibleAny = reactor.handInstances.some((inst) => {
    const c = cardMap.get(inst.cardId);
    if (!c || c.cardType !== "Utility") return false;
    if ((c.utilityFields?.utilityType ?? "").toLowerCase() !== "reaction") return false;
    const triggers = c.utilityFields?.reactionTriggers ?? [];
    return triggers.includes(event) || triggers.includes("any-priority-action");
  });
  const fullEntry: ActionChainEntry = {
    id: uid("ac_"),
    playerNum: actorNum,
    triggerEvent: event,
    cardId: cardId ?? "",
    instanceId: instanceId ?? "",
    description,
  };
  const chain = gs.actionChain ?? emptyActionChain();
  const isFirstPush = chain.entries.length === 0;
  const next: GameState = {
    ...gs,
    actionChain: {
      entries: [...chain.entries, fullEntry],
      // Always open the window for the proposed reactor (no eligibility gate).
      reactor: reactorNum,
      originalActor: isFirstPush ? actorNum : chain.originalActor,
      consecutivePasses: 0,
    },
  };
  trace.emit({
    category: "chain.event",
    label: event,
    summary: `Action Chain event: ${description} (reactor=P${reactorNum})`,
    round: gs.round,
    activePlayer: actorNum,
    details: {
      actor: actorNum,
      proposedReactor: reactorNum,
      finalReactor: reactorNum,
      eligibleAny,
      event,
      description,
      cardId,
      instanceId,
      reason: "emitActionChainEvent",
      windowAlwaysOpen: true,
    },
  });
  return pushLog(
    next,
    "system",
    `Action Chain — ${gs.players[reactorNum].name} may react to ${description}`,
  );
}

/**
 * Reactor passes. Single-pass-resolve semantics:
 *  - Pop the top of the stack and run its deferred effect (if any).
 *  - Priority returns to opp(top.playerNum) so they can react to the result.
 *  - If the stack is now empty, the chain closes.
 *
 * Entries without a `deferredEffect` are legacy descriptive events (e.g. push
 * from playAttackDefend in Phase A, or emitActionChainEvent informational
 * pushes). For those, popping the entry is the entire "resolution" — their
 * effects already applied at push time.
 */
export function passActionChain(gs: GameState, cardMap?: Map<string, Card>): GameState {
  const chain = getActionChain(gs);
  if (!chain.reactor) return gs;
  if (chain.entries.length === 0) {
    // Defensive — chain marked open with no entries. Just close it.
    return setActionChain(gs, null);
  }
  const passerNum = chain.reactor;
  const topIdx = chain.entries.length - 1;
  const topEntry = chain.entries[topIdx];
  const topActor = topEntry.playerNum;

  trace.emit({
    category: "chain.event",
    label: "pass-resolves-top",
    summary: `P${passerNum} passes — resolving top of stack (${topEntry.description})`,
    round: gs.round,
    activePlayer: passerNum,
    details: {
      passer: passerNum,
      topEntryId: topEntry.id,
      topActor,
      topDescription: topEntry.description,
      hasDeferredEffect: !!topEntry.deferredEffect,
      chainLengthBefore: chain.entries.length,
      reason: "single-pass-resolution",
    },
  });

  let next: GameState = pushLog(
    gs,
    gs.players[passerNum].name,
    `Stack — passed; resolving ${topEntry.description}`,
  );

  // Remove the top entry from the stack BEFORE running its effect. This way,
  // if the effect itself pushes another entry (rare), it stacks correctly.
  const remaining = chain.entries.slice(0, topIdx);
  next = setActionChain(next, {
    ...chain,
    entries: remaining,
    // While the effect runs, mark reactor null so isActionChainReactor doesn't
    // get queried mid-resolution. We'll set it correctly after.
    reactor: null,
    consecutivePasses: 0,
  });

  // Run the deferred effect (no-op for legacy entries without one).
  if (topEntry.deferredEffect && cardMap) {
    next = resolveStackEntry(next, topEntry, cardMap);
  }

  // After running the effect, decide chain state. Re-read because resolution
  // may have pushed new entries onto the chain (e.g. an effect with side
  // reaction triggers).
  const postChain = next.actionChain;
  if (!postChain || postChain.entries.length === 0) {
    // Stack fully resolved — close it.
    trace.emit({
      category: "chain.close",
      label: "stack-empty",
      summary: `Action Chain closed (stack empty after resolution)`,
      round: next.round,
      activePlayer: passerNum,
      details: { reason: "stack-fully-resolved", lastResolvedEntry: topEntry.id },
    });
    return setActionChain(next, null);
  }

  // Stack still has entries. Priority returns to opp(topActor) — the player
  // who pushed the just-resolved entry now sees the result and can react to
  // the next entry below, OR pass again to resolve it too.
  const nextReactor: 1 | 2 = opp(topActor);
  trace.emit({
    category: "chain.event",
    label: "resolved-priority-shift",
    summary: `P${topActor}'s entry resolved — priority to P${nextReactor}`,
    round: next.round,
    activePlayer: nextReactor,
    details: {
      resolvedActor: topActor,
      nextReactor,
      remainingEntries: postChain.entries.length,
      reason: "top-resolved-priority-bounces",
    },
  });
  return setActionChain(next, {
    ...postChain,
    reactor: nextReactor,
    consecutivePasses: 0,
  });
}

/**
 * Stack-entry resolver dispatcher. Reads `entry.deferredEffect.kind` and runs
 * the matching effect path. Called from passActionChain when an entry pops.
 *
 * Phase A: only "utility" and "negate" kinds exist. Phase B will add the rest.
 */
function resolveStackEntry(
  gs: GameState,
  entry: ActionChainEntry,
  cardMap: Map<string, Card>,
): GameState {
  const eff = entry.deferredEffect;
  if (!eff) return gs;
  const actor = entry.playerNum;

  switch (eff.kind) {
    case "utility":
      return resolveUtilityEffects(
        gs,
        actor,
        eff.cardId,
        eff.instanceId,
        eff.refundCost,
        cardMap,
      );
    case "negate":
      return resolveNegateEntry(gs, actor, entry, cardMap);
    case "attack-play":
      return resolveAttackPlay(gs, actor, eff.snapshot, cardMap);
    case "counter":
      return resolveCounterEffects(
        gs,
        actor,
        eff.cardId,
        eff.instanceId,
        cardMap,
      );
    case "sig-utility":
      return resolveSigUtilityEffects(
        gs,
        actor,
        eff.cardId,
        eff.instanceId,
        eff.sigSlotIdx,
        eff.refundCost,
        cardMap,
      );

    default: {
      // Exhaustiveness guard — if a new kind is added without a case here, TS
      // will narrow `eff` to `never` and the line below will error at compile.
      const _exhaustive: never = eff;
      void _exhaustive;
      return gs;
    }
  }
}

/**
 * Resolve a "negate" stack entry. Pops the most-recent opposing entry below
 * this one on the stack:
 *  - Refunds its stamina cost (from deferredEffect.refundCost).
 *  - Removes it from the stack so its effect never runs.
 *  - For deferred attack-play entries, also rolls back the push-time
 *    mutations that DID commit (equip slot tap, sig slot inPlay flag).
 *    Stamina refund + (skipped) buff fire + (skipped) CombatSlot creation
 *    are handled naturally — buffs were never consumed and no slot was
 *    created at push time, so there is nothing else to undo.
 *  - For deferred counter entries, the counter card stays in utilityInPlay
 *    per the "card stays where it was" design rule; only the stack entry
 *    is removed (resolver never runs, so rules never fire).
 *  - Refuses to negate `nonNegatable` entries (e.g. future reflect pushes).
 */
function resolveNegateEntry(
  gs: GameState,
  reactorNum: 1 | 2,
  negateEntry: ActionChainEntry,
  cardMap: Map<string, Card>,
): GameState {
  const chain = gs.actionChain;
  if (!chain) return pushLog(gs, "system", "Negate — no stack open.");
  const opponentNum = opp(reactorNum);
  // Find most-recent OPPOSING entry below the just-popped negate (the negate
  // was already removed from `chain.entries` before this resolver ran).
  let targetIdx = -1;
  for (let i = chain.entries.length - 1; i >= 0; i--) {
    if (chain.entries[i].playerNum !== opponentNum) continue;
    if (chain.entries[i].nonNegatable) continue;
    targetIdx = i;
    break;
  }
  if (targetIdx < 0) {
    return pushLog(gs, "system", "Negate — no opposing action to negate.");
  }
  const target = chain.entries[targetIdx];
  const targetCard = target.cardId ? cardMap.get(target.cardId) : undefined;

  const negatingCardId =
  negateEntry.deferredEffect?.kind === "negate"
    ? negateEntry.deferredEffect.negatingCardId
    : undefined;

  const negatingCard =
  negatingCardId ? cardMap.get(negatingCardId) : undefined;

  let refund = 0;
  if (target.deferredEffect && "refundCost" in target.deferredEffect) {
  refund = target.deferredEffect.refundCost;
  } else if (targetCard) {
  refund = staminaCostOf(targetCard);
  }

  const skipRefund =
  canonicalName(negatingCard?.name ?? "") === canonicalName("Reversal");

let next = gs;
if (refund > 0 && !skipRefund) {
  next = updatePlayer(next, opponentNum, (pl) => ({
    ...pl,
    stamina: Math.min(pl.maxStamina, pl.stamina + refund),
  }));

  trace.emit({
    category: "stamina",
    label: "refund",
    summary: `P${opponentNum} +${refund} stamina (Negate refund)`,
    round: gs.round,
    activePlayer: reactorNum,
    details: { playerNum: opponentNum, refund, negatedCard: targetCard?.name },
  });
}

  // Phase B: deferred attack-play entries committed limited push-time state
  // (equip slot tapped, sig slot inPlay). Roll those back so the player
  // could try a different action this round. Buffs were not consumed and no
  // CombatSlot was created — nothing to undo there.
  if (target.deferredEffect?.kind === "attack-play") {
    const snap = target.deferredEffect.snapshot;
    if (typeof snap.equipSlotIdx === "number") {
      const slotIdx = snap.equipSlotIdx;
      next = updatePlayer(next, opponentNum, (pl) => {
        const slots = pl.equipSlots.slice();
        if (slots[slotIdx]) {
          slots[slotIdx] = { ...slots[slotIdx], tapped: false };
        }
        return { ...pl, equipSlots: slots };
      });
      trace.emit({
        category: "status.exhaust",
        label: "weapon-untap-negate",
        summary: `P${opponentNum} equip slot ${slotIdx} untapped (attack negated)`,
        round: gs.round,
        activePlayer: reactorNum,
        details: { playerNum: opponentNum, slotIdx, reason: "stack-negate" },
      });
    }
    if (typeof snap.sigSlotIdx === "number") {
      const slotIdx = snap.sigSlotIdx;
      next = updatePlayer(next, opponentNum, (pl) => {
        const slots = pl.sigMoveSlots.slice();
        if (slots[slotIdx]) {
          slots[slotIdx] = { ...slots[slotIdx], inPlay: false };
        }
        return { ...pl, sigMoveSlots: slots };
      });
      trace.emit({
        category: "sig.play",
        label: "sig-rollback-negate",
        summary: `P${opponentNum} sig slot ${slotIdx} inPlay cleared (attack negated)`,
        round: gs.round,
        activePlayer: reactorNum,
        details: { playerNum: opponentNum, slotIdx, reason: "stack-negate" },
      });
    }
  }

  // Phase B: deferred sig-utility entries committed push-time state too
  // (stamina paid, slot.inPlay=true, copy added to utilityInPlay/playedCards).
  // Stamina is refunded above via the standard refundCost path; here we roll
  // back the slot flag and remove the copy from utilityInPlay/playedCards so
  // the player can attempt a different sig-slot play this round.
  if (target.deferredEffect?.kind === "sig-utility") {
    const slotIdx = target.deferredEffect.sigSlotIdx;
    const instanceId = target.deferredEffect.instanceId;
    const negatedCardId = target.deferredEffect.cardId;
    next = updatePlayer(next, opponentNum, (pl) => {
      const slots = pl.sigMoveSlots.slice();
      if (slots[slotIdx]) {
        slots[slotIdx] = { ...slots[slotIdx], inPlay: false };
      }
      // Remove the sig copy from utilityInPlay (matched by instanceId) and
      // strip the most recent matching playedCards entry (matched by cardId
      // — the played-zone uses a fresh uid that we don't track here).
      const played = (pl.playedCards ?? []).slice();
      for (let i = played.length - 1; i >= 0; i--) {
        if (played[i].cardId === negatedCardId) {
          played.splice(i, 1);
          break;
        }
      }
      return {
        ...pl,
        sigMoveSlots: slots,
        utilityInPlay: pl.utilityInPlay.filter((z) => z.id !== instanceId),
        playedCards: played,
      };
    });
    trace.emit({
      category: "sig.play",
      label: "sig-utility-rollback-negate",
      summary: `P${opponentNum} sig slot ${slotIdx} inPlay cleared (sig utility negated)`,
      round: gs.round,
      activePlayer: reactorNum,
      details: { playerNum: opponentNum, slotIdx, reason: "stack-negate", instanceId },
    });
  }

  // Remove target from the stack.
  const remaining = [
    ...chain.entries.slice(0, targetIdx),
    ...chain.entries.slice(targetIdx + 1),
  ];
  next = setActionChain(next, { ...chain, entries: remaining });

  trace.emit({
    category: "chain.event",
    label: "negate-resolved",
    summary: `Negate — ${target.description}`,
    round: gs.round,
    activePlayer: reactorNum,
    cardName: targetCard?.name,
    details: {
      reactorNum,
      negateEntryId: negateEntry.id,
      negatedEntryId: target.id,
      negatedDescription: target.description,
      negatedCardName: targetCard?.name,
      refund,
    },
  });
  next = pushLog(
    next,
    "system",
    `Negate — ${gs.players[opponentNum].name}'s ${targetCard?.name ?? target.description} cancelled${refund > 0 ? ` (+${refund} stamina refunded)` : ""}.`,
  );

  // Phase B fix: the Negate kind bypasses resolveUtilityEffects (which is
  // where onUtilityPlay normally fires). Cards like Pocket Sand still need
  // their card-specific effect (stun attacker + becomeAggressor) to run
  // AFTER the negate completes. Look up the negating card's onUtilityPlay
  // hook and invoke it here, mirroring the resolveUtilityEffects path.
  
  if (negatingCard) {
    const hooks = getCardHooks(negatingCard);
    if (hooks?.onUtilityPlay) {
      const ctx: CardHookCtx = {
        self: next.players[reactorNum],
        opponent: next.players[opp(reactorNum)],
        game: next,
        card: negatingCard,
        cardMap,
      };
      const r = hooks.onUtilityPlay(ctx) ?? {};
      trace.emit({
        category: "hook.fire",
        summary: `${negatingCard.name} onUtilityPlay returned (post-negate)`,
        details: { card: negatingCard.name, mode: "negate", result: r as unknown as Record<string, unknown> },
      });
      next = applyResults(next, reactorNum, [r], cardMap);
    }
  }

  return next;
}

/**
 * Resolve a deferred attack-play stack entry. Runs everything that the legacy
 * playAttackDefend / playEquip / playSig attack branches used to do at push
 * time:
 *   - Fire the pending buffs that were earmarked at push (consume from
 *     pendingBuffs by id).
 *   - Install the displayed power override on the combat-slot copy id.
 *   - Append a CombatSlot to combatSlots.
 *   - Bump attacksThisRound counters.
 *   - Flip priority to opp(actor) + subPhase "defender-pre-block".
 *   - Fire champion/armor onAttackPlay passives.
 *   - Run evalMechanics + onAttackPlay hooks.
 * The card has already been paid for, removed from hand (A/D) or its source
 * slot exhausted (equip/sig), and the in-zone copy already exists in
 * attackDefendZone. None of those mutations are touched here.
 */
function resolveAttackPlay(
  gs: GameState,
  actorNum: 1 | 2,
  snap: AttackPlaySnapshot,
  cardMap: Map<string, Card>,
): GameState {
  const card = cardMap.get(snap.cardId);
  if (!card) return gs;
  let next = gs;
  const mode: AdMode = "attack";

  // 1) Consume the snapshotted fired buffs from pendingBuffs by id, leaving
  //    any other buffs alone (other pushes between push and resolve are not
  //    expected, but the by-id remove is robust to them).
  const firedIds = new Set(snap.firedBuffIds);
  let firedBuffs: Buff[] = [];
  if (firedIds.size > 0) {
    const actorBuffs = next.players[actorNum].pendingBuffs;
    firedBuffs = actorBuffs.filter((b) => firedIds.has(b.id));
    const remaining = actorBuffs.filter((b) => !firedIds.has(b.id));
    next = updatePlayer(next, actorNum, (pl) => ({
      ...pl,
      pendingBuffs: remaining,
    }));
  }

  // 2) Install displayed power override on the combat-slot copy id.
  if (typeof snap.displayOverride === "number") {
    next = updatePlayer(next, actorNum, (pl) => {
      const ovr = new Map(pl.adValueOverrides);
      ovr.set(snap.copyInstanceId, snap.displayOverride!);
      return { ...pl, adValueOverrides: ovr };
    });
  }

  // 3) Build the CombatSlot and append.
  const csSlot: CombatSlot = {
    id: uid("cs_"),
    attackerNum: actorNum,
    attackCard: { id: snap.copyInstanceId, cardId: snap.cardId },
    blockCard: null,
    resolved: false,
    damageTaken: 0,
    attackGrantedKeywords:
      snap.grantedKeywords.length > 0 ? snap.grantedKeywords : undefined,
  };
  const isFirstAttack = (next.attacksThisRound ?? 0) === 0;
  if (isFirstAttack) {
    trace.emit({
      category: "combat.start",
      summary: `combat starts — P${actorNum} attacks with ${card.name}`,
      round: next.round,
      activePlayer: actorNum,
      details: {
        attacker: actorNum,
        attackCardId: card.id,
        attackCardName: card.name,
        source: `${snap.source}:resolveAttackPlay`,
      },
    });
  }
  trace.emit({
    category: "combat.slot.add",
    label: "stack-resolve",
    summary: `slot ${csSlot.id} added (P${actorNum} attack: ${card.name}) [via stack resolve]`,
    round: next.round,
    activePlayer: actorNum,
    details: {
      slotId: csSlot.id,
      attacker: actorNum,
      attackCardId: card.id,
      attackCardName: card.name,
      instanceId: snap.copyInstanceId,
      attackGrantedKeywords: csSlot.attackGrantedKeywords,
      source: snap.source,
      stackResolved: true,
    },
  });


  next = {
    ...next,
    players: {
      ...next.players,
      [actorNum]: {
        ...next.players[actorNum],
       // Only bump the streak on chain attacks; reset to 0 on a non-chain attack.
       attacksThisRound:(next.attacksThisRound ?? 0) + 1,
       consecutiveCounters: 0,
      },
    },
    combatSlots: [...(next.combatSlots ?? []), csSlot],
    defenderPassed: true,
    mustAttackWarning: false,
    attacksThisRound: (next.attacksThisRound ?? 0) + 1,
    priority: opp(actorNum),
    subPhase: "defender-pre-block",
  };

  // 4) Champion + armor onAttackPlay passives (e.g. Crixus, Bloodforged Plate).
  next = fireOnAttackPlayPassives(
    next,
    actorNum,
    card,
    cardMap,
    `${snap.source}:resolveAttackPlay`,
  );

  // Compute whether THIS attack has Chain. We defer the chainStreak update
  // until AFTER the per-card onAttackPlay hook fires (below) so cards like
  // Relentless End see the PRIOR streak via ctx.game.chainStreak.
  const attackHasChain = (card.keywords ?? []).some((k) => k.toLowerCase() === "chain") ||
    hasKeyword(card.attackKeywords, "Chain") ||
    (snap.grantedKeywords ?? []).some((k) => k.toLowerCase() === "chain");

  // 5) Data-driven mechanics + per-card onAttackPlay hook (mirrors the inline
  //    block that used to live in playAttackDefend / playSig).
  const mechRes = evalMechanics(card, mode, {
    self: next.players[actorNum],
    opponent: next.players[opp(actorNum)],
    cardName: card.name,
    currentRound: next.round,
    selfNum: actorNum,
  });
  next = applyResults(next, actorNum, [mechRes], cardMap);

  const hooks = getCardHooks(card);
  if (hooks?.onAttackPlay) {
    const ctx: CardHookCtx = {
      self: next.players[actorNum],
      opponent: next.players[opp(actorNum)],
      game: next,
      card,
      slotId: csSlot.id,
      instanceId: snap.copyInstanceId,
    };
    const r = hooks.onAttackPlay(ctx) ?? {};
    trace.emit({
      category: "hook.fire",
      summary: `${card.name} onAttackPlay returned (stack-resolve)`,
      details: {
        card: card.name,
        mode,
        stackResolved: true,
        result: r as unknown as Record<string, unknown>,
      },
    });
    next = applyResults(next, actorNum, [r], cardMap);

    // Hook-driven boosts to THIS attack's combat slot.
    // The slot we just pushed is the last one in next.combatSlots for this
    // actor's copyInstanceId (= snap.copyInstanceId). We bump its displayed
    // power via adValueOverrides AND/OR add keywords directly to the slot.
    const boost = r.boostCurrentAttackPower ?? 0;
    const grants = r.slotKeywordGrants ?? [];
    if (boost > 0 || grants.length > 0) {
      next = updatePlayer(next, actorNum, (pl) => {
        let ovr = pl.adValueOverrides;
        if (boost > 0) {
          const prevDisplay =
            pl.adValueOverrides.get(snap.copyInstanceId) ??
            safeNum(card.signatureMoveFields?.powerValue ?? card.attackDefendFields?.powerValue ?? 0);
          const newDisplay = Math.max(0, prevDisplay + boost);
          ovr = new Map(pl.adValueOverrides);
          ovr.set(snap.copyInstanceId, newDisplay);
          trace.emit({
            category: "power.boost",
            label: "hook-current-attack",
            summary: `${card.name} +${boost} power to current attack (display ${prevDisplay} → ${newDisplay})`,
            round: next.round,
            activePlayer: actorNum,
            details: {
              card: card.name,
              copyInstanceId: snap.copyInstanceId,
              boost,
              prevDisplay,
              newDisplay,
            },
          });
        }
        return { ...pl, adValueOverrides: ovr };
      });
      if (grants.length > 0) {
        // Merge keyword grants into the slot we just pushed. Find it by
        // copyInstanceId so we don't accidentally mutate a stale slot.
        next = {
          ...next,
          combatSlots: (next.combatSlots ?? []).map((s) =>
            s.attackCard.id === snap.copyInstanceId
              ? {
                  ...s,
                  attackGrantedKeywords: Array.from(
                    new Set([...(s.attackGrantedKeywords ?? []), ...grants]),
                  ),
                }
              : s,
          ),
        };
        trace.emit({
          category: "keyword.grant",
          label: "hook-current-attack",
          summary: `${card.name} +[${grants.join(", ")}] to current attack slot`,
          round: next.round,
          activePlayer: actorNum,
          details: { card: card.name, copyInstanceId: snap.copyInstanceId, grants },
        });
      }
    }
  }

  // 6) NOW update chainStreak. The per-card onAttackPlay hook above has
  //    already read the PRIOR value via ctx.game.chainStreak. Chain attacks
  //    extend the streak by 1; any non-chain attack resets it to 0.
  //    Aggressor changes (round start, becomeAggressor) reset elsewhere.
  next = {
    ...next,
    chainStreak: attackHasChain ? (next.chainStreak ?? 0) + 1 : 0,
  };

  trace.emit({
    category: "priority.shift",
    label: "to-defender-after-stack",
    summary: `priority P${actorNum} → P${opp(actorNum)} (defender pre-block) [after stack resolve]`,
    round: next.round,
    activePlayer: opp(actorNum),
    details: { from: actorNum, to: opp(actorNum), reason: "attack-resolved-from-stack" },
  });
  next = pushLog(
    next,
    "system",
    `🔄 ${card.name} resolved off the stack — Priority → ${next.players[opp(actorNum)].name}`,
  );

  void firedBuffs; // referenced for trace if needed in future
  return next;
}

/**
 * Resolve a deferred counter activation. The counter's `counterActivatedIds`
 * flag and zone moves already happened at push time; this runs the actual
 * counter-effect rows (buffs/debuffs/popup-overrides). If the counter has
 * the Negate keyword, the negate piece has been pushed as a separate
 * "negate" stack entry sibling so it can itself be reacted to — nothing
 * special to do here for that.
 */
function resolveCounterEffects(
  gs: GameState,
  actorNum: 1 | 2,
  cardId: string,
  instanceId: string,
  cardMap: Map<string, Card>,
): GameState {
  const card = cardMap.get(cardId);
  if (!card) return gs;
  let next = gs;
  const slot = findCounterTargetSlot(next, actorNum);
  const buffSummary: string[] = [];
  const rules = card.attackDefendFields?.counterRules ?? [];
  let matchedRules = 0;
  let firedEffects = 0;
  trace.emit({
    category: "counter.resolve",
    label: "stack-resolve",
    summary: `${card.name} counter resolves off stack (${rules.length} rule${rules.length === 1 ? "" : "s"})`,
    round: next.round,
    activePlayer: actorNum,
    details: {
      player: actorNum,
      cardName: card.name,
      instanceId,
      cardId,
      ruleCount: rules.length,
      slotId: slot?.id ?? null,
    },
  });

 

  for (const rule of rules) {
    if (!ruleMatchesSlot(rule, slot, actorNum, cardMap, next)) {
      trace.emit({
        category: "counter.fizzle",
        label: "rule-no-match-stack",
        summary: `${card.name} counter rule did not match slot (stack resolve)`,
        round: next.round,
        activePlayer: actorNum,
        details: { player: actorNum, cardName: card.name, ruleTrigger: rule.trigger, slotId: slot?.id ?? null },
      });
      continue;
    }
    matchedRules++;
    for (const row of rule.effects) {
      next = applyCounterEffectRow(next, actorNum, card.name, row, slot, cardMap, buffSummary);
      firedEffects++;
    }
  }
  trace.emit({
    category: "counter.resolve",
    label: "stack-done",
    summary: `${card.name} counter finished (${matchedRules}/${rules.length} rules matched, ${firedEffects} effects fired)`,
    round: next.round,
    activePlayer: actorNum,
    details: { player: actorNum, cardName: card.name, matchedRules, totalRules: rules.length, firedEffects, buffSummary },
  });

   next = updatePlayer(next, actorNum, (p) => ({
  ...p,
  consecutiveCounters: (p.consecutiveCounters ?? 0) + 1,
}));

  next = pushLog(next, gs.players[actorNum].name, `Counter resolved: ${card.name}`);
  return next;
}

/**
 * Predicate — is there an open Action Chain reaction window for player `n`?
 * UI uses this to decide whether to show the Pass/Respond overlay.
 */
export function isActionChainReactor(gs: GameState, n: 1 | 2): boolean {
  return gs.actionChain?.reactor === n;
}

/**
 * Apply a patch produced by mechanics or hooks to a single player.
 * Patches may reference Set/Map/array fields — we do shallow merge.
 */
function mergePatch(prev: PlayerState, patch: Partial<PlayerState>): PlayerState {
  return { ...prev, ...patch };
}

/**
 * Apply mechanic + hook results from one play to the game state.
 * - Self-patch goes to the actor.
 * - Opponent-patch goes to opp(actor).
 * - log lines pushed via pushLog.
 * - prompt (if any) set on pendingPrompt unless one already exists.
 */
interface ResultLike {
  selfPatch?: Partial<PlayerState>;
  opponentPatch?: Partial<PlayerState>;
  log?: string[];
  prompt?: PendingPrompt;
  focusGrant?: number;
  opponentFocusGrant?: number;
  drawSelf?: number;
  drawOpponent?: number;
  disarmOpponentEquipment?: { count?: number };
  queueSelfBuff?: Buff | Buff[];
  queueOpponentBuff?: Buff | Buff[];
  grantSelfEquipSlotKeyword?: { slotIdx: number; keyword: string };
  becomeAggressor?: boolean;
  negateInPlaySlot?: boolean;
  /** Return the just-played A/D card to the actor's hand. See
   *  CardHookResult.returnSelfToHand in registry.ts for the full contract. */
  returnSelfToHand?: {
    instanceId: string;
    mode: "attack" | "defend";
    flagKey: string;
  };
}

function applyResults(
  gs: GameState,
  actor: 1 | 2,
  results: Array<ResultLike>,
  cardMap?: Map<string, Card>,
): GameState {
  let next = gs;
  for (const r of results) {
    // Snapshot pre-apply stamina/health so we can detect gains for the
    // Action Chain reaction triggers (opponent-gains-stamina/life).
    const preSelfStamina = next.players[actor].stamina;
    const preSelfHealth = next.players[actor].health;
    const preOppStamina = next.players[opp(actor)].stamina;
    const preOppHealth = next.players[opp(actor)].health;

    // Pass 4: dispatch trace — enumerate which branches this result will hit.
    const branches: string[] = [];
    if (r.selfPatch && Object.keys(r.selfPatch).length > 0) branches.push("selfPatch");
    if (r.opponentPatch && Object.keys(r.opponentPatch).length > 0) branches.push("opponentPatch");
    if (r.log && r.log.length > 0) branches.push("log");
    if (r.prompt) branches.push("prompt");
    if (r.focusGrant) branches.push("focusGrant");
    if (r.opponentFocusGrant) branches.push("opponentFocusGrant");
    if (r.drawSelf) branches.push("drawSelf");
    if (r.drawOpponent) branches.push("drawOpponent");
    if (r.disarmOpponentEquipment) branches.push("disarmOpponentEquipment");
    if (r.queueSelfBuff) branches.push("queueSelfBuff");
    if (r.queueOpponentBuff) branches.push("queueOpponentBuff");
    if (r.grantSelfEquipSlotKeyword) branches.push("grantSelfEquipSlotKeyword");
    if (r.becomeAggressor) branches.push("becomeAggressor");
    if (r.negateInPlaySlot) branches.push("negateInPlaySlot");
    if (branches.length > 0) {
      trace.emit({
        category: "applyResults.dispatch",
        label: branches.join("+"),
        summary: `applyResults P${actor}: ${branches.join(", ")}`,
        round: next.round,
        activePlayer: actor,
        details: { actor, branches, result: r },
      });
    }

    if (r.selfPatch && Object.keys(r.selfPatch).length > 0) {
      next = updatePlayer(next, actor, (p) => mergePatch(p, r.selfPatch!));
    }
    if (r.opponentPatch && Object.keys(r.opponentPatch).length > 0) {
      next = updatePlayer(next, opp(actor), (p) => mergePatch(p, r.opponentPatch!));
    }
    if (r.log) {
      for (const line of r.log) {
        next = pushLog(next, next.players[actor].name, line);
      }
    }
    if (r.prompt && !next.pendingPrompt) {
      next = setPendingPrompt(next, r.prompt);
    }
    if (r.focusGrant && r.focusGrant > 0 && cardMap) {
      next = grantFocus(next, actor, r.focusGrant, cardMap);
    }
    if (r.opponentFocusGrant && r.opponentFocusGrant > 0 && cardMap) {
      next = grantFocus(next, opp(actor), r.opponentFocusGrant, cardMap);
    }
    if (r.drawSelf && r.drawSelf > 0) {
      next = drawCards(next, actor, r.drawSelf);
      // Action Chain: a card effect drew cards for `actor`. Reactor = opp(actor)
      // ("opponent-draws-card" from their POV).
      if (cardMap) {
        next = emitActionChainEvent(
          next,
          {
            actorNum: actor,
            reactorNum: opp(actor),
            event: "opponent-draws-card",
            description: `drew ${r.drawSelf} card${r.drawSelf === 1 ? "" : "s"}`,
          },
          cardMap,
        );
      }
    }
    if (r.drawOpponent && r.drawOpponent > 0) {
      next = drawCards(next, opp(actor), r.drawOpponent);
      if (cardMap) {
        next = emitActionChainEvent(
          next,
          {
            actorNum: actor,
            reactorNum: actor,
            event: "opponent-draws-card",
            description: `${next.players[opp(actor)].name} drew ${r.drawOpponent} card${r.drawOpponent === 1 ? "" : "s"}`,
          },
          cardMap,
        );
      }
    }
    if (r.queueSelfBuff) {
      const buffs = Array.isArray(r.queueSelfBuff) ? r.queueSelfBuff : [r.queueSelfBuff];
      for (const b of buffs) {
        next = queueBuff(next, actor, b);
      }
    }
    if (r.queueOpponentBuff) {
      const buffs = Array.isArray(r.queueOpponentBuff) ? r.queueOpponentBuff : [r.queueOpponentBuff];
      for (const b of buffs) {
        next = queueBuff(next, opp(actor), b);
      }
    }
    if (r.grantSelfEquipSlotKeyword) {
      const { slotIdx, keyword } = r.grantSelfEquipSlotKeyword;
      next = updatePlayer(next, actor, (p) => {
        const slots = p.equipSlots.slice();
        if (slotIdx < 0 || slotIdx >= slots.length) return p;
        const s = slots[slotIdx];
        const list = Array.from(new Set([...(s.pendingKeywordGrants ?? []), keyword]));
        slots[slotIdx] = { ...s, pendingKeywordGrants: list };
        return { ...p, equipSlots: slots };
      });
      trace.emit({
        category: "equip.keyword.grant",
        label: keyword,
        summary: `P${actor} equip slot ${slotIdx} gains ${keyword} for the round`,
        round: next.round,
        activePlayer: actor,
        details: { playerNum: actor, slotIdx, keyword },
      });
    }
    if (r.becomeAggressor) {
      // Actor becomes the new aggressor for this round. Mirrors the
      // post-state of a stun attack landing: roles flip, new aggressor gets
      // priority, subPhase resets to pre-attack so they can attack/play.
      next = {
        ...next,
        aggressor: actor,
        priority: actor,
        subPhase: "aggressor-pre-attack",
        aggressorPassed: false,
        defenderPassed: false,
      };
      trace.emit({
        category: "role.flip",
        label: "become-aggressor",
        summary: `P${actor} becomes aggressor (hook-requested)`,
        round: next.round,
        activePlayer: actor,
        details: { newAggressor: actor, reason: "becomeAggressor" },
      });
    }
    if (r.negateInPlaySlot) {
      // Negate an attack that has ALREADY resolved off the stack and into
      // the combat slot. Used by Reversal (sig utility played after 2
      // counters). Removes the most-recent unresolved opposing-owned slot,
      // decrements attacksThisRound (game + attacker player), hands priority
      // back to the attacker at aggressor-pre-attack. Does NOT refund
      // stamina, untap weapon, or roll back sig-inPlay — the attacker keeps
      // what they spent (per Reversal's design). Resets consecutiveCounters
      // on the actor so the streak can't be re-used.
      const attackerNum = opp(actor);
      const oldSlots = next.combatSlots ?? [];
      // Find most-recent unresolved slot owned by the opponent (attacker).
      let targetIdx = -1;
      for (let i = oldSlots.length - 1; i >= 0; i--) {
        const s = oldSlots[i];
        if (s.resolved) continue;
        if (s.attackerNum !== attackerNum) continue;
        targetIdx = i;
        break;
      }
      if (targetIdx >= 0) {
        const target = oldSlots[targetIdx];
        const newSlots = [
          ...oldSlots.slice(0, targetIdx),
          ...oldSlots.slice(targetIdx + 1),
        ];
        next = {
          ...next,
          combatSlots: newSlots,
          attacksThisRound: Math.max(0, (next.attacksThisRound ?? 0) - 1),
          priority: attackerNum,
          subPhase: "aggressor-pre-attack",
          aggressorPassed: false,
          defenderPassed: false,
          reactionChain: null,
          combatPopup: null,
        };
        next = updatePlayer(next, attackerNum, (pl) => ({
          ...pl,
          attacksThisRound: Math.max(0, (pl.attacksThisRound ?? 0) - 1),
        }));
        // Reset the actor's consecutiveCounters — they spent the streak.
        next = updatePlayer(next, actor, (pl) => ({
          ...pl,
          consecutiveCounters: 0,
        }));
        trace.emit({
          category: "combat.slot.resolve",
          label: "in-play-negated",
          summary: `slot ${target.id} removed (negate-in-play by P${actor})`,
          round: next.round,
          activePlayer: actor,
          details: {
            slotId: target.id,
            attackerNum,
            reason: "negate-in-play-slot",
            noRefund: true,
          },
        });
        trace.emit({
          category: "priority.shift",
          label: "to-attacker-after-in-play-negate",
          summary: `priority → P${attackerNum} (attack negated in-slot, no refund)`,
          round: next.round,
          activePlayer: attackerNum,
          details: { to: attackerNum, reason: "in-play-attack-negated" },
        });
      } else {
        next = pushLog(
          next,
          "system",
          "Reversal — no opposing attack in the combat slot to negate.",
        );
        trace.emit({
          category: "combat.slot.resolve",
          label: "in-play-negate-noop",
          summary: `negateInPlaySlot fired by P${actor} but no opposing unresolved slot`,
          round: next.round,
          activePlayer: actor,
          details: { actor, reason: "no-target-slot" },
        });
      }
    }

    // ── Return-self-to-hand (Wraith Spin) ──
    // Locate the ZoneCard the hook references in attackDefendZone, remove
    // it (the AD-zone copy ZoneCard and its hand-mirror handInstance), then
    // push a fresh handInstance back. Set the per-round flag so a second
    // trigger this round is rejected.
    if (r.returnSelfToHand) {
      const { instanceId, mode, flagKey } = r.returnSelfToHand;
      const actorP = next.players[actor];
      if (actorP.onceFlagsThisRound?.[flagKey]) {
        trace.emit({
          category: "card.return-to-hand",
          label: "flag-set",
          summary: `returnSelfToHand skipped — ${flagKey} already set this round`,
          round: next.round,
          activePlayer: actor,
          details: { actor, instanceId, mode, flagKey },
        });
      } else {
        const adEntry = actorP.attackDefendZone.find((e) => e.id === instanceId);
        if (!adEntry) {
          trace.emit({
            category: "card.return-to-hand",
            label: "no-zone-entry",
            summary: `returnSelfToHand: no AD-zone entry for ${instanceId}`,
            round: next.round,
            activePlayer: actor,
            details: { actor, instanceId, mode },
          });
        } else {
          const cardId = adEntry.cardId;
          next = updatePlayer(next, actor, (p) => {
            const newAdZone = p.attackDefendZone.filter((e) => e.id !== instanceId);
            // Strip any adValueOverrides for the returning instance — it's no
            // longer in play, and a future replay should start fresh.
            const newOverrides = new Map(p.adValueOverrides);
            newOverrides.delete(instanceId);
            const freshHandInst = { id: uid("h_"), cardId };
            return {
              ...p,
              attackDefendZone: newAdZone,
              adValueOverrides: newOverrides,
              hand: [...p.hand, cardId],
              handInstances: [...p.handInstances, freshHandInst],
              onceFlagsThisRound: { ...(p.onceFlagsThisRound ?? {}), [flagKey]: true },
            };
          });
          trace.emit({
            category: "card.return-to-hand",
            label: mode,
            summary: `P${actor} returns ${cardId} (${instanceId}) to hand after ${mode}`,
            round: next.round,
            activePlayer: actor,
            details: { actor, instanceId, cardId, mode, flagKey },
          });
        }
      }
    }

    // ── Action Chain: stamina / life gain detection (post-apply diff) ──
    {
      const postSelfHealth0 = next.players[actor].health;
      const postOppHealth0 = next.players[opp(actor)].health;
      if (postSelfHealth0 > preSelfHealth) {
        trace.emit({
          category: "heal",
          label: "self",
          summary: `P${actor} healed ${postSelfHealth0 - preSelfHealth} (${preSelfHealth} → ${postSelfHealth0})`,
          round: next.round,
          activePlayer: actor,
          details: { playerNum: actor, before: preSelfHealth, after: postSelfHealth0, delta: postSelfHealth0 - preSelfHealth },
        });
      }
      if (postOppHealth0 > preOppHealth) {
        trace.emit({
          category: "heal",
          label: "opponent",
          summary: `P${opp(actor)} healed ${postOppHealth0 - preOppHealth} (${preOppHealth} → ${postOppHealth0}) by P${actor}`,
          round: next.round,
          activePlayer: opp(actor),
          details: { playerNum: opp(actor), before: preOppHealth, after: postOppHealth0, delta: postOppHealth0 - preOppHealth, sourcePlayer: actor },
        });
      }
    }
    if (cardMap) {
      const postSelfStamina = next.players[actor].stamina;
      const postSelfHealth = next.players[actor].health;
      const postOppStamina = next.players[opp(actor)].stamina;
      const postOppHealth = next.players[opp(actor)].health;
      if (postSelfStamina > preSelfStamina) {
        next = emitActionChainEvent(
          next,
          {
            actorNum: actor,
            reactorNum: opp(actor),
            event: "opponent-gains-stamina",
            description: `gained ${postSelfStamina - preSelfStamina} stamina`,
          },
          cardMap,
        );
      }
      if (postOppStamina > preOppStamina) {
        next = emitActionChainEvent(
          next,
          {
            actorNum: actor,
            reactorNum: actor,
            event: "opponent-gains-stamina",
            description: `${next.players[opp(actor)].name} gained ${postOppStamina - preOppStamina} stamina`,
          },
          cardMap,
        );
      }
      if (postSelfHealth > preSelfHealth) {
        next = emitActionChainEvent(
          next,
          {
            actorNum: actor,
            reactorNum: opp(actor),
            event: "opponent-gains-life",
            description: `gained ${postSelfHealth - preSelfHealth} life`,
          },
          cardMap,
        );
      }
      if (postOppHealth > preOppHealth) {
        next = emitActionChainEvent(
          next,
          {
            actorNum: actor,
            reactorNum: actor,
            event: "opponent-gains-life",
            description: `${next.players[opp(actor)].name} gained ${postOppHealth - preOppHealth} life`,
          },
          cardMap,
        );
      }
    }
    if (r.disarmOpponentEquipment) {
      const count = r.disarmOpponentEquipment.count ?? 1;
      const oppNum = opp(actor);
      const disarmedSlots: { idx: number; cardId: string | null }[] = [];
      next = updatePlayer(next, oppNum, (pl) => {
        const slotsArr = pl.equipSlots.slice();
        let applied = 0;
        for (let i = 0; i < slotsArr.length && applied < count; i++) {
          const s = slotsArr[i];
          if (s.cardId && !s.disarmed) {
            slotsArr[i] = { ...s, disarmed: true };
            disarmedSlots.push({ idx: i, cardId: s.cardId });
            applied++;
          }
        }
        if (applied === 0) {
          // All weapons already disarmed — stack tokens on first equipped
          for (let i = 0; i < slotsArr.length && applied < count; i++) {
            if (slotsArr[i].cardId) {
              slotsArr[i] = {
                ...slotsArr[i],
                disarmTokens: (slotsArr[i].disarmTokens ?? 0) + 1,
              };
              applied++;
            }
          }
        }
        return { ...pl, equipSlots: slotsArr };
      });
      for (const ds of disarmedSlots) {
        trace.emit({
          category: "equip.detach",
          label: "applyResults-disarm",
          summary: `P${oppNum} equip slot ${ds.idx} disarmed by P${actor} (${ds.cardId})`,
          round: next.round,
          activePlayer: oppNum,
          details: { playerNum: oppNum, slotIdx: ds.idx, cardId: ds.cardId, source: actor, count },
        });
      }
    }
  }
  return next;
}

function setPlayer(gs: GameState, n: 1 | 2, patch: Partial<PlayerState>): GameState {
  return {
    ...gs,
    players: { ...gs.players, [n]: { ...gs.players[n], ...patch } } as GameState["players"],
  };
}

function updatePlayer(
  gs: GameState,
  n: 1 | 2,
  fn: (p: PlayerState) => PlayerState,
): GameState {
  return {
    ...gs,
    players: { ...gs.players, [n]: fn(gs.players[n]) } as GameState["players"],
  };
}

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const nameIs = (c: Card | undefined | null, name: string): boolean =>
  !!c && c.name.trim().toLowerCase() === name.trim().toLowerCase();

// ─── stamina cost  (Xr) ─────────────────────────────────────────────────────

/** Read base staminaCost off whichever fields block applies. (spec §1.5) */
export function staminaCostOf(card: Card): number {
  return safeNum(
    card.attackDefendFields?.staminaCost ??
      card.equipmentFields?.staminaCost ??
      card.signatureMoveFields?.staminaCost ??
      card.tokenFields?.staminaCost ??
      card.utilityFields?.staminaCost ??
      0,
  );
}

/**
 * Stance-driven cost reduction (Zr).
 * - Committed Form (stance): Heavy attacks cost 1 less stamina.
 * - Zeus' Wrathbrand (utilityInPlay): Heavy attacks cost 1 less stamina.
 *
 * Reads weight from whichever field block applies — A/D, signature move,
 * OR equipment (so heavy equipment-weapon attacks like Soulreaver get the
 * reduction too).
 */
export function stanceCostReduction(
  p: PlayerState,
  card: Card,
  mode: AdMode,
  cardMap?: Map<string, Card>,
): number {
  if (!cardMap) return 0;
  let reduction = 0;
  // Weight is mode-aware for equipment (separate attackWeight / defendWeight).
  const eqWeight = mode === "attack"
    ? card.equipmentFields?.attackWeight
    : card.equipmentFields?.defendWeight;
  const weight = (
    card.attackDefendFields?.weight ??
    card.signatureMoveFields?.attackWeight ??
    eqWeight ??
    ""
  ).toLowerCase();
  // Stance: Committed Form
  if (p.stanceCardId) {
    const stance = cardMap.get(p.stanceCardId);
    if (stance && mode === "attack" && stance.name === "Committed Form" && weight === "heavy") {
      reduction += 1;
    }
  }
  // Utility: Zeus' Wrathbrand (any spelling)
  if (mode === "attack" && weight === "heavy") {
    for (const inst of p.utilityInPlay) {
      const u = cardMap.get(inst.cardId);
      if (!u) continue;
      const nm = u.name.trim().toLowerCase();
      if (nm === "zeus' wrathbrand" || nm === "zues' wrathbrand" || nm === "zeus wrathbrand") {
        reduction += 1;
        break;
      }
    }
  }
  // Fluid Motion (data-driven): for the rest of the round, weapon
  //   attacks/defends cost N less stamina. Buffs are queued by the
  //   followUp-style hook with kind "weapon-cost-reduction" and
  //   trigger "round-end" so they persist until pruneBuffs drops them at
  //   the next round start (same pattern as Ignite Blade's quick-power-bonus).
  //   We only honor the reduction when the source card is an Equipment
  //   weapon — A/D cards from the hand are unaffected.
  const isWeaponSource =
    card.cardType === "Equipment" &&
    (card.equipmentFields?.equipmentSubtype ?? "").toLowerCase() === "weapon";
  if (isWeaponSource) {
    for (const b of p.pendingBuffs) {
      if (b.effect.kind === "weapon-cost-reduction") {
        reduction += b.effect.amount ?? 0;
      }
    }
  }
  return reduction;
}

/** Sum cost-reduction amounts from a list of fired buffs (cp). */
export function costReductionFromFired(fired: Buff[]): number {
  return fired
    .filter((b) => b.effect.kind === "cost-reduction")
    .reduce((s, b) => s + (b.effect.amount ?? 0), 0);
}

/**
 * Sum of `quick-power-bonus` amounts in a buff list. Ignite Blade queues a
 * `quick-power-bonus` buff with trigger `round-end` so it persists in
 * `pendingBuffs` (and stays in `remaining` after firePendingBuffs) — read
 * from there at play time to bake the +power into the displayed value, and
 * also re-read at resolveCombat time for the actual damage calculation.
 *
 * Used by playAttackDefend / playEquip / playSig to fold Ignite Blade's +1
 * into adValueOverrides whenever the played card will be a Quick attack —
 * either printed Quick or one that gains Quick from a fired `gain-quick`
 * buff (Crushing Strength). This keeps the displayed power on the combat
 * zone / active combat popup in sync with the damage actually dealt.
 */
export function quickPowerBonusFromBuffs(buffs: Buff[]): number {
  return buffs
    .filter((b) => b.effect.kind === "quick-power-bonus")
    .reduce((s, b) => s + (b.effect.amount ?? 0), 0);
}

/**
 * Will the played card count as Quick for resolveCombat / take-damage paths?
 * True if the card has Quick printed (any of the four legacy attackType
 * strings or generic / attackKeywords lists) OR if a `gain-quick` buff is
 * about to fire on this play (Crushing Strength). Mirrors the slot-Quick
 * detection logic in resolveCombat (cardIsQuick + slotGranted), but for the
 * play-time path where we don't yet have a CombatSlot.
 */
export function willBeQuickAtPlay(card: Card, fired: Buff[]): boolean {
  if (cardIsQuick(card)) return true;
  return fired.some((b) => b.effect.kind === "gain-quick");
}

/**
 * Pure preview: compute the effective stamina cost of playing `card` as
 * `mode` for player `p` right now, accounting for stance reductions,
 * Zeus' Wrathbrand, and any pending buffs that would fire on this play
 * (e.g. Tensed Core's next-quick-attack -1, Unbroken Sands' next-action -1).
 *
 * Does NOT mutate state (firePendingBuffs is pure). Use for popup previews
 * and any other UI surface that needs to show "final cost".
 */
export function previewEffectiveCost(
  p: PlayerState,
  card: Card,
  mode: AdMode | null,
  cardMap: Map<string, Card>,
  round = 1,
  channelFocusOverride?: number,
): number {
  const baseCost = channelFocusOverride ?? staminaCostOf(card);
  if (!mode) return Math.max(0, baseCost);
  const keywords = effectiveKeywordsForBuffFire(card);
  const eqWeight = mode === "attack"
    ? card.equipmentFields?.attackWeight
    : card.equipmentFields?.defendWeight;
  const weight =
    card.attackDefendFields?.weight ??
    card.signatureMoveFields?.attackWeight ??
    eqWeight ??
    "";
  const { fired } = firePendingBuffs(p.pendingBuffs, mode, keywords, weight, round);
  const stanceRed = stanceCostReduction(p, card, mode, cardMap);
  const buffRed = costReductionFromFired(fired);
  return Math.max(0, baseCost - stanceRed - buffRed);
}

/**
 * Translate fired `gain-*` buffs into a list of granted keywords for a
 * combat-slot side. Mirrors the original-zip behavior: 1/stun/quick are
 * flags; reflect and disarm carry numeric values rendered as "Reflect N" /
 * "Disarm N".
 */
export function keywordGrantsFromFired(fired: Buff[]): string[] {
  const out: string[] = [];
  if (fired.some((b) => b.effect.kind === "gain-piercing")) out.push("Piercing");
  if (fired.some((b) => b.effect.kind === "gain-stun")) out.push("Stun");
  if (fired.some((b) => b.effect.kind === "gain-quick")) out.push("Quick");
  if (fired.some((b) => b.effect.kind === "gain-chain")) out.push("Chain");
  if (
    fired.some((b) => b.effect.kind === "gain-serrated" || b.effect.serrated === true)
  ) out.push("Serrated");
  if (fired.some((b) => b.effect.kind === "gain-displace")) out.push("Displace");
  if (fired.some((b) => b.effect.kind === "gain-crush")) out.push("Crush");
  const reflect = fired.find((b) => b.effect.kind === "gain-reflect");
  if (reflect && (reflect.effect.power ?? 0) > 0) out.push(`Reflect ${reflect.effect.power}`);
  const disarm = fired.find((b) => b.effect.kind === "gain-disarm");
  if (disarm && (disarm.effect.count ?? 0) > 0) out.push(`Disarm ${disarm.effect.count}`);
  return out;
}

// ─── pending buffs (Pt / gt / yv / Y2) ─────────────────────────────────────

export function makeBuff(
  sourceCardName: string,
  trigger: BuffTrigger,
  effect: BuffEffect,
  currentRound: number,
  opts: { persistRound?: boolean; expiresInRounds?: number; requireKeyword?: string } = {},
): Buff {
  const expiresRound = opts.persistRound
    ? currentRound + (opts.expiresInRounds ?? 1)
    : currentRound;
  return {
    id: uid("buff_"),
    sourceCardName,
    trigger,
    effect,
    createdRound: currentRound,
    expiresRound,
    persistRound: !!opts.persistRound,
    requireKeyword: opts.requireKeyword,
  };
}

/** Append a buff to a player's pendingBuffs list. (gt) */
export function queueBuff(gs: GameState, n: 1 | 2, buff: Buff): GameState {
  trace.emit({
    category: "buff.queue",
    summary: `P${n} queue ${buff.effect.kind}${buff.effect.amount !== undefined ? ` ${buff.effect.amount}` : ""}${buff.effect.factor !== undefined ? ` x${buff.effect.factor}` : ""} (trigger=${buff.trigger})`,
    round: gs.round,
    activePlayer: gs.priority,
    details: {
      player: n,
      buffId: buff.id,
      sourceCardName: buff.sourceCardName,
      trigger: buff.trigger,
      effect: buff.effect,
      persistRound: buff.persistRound,
      expiresRound: buff.expiresRound,
    },
  });
  return updatePlayer(gs, n, (p) => ({
    ...p,
    pendingBuffs: [...p.pendingBuffs, buff],
  }));
}

/**
 * Examine pendingBuffs and split into `fired` (matching this play) vs
 * `remaining` (untouched).  (yv)
 *
 * Two-pass evaluation: a `next-attack` buff that grants Quick (e.g.
 * Crushing Strength's gain-quick) makes the SAME attack count as Quick for
 * any `next-quick-attack` triggers (e.g. Tensed Core's cost reduction).
 * Pass 1 fires unconditional next-attack/next-defend/next-action triggers
 * and detects whether any of them grants Quick. Pass 2 fires
 * next-quick-attack triggers using the augmented Quick state.
 */
export function firePendingBuffs(
  buffs: Buff[],
  mode: AdMode,
  keywords: string[],
  _weight: string,
  _round: number,
): { fired: Buff[]; remaining: Buff[] } {
  const fired: Buff[] = [];
  const remaining: Buff[] = [];
  const printedQuick = keywords.some((k) => k.toLowerCase() === "quick");
  const keywordSetLc = new Set(keywords.map((k) => k.toLowerCase()));
  // Pass 1: unconditional triggers.
  const deferredQuickAttack: Buff[] = [];
  let grantedQuick = false;
  for (const b of buffs) {
    let matches = false;
    if (b.trigger === "next-attack" && mode === "attack") matches = true;
    else if (b.trigger === "next-defend" && mode === "defend") matches = true;
    else if (b.trigger === "next-action") matches = true;
    else if (b.trigger === "next-quick-attack" && mode === "attack") {
      // Re-evaluated in pass 2 once we know whether Quick was granted.
      deferredQuickAttack.push(b);
      continue;
    }
    // Conditional gate: buff only fires if the card has the required keyword
    // (printed or granted via fired buffs in this same play). For now we
    // only check printed keywords; gain-keyword buffs that fire in the same
    // pass do NOT retroactively satisfy requireKeyword (kept simple for v1).
    if (matches && b.requireKeyword) {
      const need = b.requireKeyword.toLowerCase();
      if (!keywordSetLc.has(need)) {
        matches = false;
      }
    }
    if (matches) {
      fired.push(b);
      if (b.effect.kind === "gain-quick") grantedQuick = true;
      trace.emit({
        category: "buff.fire",
        label: "pass1",
        summary: `${b.sourceCardName ?? "buff"} fired (${b.effect.kind}, ${b.trigger})`,
        details: {
          buffId: b.id,
          sourceCardName: b.sourceCardName,
          trigger: b.trigger,
          effect: b.effect,
          mode,
          keywords,
          why: matchReason(b.trigger, mode),
        },
      });
    } else {
      remaining.push(b);
    }
  }
  // Pass 2: next-quick-attack triggers, now that we know Quick state.
  const isQuick = printedQuick || grantedQuick;
  for (const b of deferredQuickAttack) {
    if (isQuick) {
      fired.push(b);
      trace.emit({
        category: "buff.fire",
        label: "pass2-quick",
        summary: `${b.sourceCardName ?? "buff"} fired (${b.effect.kind}, next-quick-attack)`,
        details: {
          buffId: b.id,
          sourceCardName: b.sourceCardName,
          trigger: b.trigger,
          effect: b.effect,
          mode,
          keywords,
          printedQuick,
          grantedQuick,
          why: printedQuick
            ? "card has printed Quick"
            : "another fired buff granted Quick (gain-quick)",
        },
      });
    } else {
      remaining.push(b);
    }
  }
  return { fired, remaining };
}

/** Human-readable explanation of why a trigger matched the current play. */
function matchReason(trigger: BuffTrigger, mode: AdMode): string {
  if (trigger === "next-attack" && mode === "attack") return "trigger=next-attack, mode=attack";
  if (trigger === "next-defend" && mode === "defend") return "trigger=next-defend, mode=defend";
  if (trigger === "next-action") return "trigger=next-action (any play)";
  return `trigger=${trigger}, mode=${mode}`;
}

/** Drop expired buffs at round-end. (Y2 / pruneBuffs) */
export function pruneBuffs(buffs: Buff[], newRound: number): Buff[] {
  const kept: Buff[] = [];
  const dropped: Buff[] = [];
  for (const b of buffs) {
    const keep = b.persistRound ? (b.expiresRound ?? newRound) >= newRound : false;
    if (keep) kept.push(b);
    else dropped.push(b);
  }
  for (const b of dropped) {
    trace.emit({
      category: "buff.expire",
      summary: `${b.sourceCardName ?? "buff"} expired at round ${newRound} (${b.effect.kind})`,
      round: newRound,
      details: {
        buffId: b.id,
        sourceCardName: b.sourceCardName,
        trigger: b.trigger,
        effect: b.effect,
        persistRound: b.persistRound,
        expiresRound: b.expiresRound,
      },
    });
  }
  return kept;
}

// ─── adValueOverrides helpers (xr / Ra) ────────────────────────────────────

/** Read the current power/block value for a played-instance copy. (xr) */
export function instanceValue(
  player: PlayerState,
  instanceId: string,
  mode: AdMode,
  card: Card,
): number {
  if (player.adValueOverrides.has(instanceId)) {
    return player.adValueOverrides.get(instanceId) ?? 0;
  }
  if (mode === "attack") {
    return safeNum(
      card.attackDefendFields?.powerValue ??
        card.equipmentFields?.powerValue ??
        card.signatureMoveFields?.powerValue ??
        card.tokenFields?.powerValue ??
        0,
    );
  }
  return safeNum(
    card.attackDefendFields?.blockValue ??
      card.equipmentFields?.blockValue ??
      card.signatureMoveFields?.blockValue ??
      card.armorFields?.blockValue ??
      card.tokenFields?.blockValue ??
      0,
  );
}

/** ±N nudge to a played-instance value. (Ra) */
export function adjustInstanceValue(
  gs: GameState,
  n: 1 | 2,
  instanceId: string,
  card: Card,
  mode: AdMode,
  delta: number,
): GameState {
  return updatePlayer(gs, n, (p) => {
    const ovr = new Map(p.adValueOverrides);
    const current = ovr.has(instanceId)
      ? (ovr.get(instanceId) ?? 0)
      : instanceValue(p, instanceId, mode, card);
    ovr.set(instanceId, Math.max(0, current + delta));
    return { ...p, adValueOverrides: ovr };
  });
}

// ─── tokens (Pa / Aa / Wc / Zn / Kn) ───────────────────────────────────────

/** Add a token instance to player's tokenArea. (Pa) */
export function addToken(gs: GameState, n: 1 | 2, tokenCardId: string): GameState {
  const inst: ZoneCard = { id: uid("tok_"), cardId: tokenCardId };
  trace.emit({
    category: "token.add",
    summary: `P${n} token added (${tokenCardId})`,
    round: gs.round,
    activePlayer: n,
    details: { playerNum: n, cardId: tokenCardId, instanceId: inst.id },
  });
  return updatePlayer(gs, n, (p) => ({ ...p, tokenArea: [...p.tokenArea, inst] }));
}

/** Remove a token by instance id. (Aa) */
export function removeToken(gs: GameState, n: 1 | 2, instanceId: string): GameState {
  const tok = gs.players[n].tokenArea.find((t) => t.id === instanceId);
  trace.emit({
    category: "token.remove",
    summary: tok ? `P${n} token removed (${tok.cardId})` : `P${n} token remove no-op (${instanceId})`,
    round: gs.round,
    activePlayer: n,
    details: { playerNum: n, instanceId, cardId: tok?.cardId, found: !!tok },
  });
  return updatePlayer(gs, n, (p) => {
    const tap = new Set(p.tokenAreaTapped);
    tap.delete(instanceId);
    return {
      ...p,
      tokenArea: p.tokenArea.filter((t) => t.id !== instanceId),
      tokenAreaTapped: tap,
    };
  });
}

const isFocusToken = (z: ZoneCard, cardMap: Map<string, Card>): boolean =>
  cardMap.get(z.cardId)?.name === "Focus";

/** Count Focus tokens — sums tokenArea instances AND legacy counter so storage
 *  splits never under-report. (Pre-fix this used `inArea > 0 ? inArea : p.focus`,
 *  which hid legacy focus when even one was minted to tokenArea.) */
export function focusTokenCount(p: PlayerState, cardMap: Map<string, Card>): number {
  const inArea = p.tokenArea.filter((t) => isFocusToken(t, cardMap)).length;
  return inArea + p.focus;
}

/** Grant N Focus tokens. Mints to tokenArea when a Focus card exists, AND
 *  migrates any pre-existing legacy `p.focus` counter into tokenArea so the
 *  two stores never diverge. Falls back to legacy counter only if no Focus
 *  card is registered yet. (Zn) */
export function grantFocus(
  gs: GameState,
  n: 1 | 2,
  amount: number,
  cardMap: Map<string, Card>,
): GameState {
  if (amount <= 0) return gs;
  const beforeCount = focusTokenCount(gs.players[n], cardMap);
  // Find a Focus card in the registry
  let focusCardId: string | undefined;
  for (const [id, c] of cardMap) if (c.name === "Focus") { focusCardId = id; break; }
  let next: GameState;
  if (focusCardId) {
    // Migrate any legacy focus into tokenArea, then mint the new amount.
    const legacy = gs.players[n].focus;
    const total = legacy + amount;
    next = legacy > 0
      ? updatePlayer(gs, n, (p) => ({ ...p, focus: 0 }))
      : gs;
    for (let i = 0; i < total; i++) next = addToken(next, n, focusCardId);
  } else {
    // Fallback: legacy counter (Focus card not yet registered).
    next = updatePlayer(gs, n, (p) => ({ ...p, focus: p.focus + amount }));
  }
  trace.emit({
    category: "focus.grant",
    summary: `P${n} +${amount} Focus (now ${beforeCount + amount})`,
    round: gs.round,
    activePlayer: gs.priority,
    details: { player: n, amount, before: beforeCount, after: beforeCount + amount },
  });
  return next;
}

/** Consume N Focus tokens. (Kn) */
export function consumeFocus(
  gs: GameState,
  n: 1 | 2,
  amount: number,
  cardMap: Map<string, Card>,
): GameState {
  if (amount <= 0) return gs;
  const beforeCount = focusTokenCount(gs.players[n], cardMap);
  let remaining = amount;
  // Single-pass drain of tokenArea: walk the list and drop the first `remaining`
  // Focus tokens. Resilient to duplicate ids (keeps non-Focus tokens; only drops
  // up to `remaining` Focus tokens regardless of how many share an id).
  const player = gs.players[n];
  const droppedIds = new Set<string>();
  const newTokenArea = player.tokenArea.filter((t) => {
    if (remaining > 0 && isFocusToken(t, cardMap)) {
      droppedIds.add(t.id);
      remaining--;
      return false;
    }
    return true;
  });
  // Clean tapped set for any dropped instance ids.
  const newTapped = new Set(player.tokenAreaTapped);
  for (const id of droppedIds) newTapped.delete(id);
  let next = updatePlayer(gs, n, (p) => ({
    ...p,
    tokenArea: newTokenArea,
    tokenAreaTapped: newTapped,
  }));
  // Drain the legacy counter for any remainder, so total removed === amount
  // even when focus is split between both stores.
  if (remaining > 0) {
    const legacy = next.players[n].focus;
    const take = Math.min(legacy, remaining);
    if (take > 0) {
      next = updatePlayer(next, n, (pl) => ({ ...pl, focus: pl.focus - take }));
      remaining -= take;
    }
  }
  const consumed = amount - remaining;
  trace.emit({
    category: "focus.consume",
    summary: `P${n} -${consumed} Focus (now ${beforeCount - consumed})${remaining > 0 ? ` [requested ${amount}, short ${remaining}]` : ""}`,
    round: gs.round,
    activePlayer: gs.priority,
    details: { player: n, requested: amount, consumed, shortBy: remaining, before: beforeCount, after: beforeCount - consumed },
  });
  return next;
}

// ─── deck helpers ───────────────────────────────────────────────────────────

/** Draw N cards into the player's hand (mirroring legacy + spec sides). */
export function drawCards(gs: GameState, n: 1 | 2, count: number): GameState {
  const beforeP = gs.players[n];
  const beforeDeck = beforeP.deckCards.length;
  const beforeDiscard = beforeP.discard.length;
  let reshuffled = false;
  let fatigued = false;
  const drawnIds: string[] = [];
  const next = updatePlayer(gs, n, (p) => {
    let deckCards = p.deckCards.slice();
    let discard = p.discard.slice();
    for (let i = 0; i < count; i++) {
      if (deckCards.length === 0) {
        if (discard.length === 0) {
          fatigued = true;
          break;
        }
        deckCards = shuffle(discard);
        discard = [];
        reshuffled = true;
      }
      drawnIds.push(deckCards.shift()!);
    }
    const newInstances = drawnIds.map((cid) => ({ id: uid("h_"), cardId: cid }));
    return {
      ...p,
      hand: [...p.hand, ...drawnIds],
      handInstances: [...p.handInstances, ...newInstances],
      deckCards,
      discard,
      // Per-round counter used by Final Straw's `opponentDrewThisRound`
      // counter trigger. Reset in beginRound / endRound.
      cardsDrawnThisRound: (p.cardsDrawnThisRound ?? 0) + drawnIds.length,
    };
  });
  if (reshuffled) {
    trace.emit({
      category: "deck.shuffle",
      label: "discard-into-deck",
      summary: `P${n} reshuffled discard pile into deck (${beforeDiscard} cards)`,
      round: gs.round,
      activePlayer: n,
      details: { playerNum: n, discardCount: beforeDiscard, source: "drawCards" },
    });
  }
  trace.emit({
    category: "deck.draw",
    label: drawnIds.length < count ? "partial" : "full",
    summary: `P${n} drew ${drawnIds.length}/${count} card${count === 1 ? "" : "s"}`,
    round: gs.round,
    activePlayer: n,
    details: {
      playerNum: n,
      requested: count,
      drawn: drawnIds.length,
      drawnCardIds: drawnIds,
      deckBefore: beforeDeck,
      deckAfter: next.players[n].deckCards.length,
      discardBefore: beforeDiscard,
      discardAfter: next.players[n].discard.length,
      handBefore: beforeP.hand.length,
      handAfter: next.players[n].hand.length,
      reshuffled,
      fatigued,
    },
  });
  if (fatigued) {
    trace.emit({
      category: "deck.fatigue",
      label: "empty-deck-and-discard",
      summary: `P${n} fatigue — wanted ${count - drawnIds.length} more card${(count - drawnIds.length) === 1 ? "" : "s"} but deck+discard were empty`,
      round: gs.round,
      activePlayer: n,
      details: { playerNum: n, requested: count, drawn: drawnIds.length, missed: count - drawnIds.length },
    });
  }
  return next;
}

/** Remove a hand instance by instance id. Returns updated player. */
export function removeFromHand(p: PlayerState, instanceId: string): PlayerState {
  const idx = p.handInstances.findIndex((h) => h.id === instanceId);
  if (idx === -1) return p;
  const card = p.handInstances[idx];
  const handInstances = p.handInstances.filter((h) => h.id !== instanceId);
  // Remove first matching cardId from legacy hand
  const handIdx = p.hand.indexOf(card.cardId);
  const hand = handIdx === -1 ? p.hand.slice() : [...p.hand.slice(0, handIdx), ...p.hand.slice(handIdx + 1)];
  trace.emit({
    category: "hand.remove",
    summary: `card removed from hand (instance ${instanceId})`,
    details: { instanceId, cardId: card.cardId, handAfter: hand.length },
  });
  return { ...p, hand, handInstances };
}

// ─── phase machine (Is/ff/qc/gi/kl/hf/zc/Vc/eo) ─────────────────────────────

/** Set up a fresh GameState for a new playtest match. Reuses legacy setupPlayer. */
export function setupPlaytest(
  p1Name: string,
  p2Name: string,
  d1: Deck,
  d2: Deck,
  cardMap: Map<string, Card>,
): GameState {
  const ch1 = d1.championId ? cardMap.get(d1.championId) : null;
  const ch2 = d2.championId ? cardMap.get(d2.championId) : null;
  const sp1 = parseFloat(ch1?.championFields?.speed ?? "0") || 0;
  const sp2 = parseFloat(ch2?.championFields?.speed ?? "0") || 0;
  let priority: 1 | 2 = 1;
  let coinFlipRequired = false;
  let coinFlipWinner: 1 | 2 | undefined;
  if (sp1 > sp2) priority = 1;
  else if (sp2 > sp1) priority = 2;
  else {
    coinFlipRequired = true;
    coinFlipWinner = Math.random() < 0.5 ? 1 : 2;
    priority = coinFlipWinner;
    trace.emit({
      category: "coin.flip",
      label: "setup-equal-speed",
      summary: `Coin flip — equal speeds (${sp1}=${sp2}); winner P${coinFlipWinner}`,
      round: 1,
      activePlayer: coinFlipWinner,
      details: { p1Speed: sp1, p2Speed: sp2, coinFlipWinner, priority: coinFlipWinner },
    });
  }
  if (!coinFlipRequired) {
    trace.emit({
      category: "coin.flip",
      label: "setup-by-speed",
      summary: `Aggressor by speed: P${priority} (sp1=${sp1}, sp2=${sp2})`,
      round: 1,
      activePlayer: priority,
      details: { p1Speed: sp1, p2Speed: sp2, priority },
    });
  }

  return {
    status: "playing",
    round: 1,
    priority,
    turn: 1,
    players: {
      1: buildPlayerLegacy(1, p1Name, d1, cardMap),
      2: buildPlayerLegacy(2, p2Name, d2, cardMap),
    },
    log: [{ round: 1, player: "system", message: `Match started: ${p1Name} vs ${p2Name}`, ts: Date.now() }],
    winner: null,
    pendingPrompt: null,
    phase: coinFlipRequired ? "coin-flip" : "mulligan-p1",
    aggressor: priority,
    originalAggressor: priority,
    aggressorPassed: false,
    defenderPassed: false,
    mustAttackWarning: false,
    attacksThisRound: 0,
    consectutiveChainAttacks: 0,
    combatSlots: [],
    adModes: new Map<string, AdMode>(),
    resolvedInstanceIds: new Set<string>(),
    pendingPlay: null,
    counterDialog: null,
    counterActivatedDialog: null,
    reactionChain: null,
    actionChain: null,
    freeReactionWindow: false,
    combatPopup: null,
    chainStreak: 0,
    consecutiveCounters: 0,
    coinFlipRequired,
    coinFlipWinner,
    spartacusActivatedSet: new Set<1 | 2>(),
    planningDiscardSet: new Set<string>(),
  };
}

/** Confirm mulligan for player `n` — replaces selected card ids with fresh draws.  (Is) */
export function mulliganConfirm(
  gs: GameState,
  n: 1 | 2,
  toMulliganInstanceIds: string[],
): GameState {
  let next = gs;
  next = updatePlayer(next, n, (p) => {
    const removed = p.handInstances.filter((h) => toMulliganInstanceIds.includes(h.id));
    const removedCardIds = removed.map((r) => r.cardId);
    const handInstances = p.handInstances.filter((h) => !toMulliganInstanceIds.includes(h.id));
    // Remove matching legacy hand entries
    const hand = p.hand.slice();
    for (const cid of removedCardIds) {
      const i = hand.indexOf(cid);
      if (i >= 0) hand.splice(i, 1);
    }
    // Shuffle removed back into deck
    const deckCards = shuffle([...p.deckCards, ...removedCardIds]);
    return { ...p, hand, handInstances, deckCards };
  });
  next = drawCards(next, n, toMulliganInstanceIds.length);
  next = pushLog(next, next.players[n].name, `Mulliganed ${toMulliganInstanceIds.length}`);
  // Advance phase: p1 → p2 mulligan; p2 → preparation
  const fromPhase = gs.phase;
  if (gs.phase === "mulligan-p1") next = { ...next, phase: "mulligan-p2" };
  else next = { ...next, phase: "preparation" };
  trace.emit({
    category: "phase.transition",
    summary: `phase ${fromPhase} → ${next.phase} (P${n} mulligan confirmed)`,
    round: next.round,
    activePlayer: n,
    details: {
      from: fromPhase,
      to: next.phase,
      mulliganedCount: toMulliganInstanceIds.length,
      reason: "mulligan-confirm",
    },
  });
  return next;
}

/** Set the player's stance during preparation phase. (ff) */
export function setStance(gs: GameState, n: 1 | 2, stanceCardId: string | null): GameState {
  const before = gs.players[n].stanceCardId;
  const next = updatePlayer(gs, n, (p) => ({ ...p, stanceCardId }));
  if (before !== stanceCardId) {
    if (stanceCardId) {
      trace.emit({
        category: "stance.set",
        summary: `P${n} stance set: ${stanceCardId}`,
        round: gs.round,
        activePlayer: n,
        details: { playerNum: n, stanceBefore: before, stanceAfter: stanceCardId },
      });
    } else {
      trace.emit({
        category: "stance.clear",
        summary: `P${n} stance cleared (was ${before ?? "none"})`,
        round: gs.round,
        activePlayer: n,
        details: { playerNum: n, stanceBefore: before },
      });
    }
  }
  return next;
}

/** Mark player ready at end of preparation. (qc) */
export function setReady(gs: GameState, n: 1 | 2): GameState {
  let next = updatePlayer(gs, n, (p) => ({ ...p, prepReady: true }));
  trace.emit({
    category: "phase.transition",
    label: "prep-ready",
    summary: `P${n} ready (prep) — p1=${next.players[1].prepReady} p2=${next.players[2].prepReady}`,
    round: next.round,
    activePlayer: n,
    details: {
      player: n,
      p1Ready: next.players[1].prepReady,
      p2Ready: next.players[2].prepReady,
    },
  });
  if (next.players[1].prepReady && next.players[2].prepReady) {
    next = beginMatch(next);
  }
  return next;
}

/** Begin Match — transitions preparation → playing. (gi) */
export function beginMatch(gs: GameState): GameState {
  const aggressor = gs.aggressor ?? 1;
  trace.emit({
    category: "phase.transition",
    label: "begin-match",
    summary: `phase ${gs.phase} → playing (match start, aggressor=P${aggressor})`,
    round: gs.round,
    activePlayer: aggressor,
    details: {
      from: gs.phase,
      to: "playing",
      aggressor,
      reason: "begin-match",
    },
  });
  return {
    ...gs,
    phase: "playing",
    priority: aggressor,
    aggressor,
    originalAggressor: aggressor,
    initiative: aggressor,
    subPhase: "aggressor-pre-attack",
    aggressorPassed: false,
    defenderPassed: false,
    attacksThisRound: 0,
    chainStreak: 0,
    consecutiveCounters: 0,
    log: [...gs.log, { round: gs.round, player: "system", message: "⚔ Match started! Round 1 begins.", ts: Date.now() }],
  };
}

/** Begin Round — called after planning. (kl) */
export function beginRound(gs: GameState, cardMap?: Map<string, Card>): GameState {
  const aggressor = gs.aggressor ?? 1;
  trace.emit({
    category: "round",
    label: "begin",
    summary: `Round ${gs.round} begins (aggressor: P${aggressor})`,
    round: gs.round,
    activePlayer: aggressor,
    details: {
      round: gs.round,
      aggressor,
      p1: { health: gs.players[1].health, stamina: gs.players[1].stamina, focus: gs.players[1].focus, hand: gs.players[1].hand.length },
      p2: { health: gs.players[2].health, stamina: gs.players[2].stamina, focus: gs.players[2].focus, hand: gs.players[2].hand.length },
    },
  });
  let next: GameState = {
    ...gs,
    priority: aggressor,
    subPhase: "aggressor-pre-attack",
    aggressorPassed: false,
    defenderPassed: false,
    originalAggressor: aggressor,
    initiative: aggressor,
    mustAttackWarning: false,
    attacksThisRound: 0,
    chainStreak: 0,
    consecutiveCounters: 0,
    phase: "playing",
  };
  // Reset per-player attacksThisRound counter (mirrors the GameState reset).
  // Hooks like Crixus and Bloodforged Plate read ctx.self.attacksThisRound,
  // so this MUST be cleared each round or the counter accumulates forever
  // and "=== N" checks never re-trigger after round 1.
  for (const pn of [1, 2] as (1 | 2)[]) {
    next = updatePlayer(next, pn, (p) => ({
      ...p,
      attacksThisRound: 0,
      chainStreak: 0,
      attacksDealtDamageThisRound: 0,
      damageDealtByAttackThisRound: [],
      // Reset per-round trackers used by counters (Final Straw) and
      // once-per-round hook flags (Wraith Spin return-to-hand).
      cardsDrawnThisRound: 0,
      onceFlagsThisRound: {},
      // Reset Phase 3 per-round event flag used by Crimson Tempest's counter
      // (selfStunnedThisRound). tookDamageThisRound is already reset elsewhere.
      wasStunnedThisRound: false,
    }));
  }
  // Clear per-equip-slot pendingKeywordGrants (granted last round, expire now).
  for (const pn of [1, 2] as (1 | 2)[]) {
    next = updatePlayer(next, pn, (p) => ({
      ...p,
      equipSlots: p.equipSlots.map((s) => (s.pendingKeywordGrants && s.pendingKeywordGrants.length > 0 ? { ...s, pendingKeywordGrants: [] } : s)),
    }));
  }
  // Clear "disarmed" flag (decrement happened at round-end)
  for (const pn of [1, 2] as (1 | 2)[]) {
    const before = next.players[pn].equipSlots;
    before.forEach((s, idx) => {
      if (s.disarmed && s.cardId) {
        trace.emit({
          category: "equip.attach",
          label: "re-attach",
          summary: `P${pn} weapon slot ${idx} re-attached (round start clear-disarm)`,
          round: next.round,
          activePlayer: pn,
          details: { playerNum: pn, slotIdx: idx, weaponCardId: s.cardId, source: "round-start-clear-disarm" },
        });
      }
    });
    next = updatePlayer(next, pn, (p) => ({
      ...p,
      equipSlots: p.equipSlots.map((s) => (s.disarmed ? { ...s, disarmed: false } : s)),
    }));
  }
  next = pushLog(next, "system", `⚔ Round ${next.round} begins!`);
  // Fire start-of-play-phase hooks for champion + armor of each player.
  if (cardMap) next = triggerStartOfRoundEffects(next, cardMap);
  return next;
}

/**
 * Fire start-of-play-phase passives for both players. Called from beginRound
 * (rounds 2+) and beginMatch (round 1). Champion / armor `onRoundStart`
 * hooks run, then card-specific opening prompts (e.g. Spartacus's Opening
 * Advantage at ≥3 Focus) are queued via `pendingPrompt` if eligible.
 *
 * Aggressor's prompt fires first; if both qualify, the second is queued
 * after the first resolves (confirmSpartacusOpening will check the other
 * player and set its prompt if needed).
 */
export function triggerStartOfRoundEffects(
  gs: GameState,
  cardMap: Map<string, Card>,
): GameState {
  let next = gs;
  // Champion + armor onRoundStart hooks for each player. focusGrant +
  // selfPatch are applied; the existing engine.ts copy of onRoundStart was
  // never wired in — this is the canonical entry point.
  for (const n of [1, 2] as (1 | 2)[]) {
    const player = next.players[n];
    const oppP = next.players[opp(n)];
    const cards: (Card | undefined)[] = [];
    if (player.championCardId) cards.push(cardMap.get(player.championCardId));
    if (player.armorCardId) cards.push(cardMap.get(player.armorCardId));
    for (const card of cards) {
      if (!card) continue;
      const hooks = getCardHooks(card);
      const fn = hooks?.onRoundStart;
      if (!fn) continue;
      const res = fn({ self: player, opponent: oppP, game: next, card }) ?? {};
      trace.emit({
        category: "hook.fire",
        label: "onRoundStart",
        summary: `${card.name} onRoundStart fired (P${n})`,
        round: next.round,
        activePlayer: n,
        details: { card: card.name, hook: "onRoundStart", playerNum: n, result: res as unknown as Record<string, unknown> },
      });
      if (res.selfPatch) {
        next = updatePlayer(next, n, (p) => ({ ...p, ...res.selfPatch }));
      }
      if (res.focusGrant && res.focusGrant > 0) {
        next = grantFocus(next, n, res.focusGrant, cardMap);
      }
      if (res.drawSelf && res.drawSelf > 0) {
        next = drawCards(next, n, res.drawSelf);
      }
      for (const line of res.log ?? []) {
        next = pushLog(next, player.name, line);
      }
    }
  }
  // Spartacus, Undefeated — Opening Advantage prompt at the start of the
  // play phase. Triggers when controller has ≥ 3 Focus tokens. Aggressor
  // first; defender's prompt is queued by confirmSpartacusOpening after
  // the aggressor's choice resolves so we never set two prompts at once.
  // Each player is gated to ONCE per round via spartacusActivatedSet —
  // re-entering this function (e.g. after a coin flip or sub-phase change)
  // must not re-prompt a player who already saw the dialog this round.
  const sActivated = next.spartacusActivatedSet ?? new Set<1 | 2>();
  for (const n of [gs.aggressor ?? 1, opp(gs.aggressor ?? 1)] as (1 | 2)[]) {
    if (sActivated.has(n)) continue;
    const player = next.players[n];
    if (!player.championCardId) continue;
    const champ = cardMap.get(player.championCardId);
    if (!champ || canonicalName(champ.name) !== "Spartacus, Undefeated") continue;
    const focus = focusTokenCount(player, cardMap);
    if (focus < 3) continue;
    if (next.pendingPrompt) break; // queue the second player's prompt later
    next = setPendingPrompt(next, { kind: "spartacus-opening", playerNum: n, step: "activate" });
    next = { ...next, spartacusActivatedSet: new Set([...sActivated, n]) };
  }
  return next;
}

/**
 * Spartacus Opening Advantage — confirm handler for the prompt set by
 * `triggerStartOfRoundEffects`. Two-step prompt:
 *   1. activate / decline
 *   2. choose stamina / draw
 * Activating consumes 3 Focus. Choosing "stamina" grants +2 stamina;
 * choosing "draw" draws 2 cards. After resolution, queues the OTHER
 * player's Spartacus prompt if they also qualify.
 */
export function confirmSpartacusOpening(
  gs: GameState,
  choice: "activate" | "decline" | "stamina" | "draw",
  cardMap: Map<string, Card>,
): GameState {
  const prompt = gs.pendingPrompt;
  if (!prompt || prompt.kind !== "spartacus-opening") return gs;
  const n = prompt.playerNum;
  let next = gs;
  if (prompt.step === "activate") {
    if (choice === "decline") {
      next = pushLog(next, next.players[n].name, "Declined Opening Advantage");
      next = setPendingPrompt(next, null);
      next = queueOtherSpartacusPrompt(next, n, cardMap);
      return next;
    }
    if (choice !== "activate") return gs;
    // Spend 3 Focus and advance to choose step.
    next = consumeFocus(next, n, 3, cardMap);
    next = pushLog(next, next.players[n].name, "Spartacus Opening Advantage — spent 3 Focus");
    next = setPendingPrompt(next, { kind: "spartacus-opening", playerNum: n, step: "choose" });
    return next;
  }
  // step === "choose"
  if (choice === "stamina") {
    next = updatePlayer(next, n, (p) => ({
      ...p,
      stamina: Math.min(p.maxStamina ?? 10, p.stamina + 2),
    }));
    next = pushLog(next, next.players[n].name, "Opening Advantage: +2 stamina");
  } else if (choice === "draw") {
    next = drawCards(next, n, 2);
    next = pushLog(next, next.players[n].name, "Opening Advantage: drew 2 cards");
  } else {
    return gs;
  }
  next = setPendingPrompt(next, null);
  next = queueOtherSpartacusPrompt(next, n, cardMap);
  return next;
}

/** Helper: after one player's Spartacus prompt resolves, check the other. */
function queueOtherSpartacusPrompt(
  gs: GameState,
  justResolvedFor: 1 | 2,
  cardMap: Map<string, Card>,
): GameState {
  const otherNum = opp(justResolvedFor);
  const sActivated = gs.spartacusActivatedSet ?? new Set<1 | 2>();
  if (sActivated.has(otherNum)) return gs;
  const other = gs.players[otherNum];
  if (!other.championCardId) return gs;
  const champ = cardMap.get(other.championCardId);
  if (!champ || canonicalName(champ.name) !== "Spartacus, Undefeated") return gs;
  const focus = focusTokenCount(other, cardMap);
  if (focus < 3) return gs;
  let nextSpa = setPendingPrompt(gs, { kind: "spartacus-opening", playerNum: otherNum, step: "activate" });
  nextSpa = { ...nextSpa, spartacusActivatedSet: new Set([...sActivated, otherNum]) };
  return nextSpa;
}

/** End Round — full cleanup pass per spec §4.4. (hf) */
export function endRound(gs: GameState, _cardMap: Map<string, Card>): GameState {
  const newRound = gs.round + 1;
  const newAggressor = opp(gs.originalAggressor ?? 1);

  let next: GameState = pushLog(gs, "system", `── Round ${gs.round} ended ──`);

  for (const n of [1, 2] as (1 | 2)[]) {
    // Pre-capture counter-kept hand discards for tracing.
    const _pBefore = next.players[n];
    const _handDiscardForTrace = _pBefore.handInstances.filter((c) => _pBefore.counterKeptIds.has(c.id));
    if (_handDiscardForTrace.length > 0) {
      trace.emit({
        category: "hand.discard",
        label: "end-round-counter-kept",
        summary: `P${n} end-round discard: ${_handDiscardForTrace.length} counter-kept card(s)`,
        round: next.round,
        activePlayer: n,
        details: { playerNum: n, count: _handDiscardForTrace.length, cardIds: _handDiscardForTrace.map((h) => h.cardId), instanceIds: _handDiscardForTrace.map((h) => h.id) },
      });
    }
    next = updatePlayer(next, n, (p) => {
      const sigInstanceIds = new Set(p.sigMoveSlots.map((s) => s.instanceId));
      const sigInPlayCardIds = new Set(p.sigMoveSlots.filter((s) => s.inPlay).map((s) => s.cardId));
      const toDiscard: ZoneCard[] = [];
      const toKeepInPlay: ZoneCard[] = [];
      for (const u of p.utilityInPlay) {
        if (sigInstanceIds.has(u.id) || sigInPlayCardIds.has(u.cardId)) continue;
        if (p.counterRefUtilityIds.has(u.id)) {
          toKeepInPlay.push(u);
          continue;
        }
        const sub = _cardMap.get(u.cardId)?.utilityFields?.utilityType ?? "";
        if (sub === "Item") toKeepInPlay.push(u);
        else toDiscard.push(u);
      }

      // Hand: counter-kept cards auto-discard; everything else stays.
      const handKeepInstances = p.handInstances.filter((c) => !p.counterKeptIds.has(c.id));
      const handDiscardInstances = p.handInstances.filter((c) => p.counterKeptIds.has(c.id));
      const handKeepIds: string[] = [];
      const handDiscardIds: string[] = [];
      for (const inst of handKeepInstances) handKeepIds.push(inst.cardId);
      for (const inst of handDiscardInstances) handDiscardIds.push(inst.cardId);

      const newStam = p.armorActive ? Math.max(0, 10 - p.armorWeight) : 10;

      return {
        ...p,
        attackDefendZone: [],
        hand: handKeepIds,
        handInstances: handKeepInstances,
        utilityInPlay: toKeepInPlay,
        // Stun does not carry across rounds — clears at round end.
        stunned: false,
        discard: [
          ...p.discard,
          ...toDiscard.map((u) => u.cardId),
          ...handDiscardIds,
        ],
        equipSlots: p.equipSlots.map((s) =>
          (s.disarmTokens ?? 0) > 0
            ? { ...s, disarmTokens: (s.disarmTokens ?? 0) - 1 }
            : { ...s, tapped: false, disarmed: false },
        ),
        sigMoveSlots: p.sigMoveSlots.map((s) => ({
          ...s,
          used: s.inPlay ? true : s.used,
          inPlay: false,
        })),
        stamina: newStam,
        maxStamina: newStam,
        adValueOverrides: new Map<string, number>(),
        counterActivatedIds: new Set<string>(),
        counterKeptIds: new Set<string>(),
        counterRefUtilityIds: new Set<string>(),
        utilityPlayed: [],
        playedCards: [],
        tokenAreaTapped: new Set<string>(),
        pendingBuffs: pruneBuffs(p.pendingBuffs, newRound),
        actionPhaseDraws: 0,
        soulreaverUsedThisRound: false,
        igniteBladePowerPending: false,
        tookDamageThisRound: false,
        attacksDealtDamageThisRound: 0,
        damageDealtByAttackThisRound: [],
        // Counter / hook per-round trackers.
        cardsDrawnThisRound: 0,
        onceFlagsThisRound: {},
        wasStunnedThisRound: false,
      };
    });
  }

  next = {
    ...next,
    round: newRound,
    aggressor: newAggressor,
    priority: newAggressor,
    adModes: new Map<string, AdMode>(),
    resolvedInstanceIds: new Set<string>(),
    combatSlots: [],
    combatPopup: null,
    reactionChain: null,
    actionChain: null,
    pendingPlay: null,
    counterDialog: null,
    counterActivatedDialog: null,
    pendingPrompt: null,
    aggressorPassed: false,
    defenderPassed: false,
    mustAttackWarning: false,
    attacksThisRound: 0,
    chainStreak: 0,
    consecutiveCounters: 0,
    planningDiscardSet: new Set<string>(),
    spartacusActivatedSet: new Set<1 | 2>(),
    phase: "recover",
  };
  next = pushLog(next, "system", "♻ Recover phase: equipment readied, stamina replenished.");
  trace.emit({
    category: "phase.transition",
    label: "end-round",
    summary: `phase ${gs.phase} → recover (round ${gs.round} ended)`,
    round: gs.round,
    details: {
      from: gs.phase,
      to: "recover",
      reason: "end-round",
      attacksThisRound: gs.attacksThisRound ?? 0,
      p1Health: next.players[1].health,
      p2Health: next.players[2].health,
    },
  });
  return next;
}

/** Move from `recover` → `planning-p1` (spec §4.5 — zc). */
export function proceedToPlanning(gs: GameState): GameState {
  trace.emit({
    category: "phase.transition",
    label: "to-planning",
    summary: `phase ${gs.phase} → planning-p1`,
    round: gs.round,
    details: { from: gs.phase, to: "planning-p1", reason: "proceed-to-planning" },
  });
  return { ...gs, phase: "planning-p1" };
}

/**
 * Planning confirm — discard the selected hand instances and draw back to 5.
 * Then proceed to the next planning step (p2) or the next round. (yi)
 */
export function planningConfirm(
  gs: GameState,
  n: 1 | 2,
  toDiscardInstanceIds: string[],
  cardMap?: Map<string, Card>,
): GameState {
  let next = gs;
  // Pre-capture for trace.
  const _planningDiscardForTrace = gs.players[n].handInstances.filter((c) => toDiscardInstanceIds.includes(c.id));
  if (_planningDiscardForTrace.length > 0) {
    trace.emit({
      category: "hand.discard",
      label: "planning",
      summary: `P${n} planning discard: ${_planningDiscardForTrace.length} card(s)`,
      round: gs.round,
      activePlayer: n,
      details: { playerNum: n, count: _planningDiscardForTrace.length, cardIds: _planningDiscardForTrace.map((h) => h.cardId), instanceIds: _planningDiscardForTrace.map((h) => h.id) },
    });
  }
  next = updatePlayer(next, n, (p) => {
    const handKeepInstances = p.handInstances.filter((c) => !toDiscardInstanceIds.includes(c.id));
    const handDiscardCardIds = p.handInstances
      .filter((c) => toDiscardInstanceIds.includes(c.id))
      .map((c) => c.cardId);
    return {
      ...p,
      handInstances: handKeepInstances,
      hand: handKeepInstances.map((h) => h.cardId),
      discard: [...p.discard, ...handDiscardCardIds],
    };
  });
  // Draw back up to 5
  const handLen = next.players[n].hand.length;
  const drawCount = Math.max(0, 5 - handLen);
  if (drawCount > 0) next = drawCards(next, n, drawCount);
  next = pushLog(
    next,
    next.players[n].name,
    `Planning: discarded ${toDiscardInstanceIds.length}, drew ${drawCount}`,
  );

  // Advance phase
  const fromPhase = gs.phase;
  if (gs.phase === "planning-p1") {
    next = { ...next, phase: "planning-p2", planningDiscardSet: new Set<string>() };
    trace.emit({
      category: "phase.transition",
      label: "planning",
      summary: `phase ${fromPhase} → planning-p2 (P${n} confirmed)`,
      round: next.round,
      activePlayer: n,
      details: {
        from: fromPhase,
        to: "planning-p2",
        discarded: toDiscardInstanceIds.length,
        drew: drawCount,
      },
    });
  } else {
    trace.emit({
      category: "phase.transition",
      label: "planning-end",
      summary: `phase ${fromPhase} → begin round ${next.round + 1} (P${n} confirmed)`,
      round: next.round,
      activePlayer: n,
      details: {
        from: fromPhase,
        to: "playing",
        discarded: toDiscardInstanceIds.length,
        drew: drawCount,
      },
    });
    next = beginRound({ ...next, planningDiscardSet: new Set<string>() }, cardMap);
  }
  return next;
}

/** Pass button (eo) — flips priority/aggressor or ends the round per spec §4.3. */
export function pass(gs: GameState, cardMap?: Map<string, Card>): GameState {
  if (gs.winner) return gs;
  const me = gs.priority;
  // Engine #3 hard guard: if the round has had no attacks and both players
  // have already passed once, the original aggressor MUST attack — reject
  // any further pass attempt server-side.
  if (
    gs.mustAttackWarning &&
    me === (gs.originalAggressor ?? gs.aggressor ?? me) &&
    gs.subPhase === "aggressor-pre-attack"
  ) {
    trace.emit({
      category: "pass",
      label: "rejected-must-attack",
      summary: `P${me} pass REJECTED — must-attack guard`,
      round: gs.round,
      activePlayer: me,
      details: {
        player: me,
        subPhase: gs.subPhase,
        originalAggressor: gs.originalAggressor,
        aggressor: gs.aggressor,
        attacksThisRound: gs.attacksThisRound ?? 0,
      },
    });
    return pushLog(
      gs,
      "system",
      `⚠ ${gs.players[me].name} must play an attack — cannot pass again this round.`,
    );
  }
  trace.emit({
    category: "pass",
    summary: `P${me} pass (subPhase=${gs.subPhase}, aggressor=P${gs.aggressor ?? "?"})`,
    round: gs.round,
    activePlayer: me,
    details: {
      player: me,
      subPhase: gs.subPhase,
      aggressor: gs.aggressor,
      aggressorPassed: gs.aggressorPassed,
      defenderPassed: gs.defenderPassed,
      hasReactionChain: !!gs.reactionChain,
      hasActionChain: !!gs.actionChain,
      attacksThisRound: gs.attacksThisRound ?? 0,
    },
  });
  let next = pushLog(gs, gs.players[me].name, `${gs.players[me].name} Passed`);
  next = { ...next, reactionChain: null, freeReactionWindow: false };

  // Saving Stamina stance: passing grants +1 stamina. Do NOT fire during a
  // reaction chain or free reaction window — only on the genuine
  // aggressor-pre-attack “end my turn” pass.
  const inReactionWindow = !!gs.reactionChain || !!gs.freeReactionWindow;
  if (cardMap && !inReactionWindow && gs.subPhase === "aggressor-pre-attack") {
    const stanceId = next.players[me].stanceCardId;
    if (stanceId) {
      const c = cardMap.get(stanceId);
      if (c && c.name === "Saving Stamina") {
        next = updatePlayer(next, me, (pl) => ({ ...pl, stamina: pl.stamina + 1 }));
        next = pushLog(next, gs.players[me].name, "Saving Stamina — passed, +1 stamina");
      }
    }
  }

  if (next.subPhase !== "aggressor-pre-attack") return next;

  const aggressor = next.aggressor ?? 1;
  if (me === aggressor && !next.aggressorPassed) {
    const o = opp(aggressor);
    trace.emit({
      category: "priority.shift",
      label: "aggressor-flip",
      summary: `priority P${me} → P${o} (aggressor flips after pass)`,
      round: next.round,
      activePlayer: o,
      details: { from: me, to: o, reason: "aggressor-passes-no-attack", newAggressor: o },
    });
    next = {
      ...next,
      aggressorPassed: true,
      aggressor: o,
      priority: o,
      subPhase: "aggressor-pre-attack",
    };
    next = pushLog(next, "system", `🔄 Priority → ${next.players[o].name} (now aggressor)`);
    return next;
  }
  // Second consecutive pass.
  // → If at least one attack has happened this round, the round ends.
  // → Otherwise, force the original aggressor back to play an attack.
  if ((next.attacksThisRound ?? 0) >= 1) {
    trace.emit({
      category: "pass",
      label: "both-passed-end-round",
      summary: `both players passed — round ${next.round} ends`,
      round: next.round,
      details: { attacksThisRound: next.attacksThisRound ?? 0 },
    });
    return endRound(next, new Map());
  }
  const original = next.originalAggressor ?? aggressor;
  trace.emit({
    category: "priority.shift",
    label: "force-original-aggressor",
    summary: `priority → P${original} (must-attack: no attacks yet this round)`,
    round: next.round,
    activePlayer: original,
    details: { to: original, reason: "both-passed-no-attacks" },
  });
  next = {
    ...next,
    aggressor: original,
    priority: original,
    aggressorPassed: false,
    defenderPassed: false,
    subPhase: "aggressor-pre-attack",
    mustAttackWarning: true,
  };
  next = pushLog(
    next,
    "system",
    `⚠ ${next.players[original].name} must play an attack before the round can end.`,
  );
  return next;
}

// ─── play handlers (Js / Nl / no / er / Os / _l) ────────────────────────────

/**
 * Fire onAttackPlay hooks for the active player's champion + armor after an
 * attack has been played and `attacksThisRound` has been incremented.
 *
 * Must be called from EVERY attack-playing path (playAttackDefend, playEquip,
 * playSig) so passive in-play hooks like Crixus (champion) and Bloodforged
 * Plate (armor) trigger on every attack regardless of which kind of card was
 * played.
 *
 * The `source` argument is for trace clarity ("playAttackDefend", "playEquip",
 * "playSig").
 */
function fireOnAttackPlayPassives(
  gs: GameState,
  n: 1 | 2,
  playedCard: Card,
  cardMap: Map<string, Card>,
  source: string,
): GameState {
  let next = gs;
  // Champion onAttackPlay (e.g. Crixus, Gaul-Breaker — 3rd attack draws).
  {
    const { card: championCard, hooks: championHooks } = getChampionHooksFor(next, n, cardMap);
    if (championHooks?.onAttackPlay && championCard) {
      const ctx: CardHookCtx = {
        self: next.players[n],
        opponent: next.players[opp(n)],
        game: next,
        card: championCard,
        attackCard: playedCard,
      };
      const r = championHooks.onAttackPlay(ctx) ?? {};
      trace.emit({
        category: "hook.fire",
        label: "onAttackPlay",
        summary: `${championCard.name} onAttackPlay fired (P${n} attack #${next.players[n].attacksThisRound ?? 0})`,
        round: next.round,
        activePlayer: n,
        details: {
          card: championCard.name,
          hook: "onAttackPlay",
          playerNum: n,
          source: `${source}:champion`,
          playedCard: playedCard.name,
          playedCardId: playedCard.id,
          attacksThisRound: next.players[n].attacksThisRound ?? 0,
          result: r as unknown as Record<string, unknown>,
        },
      });
      next = applyResults(next, n, [r], cardMap);
    }
  }
  // Armor onAttackPlay (e.g. Bloodforged Plate — 2nd attack draws).
  // Armor is in play but never "played this turn," so it has to be asked
  // explicitly here. Gated on armorActive so a broken armor does not fire.
  {
    const armorState = next.players[n];
    if (armorState.armorActive && armorState.armorCardId) {
      const armorCard = cardMap.get(armorState.armorCardId);
      const armorHook = armorCard ? getCardHooks(armorCard)?.onAttackPlay : undefined;
      if (armorCard && armorHook) {
        const ctx: CardHookCtx = {
          self: next.players[n],
          opponent: next.players[opp(n)],
          game: next,
          card: armorCard,
          attackCard: playedCard,
        };
        const r = armorHook(ctx) ?? {};
        trace.emit({
          category: "hook.fire",
          label: "onAttackPlay",
          summary: `${armorCard.name} onAttackPlay fired (armor passive, P${n} attack #${next.players[n].attacksThisRound ?? 0})`,
          round: next.round,
          activePlayer: n,
          details: {
            card: armorCard.name,
            hook: "onAttackPlay",
            playerNum: n,
            source: `${source}:armor`,
            playedCard: playedCard.name,
            playedCardId: playedCard.id,
            attacksThisRound: next.players[n].attacksThisRound ?? 0,
            result: r as unknown as Record<string, unknown>,
          },
        });
        next = applyResults(next, n, [r], cardMap);
      }
    }
  }
  return next;
}

/**
 * Play A/D card from hand (Js).
 * Returns updated state.  Combat logic / popups handled by caller via flags.
 */
export function playAttackDefend(
  gs: GameState,
  n: 1 | 2,
  handInstanceId: string,
  mode: AdMode,
  cardMap: Map<string, Card>,
): GameState {
  const player = gs.players[n];
  const handInst = player.handInstances.find((h) => h.id === handInstanceId);
  if (!handInst) return pushLog(gs, "system", "Card not in hand");
  const card = cardMap.get(handInst.cardId);
  if (!card) return pushLog(gs, "system", "Card not found in registry");

  const baseCost = staminaCostOf(card);
  const keywords = effectiveKeywordsForBuffFire(card);
  const weight = card.attackDefendFields?.weight ?? "";
  trace.emit({
    category: "play",
    label: "a/d-attempt",
    summary: `P${n} plays ${card.name} as ${mode}`,
    round: gs.round,
    activePlayer: gs.priority,
    details: { player: n, card: card.name, cardId: card.id, mode, baseCost, keywords, weight, handInstanceId },
  });
  const { fired, remaining } = firePendingBuffs(player.pendingBuffs, mode, keywords, weight, gs.round);
  const stanceRed = stanceCostReduction(player, card, mode, cardMap);
  const buffRed = costReductionFromFired(fired);
  const cost = Math.max(0, baseCost - stanceRed - buffRed);
  if (player.stamina < cost) {
    trace.emit({
      category: "error",
      summary: `P${n} can't play ${card.name}: insufficient stamina (${player.stamina} < ${cost})`,
      details: { player: n, card: card.name, baseCost, stanceRed, buffRed, cost, stamina: player.stamina },
    });
    return pushLog(gs, "system", "⚠ Not enough stamina");
  }
  trace.emit({
    category: "stamina",
    summary: `P${n} pay ${cost} stamina (${player.stamina} → ${player.stamina - cost}) for ${card.name}`,
    round: gs.round,
    activePlayer: gs.priority,
    details: { player: n, card: card.name, baseCost, stanceRed, buffRed, finalCost: cost, before: player.stamina, after: player.stamina - cost },
  });

  // Build copy instance
  const copy: ZoneCard = { id: uid("ad_"), cardId: handInst.cardId };

  // Compute power/block adjustments from fired buffs
  const powerMod = fired
    .filter((b) => b.effect.kind === "power-bonus")
    .reduce((s, b) => s + (b.effect.amount ?? 0), 0);
  const blockMod = fired
    .filter((b) => b.effect.kind === "block-bonus")
    .reduce((s, b) => s + (b.effect.amount ?? 0), 0);

  // Base-power-multiplier (Crushing Strength): scales the PRINTED power BEFORE
  // any flat power-bonus is added. Only applies to attack mode. The buff fires
  // on `next-attack` so it lives in `fired`, not `remaining` — iterate `fired`.
  let basePowerFactor = 1;
  if (mode === "attack") {
    for (const b of fired) {
      if (b.effect.kind !== "base-power-multiplier") continue;
      const f = b.effect.factor ?? 1;
      if (f > basePowerFactor) basePowerFactor = f;
    }
  }

  // Quick-power-bonus (Ignite Blade): the buff has trigger `round-end` and
  // stays in `remaining` rather than firing on this play. resolveCombat reads
  // it from pendingBuffs at damage time, but we ALSO bake it into the
  // displayed power here so the combat zone / active combat popup match the
  // damage actually dealt. Applies only when the played card will count as
  // Quick — printed Quick OR a fired gain-quick (Crushing Strength).
  const quickBonusPlay =
    mode === "attack" && willBeQuickAtPlay(card, fired)
      ? quickPowerBonusFromBuffs(remaining)
      : 0;

  // ───────────────────────────────────────────────────────────────────────
  // ENGINE INVARIANT — displayed power/block on a played card.
  //
  // Every buff that modifies the NUMERIC power or block of a played A/D /
  // equipment / sig card must be folded into `adValueOverrides` here at
  // play time. The combat-zone UI (CombatDock) and active-combat popup
  // (ActiveCombatPopup) read displayed values via `instanceValue`, which
  // returns the override when present. If a future buff effect changes a
  // displayed number but is NOT baked here, the UI will show the printed
  // value while damage uses the post-buff value (the Ignite Blade bug).
  //
  // Current attack-mode contributors (sum at this site):
  //   power-bonus            → powerMod        (additive, from `fired`)
  //   base-power-multiplier  → basePowerFactor (multiplicative, from `fired`)
  //   quick-power-bonus      → quickBonusPlay  (additive, from `remaining`,
  //                                              gated on willBeQuickAtPlay)
  // Current defend-mode contributors:
  //   block-bonus            → blockMod        (additive, from `fired`)
  //
  // Effects that intentionally do NOT bake here:
  //   gain-* keyword grants (rendered as keyword chips, not numbers)
  //   damage-multiplier    (post-block in damage calc; not a displayed PWR)
  //   cost-reduction       (changes stamina, not displayed PWR/BLK)
  //
  // The same contributor list is mirrored in playEquip and playSig. When
  // adding a new effect kind that modifies a displayed number, update all
  // three play sites in lockstep.
  // ───────────────────────────────────────────────────────────────────────
  // Pre-compute override decision so we can emit a structured trace entry
  // showing the contributors (printed base, multiplier, flat mod, quick
  // bonus) and the final displayed value. Mirrors the math inside the
  // updatePlayer callback below.
  if (mode === "attack" && (powerMod !== 0 || basePowerFactor > 1 || quickBonusPlay > 0)) {
    const base = safeNum(card.attackDefendFields?.powerValue ?? 0);
    const finalVal = Math.max(0, base * basePowerFactor + powerMod + quickBonusPlay);
    trace.emit({
      category: "override.set",
      label: "a/d-attack",
      summary: `${card.name} PWR ${base} → ${finalVal} (=${base}×${basePowerFactor}+${powerMod}+${quickBonusPlay})`,
      details: { copyId: copy.id, card: card.name, base, basePowerFactor, powerMod, quickBonusPlay, finalVal },
    });
  } else if (mode === "defend" && blockMod !== 0) {
    const base = safeNum(card.attackDefendFields?.blockValue ?? 0);
    const finalVal = Math.max(0, base + blockMod);
    trace.emit({
      category: "override.set",
      label: "a/d-defend",
      summary: `${card.name} BLK ${base} → ${finalVal} (=${base}+${blockMod})`,
      details: { copyId: copy.id, card: card.name, base, blockMod, finalVal },
    });
  }

  let next = gs;
  // ───────────────────────────────────────────────────────────────────────
  // Phase B push-time mutation rules:
  //   ATTACK mode → buffs are NOT consumed at push, adValueOverrides are
  //     NOT installed, and no CombatSlot is created. We pay stamina, move
  //     the source card to discard, drop the visual copy in
  //     attackDefendZone, then push an `attack-play` deferred entry. The
  //     resolver (resolveAttackPlay) fires the snapshotted buffs, installs
  //     the override, and creates the slot when the entry pops off the
  //     stack. If the attack is negated, none of those mutations are ever
  //     applied — stamina is refunded by resolveNegateEntry.
  //   DEFEND mode → unchanged Phase A behaviour: buffs consume immediately,
  //     overrides install immediately, mechanics + onDefendPlay hook fire
  //     immediately, attachBlock + reaction chain open right here. Defends
  //     are not stack-deferred (they latch onto an existing combat slot).
  // ───────────────────────────────────────────────────────────────────────
  next = updatePlayer(next, n, (p) => {
    const ovr = new Map(p.adValueOverrides);
    if (mode === "defend" && blockMod !== 0) {
      const base = safeNum(card.attackDefendFields?.blockValue ?? 0);
      ovr.set(copy.id, Math.max(0, base + blockMod));
    }
    const handInstances = p.handInstances.filter((h) => h.id !== handInstanceId);
    const handIdx = p.hand.indexOf(handInst.cardId);
    const hand = handIdx === -1
      ? p.hand.slice()
      : [...p.hand.slice(0, handIdx), ...p.hand.slice(handIdx + 1)];
    return {
      ...p,
      handInstances,
      hand,
      stamina: p.stamina - cost,
      // The played source card moves to discard immediately. The entry in
      // attackDefendZone (with a fresh copy.id) is a visual reference only —
      // it shows the card during combat / in the active combat popup, then
      // clears at end-of-round.
      discard: [...p.discard, handInst.cardId],
      attackDefendZone: [...p.attackDefendZone, { id: copy.id, cardId: copy.cardId, side: mode === "attack" ? "attack" : "defend" }],
      // Defend consumes its fired buffs at push. Attack defers consumption
      // to resolveAttackPlay (by id from snap.firedBuffIds).
      pendingBuffs: mode === "defend" ? remaining : p.pendingBuffs,
      adValueOverrides: ovr,
    };
  });

  // Track mode per instance
  const adModes = new Map(next.adModes ?? new Map<string, AdMode>());
  adModes.set(copy.id, mode);
  next = { ...next, adModes };

  next = pushLog(
    next,
    gs.players[n].name,
    `Played ${card.name} as ${mode === "attack" ? "⚔ Attack" : "🛡 Defend"}`,
  );

  if (mode === "attack") {
    // ─── ATTACK push half ───
    // Compute the displayed power that the resolver will install on the
    // combat slot copy id. This mirrors the legacy in-place computation but
    // is captured in the snapshot rather than written to adValueOverrides
    // right now. We still emit the trace event so the play-attempt log line
    // is human-readable.
    let displayOverride: number | undefined;
    if (powerMod !== 0 || basePowerFactor > 1 || quickBonusPlay > 0) {
      const base = safeNum(card.attackDefendFields?.powerValue ?? 0);
      displayOverride = Math.max(0, base * basePowerFactor + powerMod + quickBonusPlay);
    }
    // Granted keywords from fired buffs only — hook-derived slot grants are
    // produced when the onAttackPlay hook runs inside resolveAttackPlay, so
    // they get folded into the CombatSlot at resolve time. (The current
    // resolver re-reads `r.slotKeywordGrants` from the hook return value but
    // does not yet merge them into the slot — that's a known follow-up; for
    // now we ship the buff-derived grants only, which matches the bulk of
    // existing in-game grants.)
    const grantedKw = keywordGrantsFromFired(fired);
    for (const kw of grantedKw) {
      const sourceBuff = fired.find((b) =>
        b.effect.kind === `gain-${kw.toLowerCase()}` ||
        (b.effect.kind === "gain-quick" && kw.toLowerCase() === "quick") ||
        (b.effect.kind === "gain-piercing" && kw.toLowerCase() === "piercing") ||
        (b.effect.kind === "gain-stun" && kw.toLowerCase() === "stun") ||
        (b.effect.kind === "gain-serrated" && kw.toLowerCase() === "serrated") ||
        (b.effect.kind === "gain-displace" && kw.toLowerCase() === "displace") ||
        (b.effect.kind === "gain-crush" && kw.toLowerCase() === "crush") ||
        (b.effect.kind === "gain-reflect" && kw.toLowerCase().startsWith("reflect")) ||
        (b.effect.kind === "gain-disarm" && kw.toLowerCase().startsWith("disarm"))
      );
      trace.emit({
        category: "keyword.grant",
        label: "from-fired-buff",
        summary: `${card.name} +${kw}${sourceBuff?.sourceCardName ? ` (from ${sourceBuff.sourceCardName})` : ""} [push]`,
        details: {
          card: card.name,
          copyId: copy.id,
          keyword: kw,
          source: sourceBuff?.sourceCardName ?? "unknown buff",
          buffId: sourceBuff?.id,
          buffEffect: sourceBuff?.effect,
          pushed: true,
        },
      });
    }
    // Build the snapshot and push the deferred attack-play stack entry.
    const snapshot: AttackPlaySnapshot = {
      source: "playAttackDefend",
      cardId: card.id,
      copyInstanceId: copy.id,
      refundCost: cost,
      firedBuffIds: fired.map((b) => b.id),
      grantedKeywords: grantedKw,
      displayOverride,
      handInstanceId,
    };
    next = pushLog(
      next,
      "system",
      `🔼 ${card.name} pushed onto the action chain — reactions open before it lands`,
    );
    next = pushActionChain(
      next,
      {
        playerNum: n,
        triggerEvent: "opponent-plays-attack",
        cardId: card.id,
        instanceId: copy.id,
        description: `${card.name} (attack)`,
        deferredEffect: { kind: "attack-play", snapshot },
      },
      "opponent-plays-attack",
      cardMap,
    );
    // Phase B fix: priority shifts to the defender the moment the attack is
    // pushed (not at resolve), so the active player can't chain more of their
    // own cards onto their own attack. The defender decides whether to react
    // (e.g. Pocket Sand / Negate) or pass. Resolution still re-applies these
    // at resolveAttackPlay time, which is a no-op when already set here.
    next = { ...next, priority: opp(n), subPhase: "defender-pre-block" };
    return next;
  }

  // ─── DEFEND push half (unchanged Phase A behaviour) ───
  // Data-driven mechanics for this card (e.g. self-gain-piercing, next-cost-reduction).
  const mechRes = evalMechanics(card, mode, {
    self: next.players[n],
    opponent: next.players[opp(n)],
    cardName: card.name,
    currentRound: next.round,
    selfNum: n,
  });
  next = applyResults(next, n, [mechRes], cardMap);

  // Card-specific onDefendPlay hook.
  const hooks = getCardHooks(card);
  const hookFn = hooks?.onDefendPlay;
  let hookSlotGrants: string[] = [];
  if (hookFn) {
    // Find the opposing slot this defend will attach to (last unresolved
    // slot owned by the opponent). Mirrors the lookup used for
    // weakenOpposingAttackPower below so the hook ctx is consistent.
    const _oppNum = opp(n);
    const _slots = next.combatSlots ?? [];
    let _targetSlot: CombatSlot | undefined;
    for (let i = _slots.length - 1; i >= 0; i--) {
      const s = _slots[i];
      if (!s.resolved && s.attackerNum === _oppNum) {
        _targetSlot = s;
        break;
      }
    }
    const ctx: CardHookCtx = {
      self: next.players[n],
      opponent: next.players[opp(n)],
      game: next,
      card,
      slotId: _targetSlot?.id,
      instanceId: copy.id,
    };
    const r = hookFn(ctx) ?? {};
    trace.emit({
      category: "hook.fire",
      summary: `${card.name} onDefendPlay returned`,
      details: { card: card.name, mode, result: r as unknown as Record<string, unknown> },
    });
    if (r.slotKeywordGrants && r.slotKeywordGrants.length > 0) {
      hookSlotGrants = r.slotKeywordGrants;
    }
    next = applyResults(next, n, [r], cardMap);

    // Phase 3 — Doubled Block: hook asked to open a free extra-defend window.
    if (r.openDoubleDefendWindow && _targetSlot) {
      next = {
        ...next,
        doubleDefendPendingSlotId: _targetSlot.id,
        doubleDefendPlayerNum: n,
      };
      next = pushLog(
        next,
        next.players[n].name,
        `Doubled Block — you may play one more defend against this attack.`,
      );
    }

    // Hook-driven weakening of the opposing attack in the combat slot.
    // Mirror of boostCurrentAttackPower (onAttackPlay) — walks combatSlots
    // from newest to oldest, finds the first unresolved slot owned by the
    // opponent (the attack this defend is blocking), and decreases its
    // displayed power via opponent.adValueOverrides. Clamped at 0.
    const weaken = r.weakenOpposingAttackPower ?? 0;
    if (weaken > 0) {
      const oppNum = opp(n);
      const slots = next.combatSlots ?? [];
      let targetSlot: CombatSlot | undefined;
      for (let i = slots.length - 1; i >= 0; i--) {
        const s = slots[i];
        if (!s.resolved && s.attackerNum === oppNum) {
          targetSlot = s;
          break;
        }
      }
      if (targetSlot) {
        const attackInstanceId = targetSlot.attackCard.id;
        next = updatePlayer(next, oppNum, (pl) => {
          // Resolve base power: prefer existing override; else look up the
          // card definition by cardId (handles synthetic Reflect attacks
          // by falling back to synthetic.power).
          let prevDisplay = pl.adValueOverrides.get(attackInstanceId);
          if (prevDisplay === undefined) {
            const atkCard = cardMap.get(targetSlot!.attackCard.cardId);
            if (atkCard) {
              prevDisplay = safeNum(
                atkCard.signatureMoveFields?.powerValue ??
                atkCard.attackDefendFields?.powerValue ??
                0
              );
            } else {
              prevDisplay = targetSlot!.synthetic?.power ?? 0;
            }
          }
          const newDisplay = Math.max(0, prevDisplay - weaken);
          const ovr = new Map(pl.adValueOverrides);
          ovr.set(attackInstanceId, newDisplay);
          return { ...pl, adValueOverrides: ovr };
        });
        trace.emit({
          category: "power.boost",
          label: "hook-weaken-opposing-attack",
          summary: `${card.name} −${weaken} power to opposing attack`,
          round: next.round,
          activePlayer: n,
          details: {
            card: card.name,
            opposingSlotId: targetSlot.id,
            attackInstanceId,
            weaken,
          },
        });
      }
    }
  }

  // Fold fired gain-* buffs into keyword grants for this defend play.
  const firedKw = keywordGrantsFromFired(fired);
  const grantedKw = Array.from(new Set([...firedKw, ...hookSlotGrants]));
  for (const kw of firedKw) {
    const sourceBuff = fired.find((b) =>
      b.effect.kind === `gain-${kw.toLowerCase()}` ||
      (b.effect.kind === "gain-quick" && kw.toLowerCase() === "quick") ||
      (b.effect.kind === "gain-piercing" && kw.toLowerCase() === "piercing") ||
      (b.effect.kind === "gain-stun" && kw.toLowerCase() === "stun") ||
      (b.effect.kind === "gain-serrated" && kw.toLowerCase() === "serrated") ||
      (b.effect.kind === "gain-displace" && kw.toLowerCase() === "displace") ||
      (b.effect.kind === "gain-crush" && kw.toLowerCase() === "crush") ||
      (b.effect.kind === "gain-reflect" && kw.toLowerCase().startsWith("reflect")) ||
      (b.effect.kind === "gain-disarm" && kw.toLowerCase().startsWith("disarm"))
    );
    trace.emit({
      category: "keyword.grant",
      label: "from-fired-buff",
      summary: `${card.name} +${kw}${sourceBuff?.sourceCardName ? ` (from ${sourceBuff.sourceCardName})` : ""}`,
      details: {
        card: card.name,
        copyId: copy.id,
        keyword: kw,
        source: sourceBuff?.sourceCardName ?? "unknown buff",
        buffId: sourceBuff?.id,
        buffEffect: sourceBuff?.effect,
      },
    });
  }
  for (const kw of hookSlotGrants) {
    trace.emit({
      category: "keyword.grant",
      label: "from-hook",
      summary: `${card.name} +${kw} (from ${card.name} hook)`,
      details: { card: card.name, copyId: copy.id, keyword: kw, source: `${card.name} hook (onDefendPlay)` },
    });
  }

  // Attach as block on the most recent unresolved attack from opp.
  next = attachBlock(next, n, copy, grantedKw);
  next = {
    ...next,
    combatPopup: { defenderNum: n, blockCard: copy, blockType: "ad" },
    freeReactionWindow: true,
  };
  // Open the reaction window: aggressor gets priority first, then defender.
  // Two consecutive Pass clicks resolve the exchange.
  next = startReactionChain(next);
  return next;
}

/** Attach a block to the latest unresolved attack from `opp(n)`. (Ta) */
export function attachBlock(
  gs: GameState,
  n: 1 | 2,
  blockCard: ZoneCard,
  blockGrantedKeywords?: string[],
): GameState {
  const slots = (gs.combatSlots ?? []).slice();
  for (let i = slots.length - 1; i >= 0; i--) {
    const s = slots[i];
    if (s.attackerNum === opp(n) && !s.resolved && !s.blockCard) {
      const merged = blockGrantedKeywords && blockGrantedKeywords.length
        ? Array.from(new Set([...(s.blockGrantedKeywords ?? []), ...blockGrantedKeywords]))
        : s.blockGrantedKeywords;
      slots[i] = { ...s, blockCard, blockGrantedKeywords: merged };
      break;
    }
  }
  // Phase 2: committing a block is an implicit pass on the attack action
  // chain — the defender has chosen to defend rather than negate. Close the
  // chain so the existing combat reaction window (post-block) can run cleanly.
  let next: GameState = { ...gs, combatSlots: slots };
  if (next.actionChain && next.actionChain.entries.length > 0) {
    const topEntry = next.actionChain.entries[next.actionChain.entries.length - 1];
    // Phase B: attack pushes live on deferredEffect; fall back to legacy snapshot.
    const isAttackTop =
      topEntry.deferredEffect?.kind === "attack-play" ||
      (topEntry as { appliedSnapshot?: { kind?: string } }).appliedSnapshot?.kind === "attack-play";
    if (isAttackTop) {
      trace.emit({
        category: "chain.close",
        label: "block-commit",
        summary: `Action Chain closed (P${n} committed a block on attack)`,
        round: next.round,
        activePlayer: n,
        details: { defender: n, reason: "block-attached", chainLength: next.actionChain.entries.length },
      });
      next = setActionChain(next, null);
    }
  }
  return next;
}

/**
 * Phase 3 — Doubled Block: attach a second defend card to an existing combat
 * slot's `doubleDefendCard` field. Called when a player with
 * `gs.doubleDefendPendingSlotId` plays an additional defend.
 *
 * This is intentionally separate from `attachBlock` because:
 *  - The primary `blockCard` slot is already filled.
 *  - We don't update `combatPopup` (the initial popup already shows).
 *  - We clear `doubleDefendPendingSlotId` after attaching.
 *
 * The block value of `doubleDefendCard` is added to `blockCard`'s value
 * inside `resolveCombat` (see that function's doubleDefendCard branch).
 */
export function attachDoubleDefend(
  gs: GameState,
  n: 1 | 2,
  defendCard: ZoneCard,
  cardMap: Map<string, Card>,
): GameState {
  const slotId = gs.doubleDefendPendingSlotId;
  if (!slotId || gs.doubleDefendPlayerNum !== n) return gs;
  const slots = (gs.combatSlots ?? []).slice();
  const idx = slots.findIndex((s) => s.id === slotId && !s.resolved);
  if (idx === -1) return gs;
  slots[idx] = { ...slots[idx], doubleDefendCard: defendCard };
  const card = cardMap.get(defendCard.cardId);
  let next: GameState = {
    ...gs,
    combatSlots: slots,
    doubleDefendPendingSlotId: undefined,
    doubleDefendPlayerNum: undefined,
  };
  next = pushLog(
    next,
    gs.players[n].name,
    `Doubled Block — ${card?.name ?? defendCard.cardId} added as second defend (block values summed).`,
  );
  trace.emit({
    category: "block.attach",
    label: "double-defend",
    summary: `P${n} attached double-defend ${card?.name ?? defendCard.cardId} on slot ${slotId}`,
    round: gs.round,
    activePlayer: n,
    details: { playerNum: n, slotId, cardId: defendCard.cardId, instanceId: defendCard.id },
  });
  return next;
}

/**
 * Play equipment as attack/defend. (Nl)
 * - Slot must not be tapped or disarmed.
 * - Charges stamina; taps the slot.
 */
export function playEquip(
  gs: GameState,
  n: 1 | 2,
  slotIdx: number,
  mode: AdMode,
  cardMap: Map<string, Card>,
): GameState {
  const player = gs.players[n];
  const slot = player.equipSlots[slotIdx];
  if (!slot || !slot.cardId || slot.tapped || slot.disarmed) {
    return pushLog(gs, "system", "Equipment slot unavailable");
  }
  const card = cardMap.get(slot.cardId);
  if (!card) return pushLog(gs, "system", "Equipment card not in registry");

  const baseCost = staminaCostOf(card);
  const keywords = effectiveKeywordsForBuffFire(card);
  const weight = mode === "attack"
    ? card.equipmentFields?.attackWeight ?? ""
    : card.equipmentFields?.defendWeight ?? "";
  trace.emit({
    category: "play",
    label: "equip-attempt",
    summary: `P${n} plays equipment ${card.name} as ${mode}`,
    round: gs.round,
    activePlayer: gs.priority,
    details: { player: n, card: card.name, mode, slotIdx, baseCost, keywords, weight },
  });
  const { fired, remaining } = firePendingBuffs(player.pendingBuffs, mode, keywords, weight, gs.round);
  // Apply stance / utility-driven reductions (Committed Form, Zeus' Wrathbrand)
  // to equipment plays as well — a heavy weapon is still a heavy attack.
  const stanceRed = stanceCostReduction(player, card, mode, cardMap);
  const cost = Math.max(0, baseCost - stanceRed - costReductionFromFired(fired));
  if (player.stamina < cost) {
    trace.emit({
      category: "error",
      summary: `P${n} can't play equipment ${card.name}: insufficient stamina`,
      details: { player: n, card: card.name, baseCost, cost, stamina: player.stamina },
    });
    return pushLog(gs, "system", "⚠ Not enough stamina");
  }

  const copy: ZoneCard = { id: uid("eq_"), cardId: slot.cardId };
  const powerMod = fired.filter((b) => b.effect.kind === "power-bonus").reduce((s, b) => s + (b.effect.amount ?? 0), 0);
  const blockMod = fired.filter((b) => b.effect.kind === "block-bonus").reduce((s, b) => s + (b.effect.amount ?? 0), 0);

  // Base-power-multiplier (Crushing Strength) applies to equipment weapon
  // attacks too. The buff fires on `next-attack` so it lives in `fired`,
  // not `remaining` — iterate `fired`.
  let basePowerFactor = 1;
  if (mode === "attack") {
    for (const b of fired) {
      if (b.effect.kind !== "base-power-multiplier") continue;
      const f = b.effect.factor ?? 1;
      if (f > basePowerFactor) basePowerFactor = f;
    }
  }

  // Quick-power-bonus (Ignite Blade) bake-into-display — see playAttackDefend
  // for the same pattern. resolveCombat re-reads pendingBuffs for the actual
  // damage; this just keeps the displayed power in sync.
  const quickBonusPlay =
    mode === "attack" && willBeQuickAtPlay(card, fired)
      ? quickPowerBonusFromBuffs(remaining)
      : 0;

  trace.emit({
    category: "status.exhaust",
    label: "weapon-used",
    summary: `P${n} weapon ${card.name} exhausted (slot ${slotIdx}, ${mode})`,
    round: gs.round,
    activePlayer: n,
    details: { playerNum: n, slotIdx, weaponCardId: card.id, weaponName: card.name, mode, source: "playEquip" },
  });
  // ───────────────────────────────────────────────────────────────────────
  // Phase B push-time mutation rules — same shape as playAttackDefend.
  //   ATTACK → tap slot, pay stamina, drop visual copy in attackDefendZone,
  //     then push `attack-play` deferred entry. Buffs/overrides/CombatSlot
  //     are deferred to resolveAttackPlay. If negated, resolveNegateEntry
  //     untaps the slot (snap.equipSlotIdx) and refunds stamina; the visual
  //     copy in attackDefendZone stays (matches A/D "card stays in discard"
  //     behaviour — it'll be cleared at end-of-round).
  //   DEFEND → unchanged: tap slot, consume buffs, install override,
  //     attachBlock, open reaction window.
  // ───────────────────────────────────────────────────────────────────────
  let next = updatePlayer(gs, n, (p) => {
    const slots = p.equipSlots.slice();
    slots[slotIdx] = { ...slots[slotIdx], tapped: true };
    const ovr = new Map(p.adValueOverrides);
    if (mode === "defend" && blockMod !== 0) {
      const base = safeNum(card.equipmentFields?.blockValue ?? 0);
      ovr.set(copy.id, Math.max(0, base + blockMod));
    }
    return {
      ...p,
      equipSlots: slots,
      stamina: p.stamina - cost,
      attackDefendZone: [...p.attackDefendZone, { id: copy.id, cardId: copy.cardId, side: mode === "attack" ? "attack" : "defend" }],
      pendingBuffs: mode === "defend" ? remaining : p.pendingBuffs,
      adValueOverrides: ovr,
    };
  });
  const adModes = new Map(next.adModes ?? new Map<string, AdMode>());
  adModes.set(copy.id, mode);
  next = { ...next, adModes };
  next = pushLog(next, gs.players[n].name, `Equipped ${card.name} as ${mode === "attack" ? "⚔" : "🛡"}`);

  // Fold fired gain-* buffs into keyword grants for this play.
  const firedGrants = keywordGrantsFromFired(fired);
  // Also merge any pending keyword grants on this equip slot (e.g. Hookfang Shiv's
  // self-granted Chain after another Chain attack dealt damage this round).
  const pendingGrants = slot.pendingKeywordGrants ?? [];
  const grantedKw = Array.from(new Set([...firedGrants, ...pendingGrants]));
  if (pendingGrants.length > 0) {
    for (const kw of pendingGrants) {
      trace.emit({
        category: "keyword.grant",
        label: "from-equip-pending",
        summary: `${card.name} +${kw} (from equip slot pending grants)`,
        details: { card: card.name, copyId: copy.id, keyword: kw, source: `equip slot ${slotIdx} pendingKeywordGrants` },
      });
    }
  }

  if (mode === "attack") {
    // ─── ATTACK push half ───
    let displayOverride: number | undefined;
    if (powerMod !== 0 || basePowerFactor > 1 || quickBonusPlay > 0) {
      const base = safeNum(card.equipmentFields?.powerValue ?? 0);
      displayOverride = Math.max(0, base * basePowerFactor + powerMod + quickBonusPlay);
    }
    const snapshot: AttackPlaySnapshot = {
      source: "playEquip",
      cardId: card.id,
      copyInstanceId: copy.id,
      refundCost: cost,
      firedBuffIds: fired.map((b) => b.id),
      grantedKeywords: grantedKw,
      displayOverride,
      equipSlotIdx: slotIdx,
    };
    next = pushLog(
      next,
      "system",
      `🔼 ${card.name} (equip) pushed onto the action chain — reactions open before it lands`,
    );
    next = pushActionChain(
      next,
      {
        playerNum: n,
        triggerEvent: "opponent-plays-attack",
        cardId: card.id,
        instanceId: copy.id,
        description: `${card.name} (equip attack)`,
        deferredEffect: { kind: "attack-play", snapshot },
      },
      "opponent-plays-attack",
      cardMap,
    );
    // Phase B fix: shift priority to defender on push (see playAttackDefend).
    next = { ...next, priority: opp(n), subPhase: "defender-pre-block" };
  } else {
    next = attachBlock(next, n, copy, grantedKw);
    next = { ...next, combatPopup: { defenderNum: n, blockCard: copy, blockType: "equip" }, freeReactionWindow: true };
    next = startReactionChain(next);
  }
  return next;
}

/**
 * Play signature move (no). Mode = "attack" | "defend" | null (utility).
 * - Slot must not be used or in-play.
 * - Charges stamina (or 1 stamina with Channel Focus 5-Focus path).
 */
export function playSig(
  gs: GameState,
  n: 1 | 2,
  slotIdx: number,
  mode: AdMode | null,
  cardMap: Map<string, Card>,
): GameState {
  const player = gs.players[n];
  const slot = player.sigMoveSlots[slotIdx];
  if (!slot || slot.used || slot.inPlay) return pushLog(gs, "system", "Sig slot unavailable");
  const card = cardMap.get(slot.cardId);
  if (!card) return pushLog(gs, "system", "Sig card not in registry");

  // ─── Channel Focus precondition: requires ≥5 Focus, discounts cost to 1 + grants Piercing
  const isChannelFocus = nameIs(card, "Channel Focus");
  let channelFocusActive = false;
  if (isChannelFocus) {
    const focusCount = focusTokenCount(player, cardMap);
    if (focusCount < 5) {
      return pushLog(gs, "system", `Channel Focus requires 5 Focus tokens (you have ${focusCount})`);
    }
    channelFocusActive = true;
  }

  // ─── Ignite Blade precondition: must have a Quick Attack/Defend card in hand.
  //     Restricted to cardType === "Attack/Defend" — equipment, signature moves,
  //     stances, utilities, etc. do NOT satisfy the precondition even if they
  //     somehow carry the Quick keyword. Quick may live on attackKeywords (the
  //     canonical home), defendKeywords, legacy generic keywords, or the
  //     legacy attackType/defendType strings.
  if (nameIs(card, "Ignite Blade")) {
    const hasQuick = player.handInstances.some((h) => {
      const hc = cardMap.get(h.cardId);
      if (!hc) return false;
      if (hc.cardType !== "Attack/Defend") return false;
      const at = hc.attackDefendFields?.attackType ?? "";
      const dt = hc.attackDefendFields?.defendType ?? "";
      if (/quick/i.test(at) || /quick/i.test(dt)) return true;
      if ((hc.keywords ?? []).some((k) => k.toLowerCase() === "quick")) return true;
      if (hasKeyword(hc.attackKeywords, "Quick")) return true;
      if (hasKeyword(hc.defendKeywords, "Quick")) return true;
      return false;
    });
    if (!hasQuick) return pushLog(gs, "system", "Ignite Blade requires a Quick Attack/Defend card in hand");
  }

  // ─── Quick Learner precondition: opponent must have ≥1 card in discard.
  //     Quick Learner is utility-only — the picked card later goes into the
  //     sig slot and is played normally from there. Block attempts to play
  //     Quick Learner directly as attack/defend.
  const isQuickLearner = nameIs(card, "Quick Learner");
  if (isQuickLearner) {
    if (mode === "attack" || mode === "defend") {
      return pushLog(gs, "system", "Quick Learner must be played as Utility (it copies the picked card into your sig slot)");
    }
    const oppDiscard = gs.players[opp(n)].discard;
    if (oppDiscard.length === 0) {
      return pushLog(gs, "system", "Quick Learner requires opponent to have ≥1 card in discard");
    }
  }

  // Fire pending buffs for attack/defend mode (utility plays don't trigger combat buffs).
  const sigKeywords = effectiveKeywordsForBuffFire(card);
  const sigWeight = card.signatureMoveFields?.attackWeight ?? "";
  trace.emit({
    category: "play",
    label: "sig-attempt",
    summary: `P${n} plays sig ${card.name}${mode ? ` as ${mode}` : " (utility)"}`,
    round: gs.round,
    activePlayer: gs.priority,
    details: { player: n, card: card.name, mode, slotIdx, channelFocusActive, keywords: sigKeywords, weight: sigWeight },
  });
  const { fired: firedSig, remaining: remainingSig } = mode
    ? firePendingBuffs(player.pendingBuffs, mode, sigKeywords, sigWeight, gs.round)
    : { fired: [] as Buff[], remaining: player.pendingBuffs };

  const baseCost = channelFocusActive ? 1 : staminaCostOf(card);
  const buffRed = costReductionFromFired(firedSig);
  // Stance / utility reductions (Committed Form, Zeus' Wrathbrand) also apply
  // to signature-move attacks/defends. Channel Focus's flat cost of 1 still
  // gets the reduction (a heavy sig under Committed Form costs 0).
  const stanceRed = mode ? stanceCostReduction(player, card, mode, cardMap) : 0;
  const cost = Math.max(0, baseCost - stanceRed - buffRed);
  if (player.stamina < cost) return pushLog(gs, "system", "⚠ Not enough stamina");

  const copy: ZoneCard = { id: uid("sig_"), cardId: slot.cardId };

  // Spend 5 Focus for Channel Focus
  let next: GameState = gs;
  if (channelFocusActive) {
    next = consumeFocus(next, n, 5, cardMap);
  }

  // Compute power/block adjustments from fired buffs.
  const sigPowerMod = firedSig.filter((b) => b.effect.kind === "power-bonus").reduce((s, b) => s + (b.effect.amount ?? 0), 0);
  const sigBlockMod = firedSig.filter((b) => b.effect.kind === "block-bonus").reduce((s, b) => s + (b.effect.amount ?? 0), 0);

  // Base-power-multiplier (Crushing Strength) applies to signature-move
  // attacks too. The buff fires on `next-attack` so it lives in `firedSig`,
  // not `remainingSig` — iterate `firedSig`.
  let sigBasePowerFactor = 1;
  if (mode === "attack") {
    for (const b of firedSig) {
      if (b.effect.kind !== "base-power-multiplier") continue;
      const f = b.effect.factor ?? 1;
      if (f > sigBasePowerFactor) sigBasePowerFactor = f;
    }
  }

  // Quick-power-bonus (Ignite Blade) bake-into-display for sig attacks. Note
  // that Ignite Blade itself only buffs A/D-card Quick attacks per its
  // canonical reading, but other quick-power-bonus sources may exist in the
  // future and the resolveCombat loop already aggregates all of them — so we
  // mirror it here for any sig that will count as Quick.
  const sigQuickBonusPlay =
    mode === "attack" && willBeQuickAtPlay(card, firedSig)
      ? quickPowerBonusFromBuffs(remainingSig)
      : 0;

  // ───────────────────────────────────────────────────────────────────────
  // Phase B push-time mutation rules — same shape as playAttackDefend.
  //   ATTACK → mark slot inPlay, pay stamina, drop visual copy in
  //     attackDefendZone, then push `attack-play` deferred entry. Buffs/
  //     overrides/CombatSlot/mechanics/hook/passives all deferred to
  //     resolveAttackPlay. If negated, resolveNegateEntry resets the slot
  //     (snap.sigSlotIdx → inPlay:false) and refunds stamina.
  //   DEFEND → unchanged: consume buffs, install override, attachBlock,
  //     open reaction window.
  //   UTILITY (mode=null) → unchanged: utilityInPlay + playedCards entries,
  //     mechanics/hook fire inline.
  // ───────────────────────────────────────────────────────────────────────
  next = updatePlayer(next, n, (p) => {
    const slots = p.sigMoveSlots.slice();
    slots[slotIdx] = { ...slots[slotIdx], inPlay: true };
    const ovr = new Map(p.adValueOverrides);
    if (mode === "defend" && sigBlockMod !== 0) {
      const base = safeNum(card.signatureMoveFields?.blockValue ?? 0);
      ovr.set(copy.id, Math.max(0, base + sigBlockMod));
    }
    // Visible "Played Cards" zone: only utility-mode sigs (attack/defend
    // sigs already appear in the combat zone and would be redundant here).
    const playedCardsNext = mode
      ? p.playedCards ?? []
      : [...(p.playedCards ?? []), { id: uid("played_"), cardId: copy.cardId }];
    // Attack defers buff consumption to resolveAttackPlay; defend and
    // utility consume immediately.
    const pendingBuffsNext = mode === "attack" ? p.pendingBuffs : remainingSig;
    return {
      ...p,
      sigMoveSlots: slots,
      stamina: p.stamina - cost,
      attackDefendZone: mode
        ? [...p.attackDefendZone, { id: copy.id, cardId: copy.cardId, side: mode === "attack" ? "attack" : "defend" }]
        : p.attackDefendZone,
      utilityInPlay: mode ? p.utilityInPlay : [...p.utilityInPlay, copy],
      playedCards: playedCardsNext,
      pendingBuffs: pendingBuffsNext,
      adValueOverrides: ovr,
    };
  });

  trace.emit({
    category: "sig.play",
    label: mode ?? "utility",
    summary: `${card.name} sig played (${mode ?? "utility"})`,
    round: gs.round,
    activePlayer: n,
    details: {
      playerNum: n,
      card: card.name,
      cardId: card.id,
      sigSlotIdx: slotIdx,
      instanceId: copy.id,
      mode: mode ?? "utility",
      cost,
      channelFocusActive,
      isQuickLearner,
    },
  });

  if (mode) {
    const adModes = new Map(next.adModes ?? new Map<string, AdMode>());
    adModes.set(copy.id, mode);
    next = { ...next, adModes };
  }

  next = pushLog(
    next,
    gs.players[n].name,
    `Played Signature Move: ${card.name} (${mode === "attack" ? "⚔" : mode === "defend" ? "🛡" : "Utility"})${channelFocusActive ? " [Channel Focus: 5 Focus → cost 1 + Piercing]" : ""}`,
  );

  // Data-driven mechanics for the sig move (mirrors playAttackDefend).
  // Skipped for utility-mode sigs — evalMechanics expects an attack/defend mode.
  // Phase B: also skipped for ATTACK mode — mechanics are deferred to
  // resolveAttackPlay so they only fire if the attack isn't negated.
  if (mode === "defend") {
    const sigMechRes = evalMechanics(card, mode, {
      self: next.players[n],
      opponent: next.players[opp(n)],
      cardName: card.name,
      currentRound: next.round,
      selfNum: n,
    });
    next = applyResults(next, n, [sigMechRes], cardMap);
  }

  // Card-specific hooks for sig moves: onAttackPlay when played as attack,
  // onDefendPlay when played as defend, onUtilityPlay when played as utility.
  // Phase B: attack-mode hooks are deferred to resolveAttackPlay; defend
  // hooks fire inline here; UTILITY hooks are deferred to
  // resolveSigUtilityEffects so the sig-utility play is reactable on the
  // stack (Saw it Coming, future negate-style sigs).
  if (mode === "defend") {
    const sigHooks = getCardHooks(card);
    const sigHookFn = sigHooks?.onDefendPlay;
    if (sigHookFn) {
      // Mirror onDefendPlay (non-sig) — find the opposing slot the defend
      // will attach to so the hook ctx can reference it.
      const _slots = next.combatSlots ?? [];
      let _targetSlot: CombatSlot | undefined;
      for (let i = _slots.length - 1; i >= 0; i--) {
        const s = _slots[i];
        if (!s.resolved && s.attackerNum === opp(n)) {
          _targetSlot = s;
          break;
        }
      }
      const ctx: CardHookCtx = {
        self: next.players[n],
        opponent: next.players[opp(n)],
        game: next,
        card,
        slotId: _targetSlot?.id,
        instanceId: copy.id,
      };
      const sigRes = sigHookFn(ctx) ?? {};
      trace.emit({
        category: "hook.fire",
        label: `sig.onDefendPlay`,
        summary: `${card.name} sig onDefendPlay returned`,
        round: next.round,
        activePlayer: n,
        details: { card: card.name, hook: "onDefendPlay", mode, kind: "sig", result: sigRes as unknown as Record<string, unknown> },
      });
      next = applyResults(next, n, [sigRes], cardMap);
    }
  }

  // (Ignite Blade reveal-buff + Quick Learner discard-prompt are deferred to
  //  resolveSigUtilityEffects — must wait for the stack to clear so a Negate
  //  response can cancel the whole play.)

  // Fold fired gain-* buffs into keyword grants.
  const sigGrantedKw = keywordGrantsFromFired(firedSig);

  if (mode === "attack") {
    // ─── ATTACK push half ───
    let displayOverride: number | undefined;
    if (sigPowerMod !== 0 || sigBasePowerFactor > 1 || sigQuickBonusPlay > 0) {
      const base = safeNum(card.signatureMoveFields?.powerValue ?? 0);
      displayOverride = Math.max(0, base * sigBasePowerFactor + sigPowerMod + sigQuickBonusPlay);
    }
    const snapshot: AttackPlaySnapshot = {
      source: "playSig",
      cardId: card.id,
      copyInstanceId: copy.id,
      refundCost: cost,
      firedBuffIds: firedSig.map((b) => b.id),
      grantedKeywords: sigGrantedKw,
      displayOverride,
      sigSlotIdx: slotIdx,
    };
    next = pushLog(
      next,
      "system",
      `🔼 ${card.name} (sig) pushed onto the action chain — reactions open before it lands`,
    );
    next = pushActionChain(
      next,
      {
        playerNum: n,
        triggerEvent: "opponent-plays-attack",
        cardId: card.id,
        instanceId: copy.id,
        description: `${card.name} (sig attack)`,
        deferredEffect: { kind: "attack-play", snapshot },
      },
      "opponent-plays-attack",
      cardMap,
    );
    // Phase B fix: shift priority to defender on push (see playAttackDefend).
    next = { ...next, priority: opp(n), subPhase: "defender-pre-block" };
  } else if (mode === "defend") {
    next = attachBlock(next, n, copy, sigGrantedKw);
    next = { ...next, combatPopup: { defenderNum: n, blockCard: copy, blockType: "sig" }, freeReactionWindow: true };
    next = startReactionChain(next);
  } else {
    // ─── UTILITY push half (mode === null) ───
    // Sig played as utility. Card is already in utilityInPlay + playedCards,
    // stamina paid, slot.inPlay=true. The card's onUtilityPlay hook,
    // Ignite Blade reveal buff, and Quick Learner discard prompt are
    // deferred to resolveSigUtilityEffects so a Negate response can cleanly
    // cancel them. If negated, resolveNegateEntry refunds stamina (unless
    // the negator is Reversal) and clears slot.inPlay.
    next = pushLog(
      next,
      "system",
      `🔼 ${card.name} (sig utility) pushed onto the action chain — reactions open before it resolves`,
    );
    next = pushActionChain(
      next,
      {
        playerNum: n,
        triggerEvent: "opponent-plays-utility",
        cardId: card.id,
        instanceId: copy.id,
        description: `${card.name} (sig utility)`,
        deferredEffect: {
          kind: "sig-utility",
          cardId: card.id,
          instanceId: copy.id,
          sigSlotIdx: slotIdx,
          refundCost: cost,
        },
      },
      "opponent-plays-utility",
      cardMap,
    );
  }
  return next;
}

/** Armor block. (_l) */
export function armorBlock(gs: GameState, n: 1 | 2, cardMap: Map<string, Card>): GameState {
  const p = gs.players[n];
  if (!p.armorActive || !p.armorCardId) return pushLog(gs, "system", "Armor unavailable");
  const armor = cardMap.get(p.armorCardId);
  if (!armor) return gs;
  // Stun consumed: a stunned defender choosing armor-block has been forced
  // to skip their defend chance — stun is now spent. They will become the
  // new aggressor (continueAfterArmor) and must be free to attack.
  if (p.stunned) {
    gs = updatePlayer(gs, n, (pl) => ({ ...pl, stunned: false }));
    trace.emit({
      category: "status.stun.consume",
      label: "forced-armor-block",
      summary: `P${n} stun cleared (forced armor-block consumed it)`,
      round: gs.round,
      activePlayer: n,
      details: { playerNum: n, source: "continueAfterArmor:forced-armor-block" },
    });
    gs = pushLog(gs, p.name, "Stun cleared (forced armor-block consumed it)");
  }
  const block = parseFloat(armor.armorFields?.blockValue ?? "0") || 0;
  const copy: ZoneCard = { id: uid("arm_"), cardId: p.armorCardId };

  let next = updatePlayer(gs, n, (pl) => {
    const ovr = new Map(pl.adValueOverrides);
    ovr.set(copy.id, block);
    return {
      ...pl,
      attackDefendZone: [...pl.attackDefendZone, { id: copy.id, cardId: copy.cardId, side: "defend" }],
      adValueOverrides: ovr,
    };
  });
  const adModes = new Map(next.adModes ?? new Map<string, AdMode>());
  adModes.set(copy.id, "defend");
  next = { ...next, adModes };
  next = pushLog(next, p.name, `Blocked with armor ${armor.name}`);
  next = attachBlock(next, n, copy);
  next = { ...next, combatPopup: { defenderNum: n, blockCard: copy, blockType: "armor" }, freeReactionWindow: true };
  next = startReactionChain(next);
  return next;
}

/** Stance (er) — replace stance, send old to discard, remove from hand. */
export function playStance(
  gs: GameState,
  n: 1 | 2,
  handInstanceId: string,
  _cardMap: Map<string, Card>,
): GameState {
  const p = gs.players[n];
  const inst = p.handInstances.find((h) => h.id === handInstanceId);
  if (!inst) return gs;
  const oldStanceIdBefore = p.stanceCardId;
  let next = updatePlayer(gs, n, (pl) => {
    const oldStanceId = pl.stanceCardId;
    return {
      ...removeFromHand(pl, handInstanceId),
      stanceCardId: inst.cardId,
      discard: oldStanceId ? [...pl.discard, oldStanceId] : pl.discard,
    };
  });
  next = pushLog(next, p.name, `Set stance: ${_cardMap.get(inst.cardId)?.name ?? "?"}`);
  if (oldStanceIdBefore && oldStanceIdBefore !== inst.cardId) {
    trace.emit({
      category: "stance.clear",
      label: "replaced-by-playStance",
      summary: `P${n} stance replaced — old ${_cardMap.get(oldStanceIdBefore)?.name ?? oldStanceIdBefore} → discard`,
      round: gs.round,
      activePlayer: n,
      details: { playerNum: n, stanceBefore: oldStanceIdBefore, stanceAfter: inst.cardId, instanceId: handInstanceId },
    });
  }
  trace.emit({
    category: "stance.set",
    label: "playStance",
    summary: `P${n} stance set via playStance: ${_cardMap.get(inst.cardId)?.name ?? inst.cardId}`,
    round: gs.round,
    activePlayer: n,
    details: { playerNum: n, stanceBefore: oldStanceIdBefore, stanceAfter: inst.cardId, instanceId: handInstanceId },
  });
  return next;
}

/**
 * Utility play (Os) — Items go to utilityInPlay; Maneuvers/Reactions go straight
 * to discard after their effect runs. Card-specific hooks fire below.
 */
export function playUtility(
  gs: GameState,
  n: 1 | 2,
  handInstanceId: string,
  cardMap: Map<string, Card>,
): GameState {
  const p = gs.players[n];
  const inst = p.handInstances.find((h) => h.id === handInstanceId);
  if (!inst) return pushLog(gs, "system", "Card not in hand");
  const card = cardMap.get(inst.cardId);
  if (!card) return gs;
  const cost = staminaCostOf(card);

  // Single source of truth for utility play legality. UI and engine agree.
  const legality = canPlayUtilityNow(gs, n, card);
  if (!legality.ok) {
    return pushLog(gs, "system", `⚠ ${legality.reason ?? "Cannot play that utility right now."}`);
  }

  const subtype = card.utilityFields?.utilityType ?? "";
  const isItem = subtype === "Item";

  // ── STACK-PUSH PHASE ────────────────────────────────────────────────────
  // Pay stamina, remove from hand, and move source card to its destination
  // zone (Items → utilityInPlay, others → discard). Card-specific effects do
  // NOT run here — they are deferred until this entry resolves off the stack.
  // This way, a Negate response can cleanly cancel the play without us having
  // to undo any state mutations the effect would have made.
  let next = updatePlayer(gs, n, (pl) => {
    const cleaned = removeFromHand(pl, handInstanceId);
    // Visible "Played Cards" zone copy — every utility (Item or non-Item)
    // appears here as a dimmed reference until endRound clears it.
    const playedCopy: ZoneCard = { id: uid("played_"), cardId: inst.cardId };
    if (isItem) {
      return {
        ...cleaned,
        stamina: cleaned.stamina - cost,
        utilityInPlay: [...cleaned.utilityInPlay, { id: inst.id, cardId: inst.cardId }],
        playedCards: [...(cleaned.playedCards ?? []), playedCopy],
      };
    }
    const trackingCopy: ZoneCard = { id: uid("util_"), cardId: inst.cardId };
    return {
      ...cleaned,
      stamina: cleaned.stamina - cost,
      discard: [...cleaned.discard, inst.cardId],
      utilityPlayed: [...cleaned.utilityPlayed, trackingCopy],
      playedCards: [...(cleaned.playedCards ?? []), playedCopy],
    };
  });

  trace.emit({
    category: "utility.activate",
    label: isItem ? "item-pushed" : "non-item-pushed",
    summary: `${card.name} utility pushed to stack${isItem ? " (Item — stays in play)" : " (→ discard on resolve)"}`,
    round: gs.round,
    activePlayer: n,
    details: { playerNum: n, card: card.name, cardId: card.id, instanceId: inst.id, subtype, isItem, cost, stage: "push" },
  });

  next = pushLog(next, p.name, `Played Utility: ${card.name} (on stack)`);

  // Decide which kind of deferred effect this push represents:
  //   • Negate-keyword cards → deferredEffect kind "negate" (pops the entry
  //     below it on stack-resolve).
  //   • All other utility plays → deferredEffect kind "utility" (runs the
  //     card-specific effect block via resolveUtilityEffects).
  // Backward-compat: Saw it Coming is still treated as Negate even if its
  // Supabase keywords list hasn't been updated yet.
  const isNegateCard =
    !!card.keywords?.includes("Negate") || nameIs(card, "Saw it Coming");

  const triggerEvent: ReactionTrigger =
    (subtype || "").toLowerCase() === "item"
      ? "opponent-plays-utility-item"
      : (subtype || "").toLowerCase() === "maneuver"
        ? "opponent-plays-utility-maneuver"
        : (subtype || "").toLowerCase() === "reaction"
          ? "opponent-plays-utility-reaction"
          : "opponent-plays-utility";

  const deferred: DeferredEffect = isNegateCard
    ? {
        kind: "negate",
        negatingCardId: inst.cardId,
        negatingInstanceId: inst.id,
        refundCost: cost,
      }
    : {
        kind: "utility",
        cardId: inst.cardId,
        instanceId: inst.id,
        subtype,
        refundCost: cost,
        isItem,
      };

  next = pushActionChain(
    next,
    {
      playerNum: n,
      triggerEvent,
      cardId: inst.cardId,
      instanceId: inst.id,
      description: isNegateCard
        ? `${card.name} (negate)`
        : `played Utility ${card.name}`,
      deferredEffect: deferred,
    },
    triggerEvent,
    cardMap,
  );

  return next;
}

/**
 * Resolve a utility's card-specific effects — called when a utility stack
 * entry pops off the stack (i.e. opponent passed without negating it). This
 * runs the same per-card branches that used to live inline in `playUtility`,
 * plus the registered card hook. The card has ALREADY been paid for and moved
 * to its destination zone; this function only runs the effect payload.
 */
function resolveUtilityEffects(
  gs: GameState,
  n: 1 | 2,
  cardId: string,
  instanceId: string,
  cost: number,
  cardMap: Map<string, Card>,
): GameState {
  const card = cardMap.get(cardId);
  if (!card) return gs;
  const p = gs.players[n];
  let next = gs;
  trace.emit({
    category: "utility.activate",
    label: "resolve",
    summary: `${card.name} utility resolves off stack`,
    round: gs.round,
    activePlayer: n,
    details: { playerNum: n, card: card.name, cardId, instanceId, cost, stage: "resolve" },
  });

  // Card-specific simple effects (full hook coverage in dedicated cards/*.ts)
  if (nameIs(card, "Vial of Sparks")) {
    next = updatePlayer(next, n, (pl) => ({ ...pl, stamina: pl.stamina + 2 }));
    next = pushLog(next, p.name, "Vial of Sparks — +2 stamina");
  } else if (nameIs(card, "Wound-Bind Cord")) {
    const healBefore = next.players[n].health;
    next = updatePlayer(next, n, (pl) => ({
      ...pl,
      health: Math.min(pl.maxHealth, pl.health + 2),
    }));
    const healAfter = next.players[n].health;
    if (healAfter > healBefore) {
      trace.emit({
        category: "heal",
        label: "wound-bind-cord",
        summary: `P${n} healed ${healAfter - healBefore} via Wound-Bind Cord (${healBefore} → ${healAfter})`,
        round: gs.round,
        activePlayer: n,
        details: { playerNum: n, before: healBefore, after: healAfter, delta: healAfter - healBefore, source: "Wound-Bind Cord" },
      });
    }
    next = drawCards(next, n, 1);
    next = pushLog(next, p.name, "Wound-Bind Cord — +2 life, draw 1");
  } else if (nameIs(card, "Battle Adrenaline")) {
    next = updatePlayer(next, n, (pl) => ({ ...pl, stamina: pl.stamina + 2 }));
    next = drawCards(next, n, 1);
    next = pushLog(next, p.name, "Battle Adrenaline — +2 stamina, draw 1");
  } else if (nameIs(card, "Tensed Core")) {
    next = queueBuff(
      next,
      n,
      makeBuff("Tensed Core", "next-quick-attack", { kind: "cost-reduction", amount: 1 }, gs.round),
    );
    next = pushLog(next, p.name, "Tensed Core — next Quick attack costs 1 less");
  } else if (nameIs(card, "Scar-Fueled Focus")) {
    if (p.tookDamageThisRound) {
      next = grantFocus(next, n, 2, cardMap);
      next = pushLog(next, p.name, "Scar-Fueled Focus — +2 Focus");
    }
  } else if (nameIs(card, "Crushing Strength")) {
    // Spend 4 stamina (in addition to the printed cost, if any). The
    // precondition above requires ≥4 stamina remaining at play time.
    next = updatePlayer(next, n, (pl) => ({ ...pl, stamina: Math.max(0, pl.stamina - 4) }));
    next = queueBuff(next, n, makeBuff("Crushing Strength", "next-attack", { kind: "gain-quick" }, gs.round));
    // base-power-multiplier doubles the PRINTED base power BEFORE any other
    // buffs (e.g. Focus +2) or block subtraction. So a 3-power attack with
    // Crushing Strength + Focus becomes (3 × 2) + 2 = 8, not (3 + 2) × 2 = 10.
    next = queueBuff(next, n, makeBuff("Crushing Strength", "next-attack", { kind: "base-power-multiplier", factor: 2 }, gs.round));
    next = pushLog(next, p.name, "Crushing Strength — spent 4, next attack base power ×2 + Quick");
  } else if (nameIs(card, "Zeus' Wrathbrand") || nameIs(card, "Zues' Wrathbrand") || nameIs(card, "Zeus Wrathbrand")) {
    // Open prompt to pick which opponent weapon to disarm
    const oppNum = opp(n);
    const oppP = next.players[oppNum];
    const candidateSlots: number[] = [];
    oppP.equipSlots.forEach((s, idx) => {
      if (!s.cardId) return;
      const ec = cardMap.get(s.cardId);
      const subtype = ec?.equipmentFields?.equipmentSubtype?.toLowerCase() ?? "";
      if (subtype === "weapon" && !s.disarmed) candidateSlots.push(idx);
    });
    if (candidateSlots.length === 0) {
      next = pushLog(next, p.name, `Zeus' Wrathbrand — ${oppP.name} has no weapons to disarm`);
    } else {
      next = setPendingPrompt(next, {
        kind: "zeus-wrathbrand",
        playerNum: n,
        oppNum,
        selectedSlots: [],
      } as PendingPrompt);
    }
  } else if (nameIs(card, "Fuel The Inferno") || nameIs(card, "Fuel the Inferno")) {
    const focusCount = focusTokenCount(next.players[n], cardMap);
    if (focusCount <= 0) {
      next = pushLog(next, p.name, "Fuel The Inferno — no Focus tokens to sacrifice");
    } else {
      next = setPendingPrompt(next, {
        kind: "fuel-inferno",
        playerNum: n,
        maxFocus: focusCount,
        selected: 1,
        playCard: { id: instanceId, cardId },
        staminaCost: cost,
      } as PendingPrompt);
    }
  } else if (nameIs(card, "Added Pressure")) {
    // Apply -2 block to the opponent's most recent unresolved defend on a
    // combat slot owned by `n`. Stored via adValueOverrides keyed on the
    // blockCard's instance id. (Pre-condition was already verified above.)
    const slots = next.combatSlots ?? [];
    const targetSlot = [...slots].reverse().find(
      (s) => !s.resolved && s.attackerNum === n && s.blockCard != null,
    );
    if (targetSlot && targetSlot.blockCard) {
      const oppN = opp(n);
      const blkId = targetSlot.blockCard.id;
      const blkCard = cardMap.get(targetSlot.blockCard.cardId);
      const baseBlock = safeNum(
        blkCard?.attackDefendFields?.blockValue ??
          blkCard?.equipmentFields?.blockValue ??
          blkCard?.armorFields?.blockValue ??
          0,
      );
      next = updatePlayer(next, oppN, (pl) => {
        const ovr = new Map(pl.adValueOverrides);
        const current = ovr.get(blkId);
        const after = Math.max(0, (current ?? baseBlock) - 2);
        ovr.set(blkId, after);
        return { ...pl, adValueOverrides: ovr };
      });
      next = pushLog(
        next,
        p.name,
        `Added Pressure — −2 block to opponent's defend (opponent may pay 1 stamina to negate)`,
      );
    }
  }

  // Card hook (e.g. Pocket Sand's onUtilityPlay). Negate-keyword cards are
  // NOT routed here — they live in the "negate" deferred-effect kind and run
  // their negation directly in resolveStackEntry. A Negate card's own card
  // hook can still run via this path if it has one, but the negation itself
  // happens via the stack pop.
  const hooks = getCardHooks(card);
  if (hooks?.onUtilityPlay) {
    const ctx: CardHookCtx = {
      self: next.players[n],
      opponent: next.players[opp(n)],
      game: next,
      card,
      cardMap,
    };
    const r = hooks.onUtilityPlay(ctx) ?? {};
    trace.emit({
      category: "hook.fire",
      summary: `${card.name} onUtilityPlay returned`,
      details: { card: card.name, mode: "utility", result: r as unknown as Record<string, unknown> },
    });
    next = applyResults(next, n, [r], cardMap);

    // Phase 3 — Doubled Block (Utility/Maneuver): hook asks to open a free
    // extra-defend window. Find the most recent unresolved attack against
    // this player (n) and set the double-defend GameState fields. This
    // mirrors the onDefendPlay-side handler, but for utility-play the slot
    // is found by scanning combatSlots for the most recent attack from
    // opp(n) that isn't yet resolved.
    if (r.openDoubleDefendWindow) {
      const oppN = opp(n);
      const slots = next.combatSlots ?? [];
      let targetSlotId: string | null = null;
      for (let i = slots.length - 1; i >= 0; i--) {
        const s = slots[i];
        if (s.attackerNum === oppN && !s.resolved) {
          targetSlotId = s.id;
          break;
        }
      }
      if (targetSlotId) {
        next = {
          ...next,
          doubleDefendPendingSlotId: targetSlotId,
          doubleDefendPlayerNum: n,
        };
        next = pushLog(
          next,
          next.players[n].name,
          `${card.name} — you may play one more defend against this attack (block values sum).`,
        );
      } else {
        // No unresolved opposing attack — the utility had nothing to attach to.
        next = pushLog(
          next,
          next.players[n].name,
          `${card.name} — no incoming attack to double-block.`,
        );
      }
    }
  }

  return next;
}

/**
 * Resolve a signature-move utility's effects — called when a sig-utility stack
 * entry pops (opponent passed without negating). The card has ALREADY been
 * paid for and placed in utilityInPlay/playedCards with sigSlot.inPlay=true.
 * This runs the deferred work: card hook (onUtilityPlay), Ignite Blade reveal
 * buff, Quick Learner discard prompt.
 */
function resolveSigUtilityEffects(
  gs: GameState,
  n: 1 | 2,
  cardId: string,
  instanceId: string,
  sigSlotIdx: number,
  cost: number,
  cardMap: Map<string, Card>,
): GameState {
  const card = cardMap.get(cardId);
  if (!card) return gs;
  const p = gs.players[n];
  let next = gs;
  trace.emit({
    category: "sig.play",
    label: "sig-utility-resolve",
    summary: `${card.name} sig utility resolves off stack`,
    round: gs.round,
    activePlayer: n,
    details: { playerNum: n, card: card.name, cardId, instanceId, sigSlotIdx, cost, stage: "resolve" },
  });

  // Card hook — onUtilityPlay (Reversal, Bloodied Edge, etc.).
  const sigHooks = getCardHooks(card);
  if (sigHooks?.onUtilityPlay) {
    const ctx: CardHookCtx = {
      self: next.players[n],
      opponent: next.players[opp(n)],
      game: next,
      card,
    };
    const sigRes = sigHooks.onUtilityPlay(ctx) ?? {};
    trace.emit({
      category: "hook.fire",
      label: "sig.onUtilityPlay",
      summary: `${card.name} sig onUtilityPlay returned`,
      round: next.round,
      activePlayer: n,
      details: { card: card.name, hook: "onUtilityPlay", mode: "utility", kind: "sig", result: sigRes as unknown as Record<string, unknown> },
    });
    next = applyResults(next, n, [sigRes], cardMap);
  }

  // Ignite Blade (utility-mode sig) → queue rest-of-round Quick power bonus.
  // The reveal-precondition (must have Quick A/D in hand) was checked at push
  // time in playSig and does not consume the revealed card. The buff uses
  // `round-end` trigger so it is read on every Quick attack and pruned at
  // next round start.
  if (nameIs(card, "Ignite Blade")) {
    next = queueBuff(
      next,
      n,
      makeBuff("Ignite Blade", "round-end", { kind: "quick-power-bonus", amount: 1 }, gs.round),
    );
    next = pushLog(next, p.name, "Ignite Blade — revealed Quick from hand; Quick attacks +1 power this round (focus on Quick-attack damage)");
  }

  // Quick Learner → open prompt to pick a card from opponent discard.
  // The picked card replaces Quick Learner in the sig slot for the rest of
  // the round and is playable normally from the sig slot UI — no immediate
  // attack/defend resolution.
  if (nameIs(card, "Quick Learner")) {
    const oppP = next.players[opp(n)];
    const legalCardIds: string[] = [];
    for (const cid of oppP.discard) {
      const c = cardMap.get(cid);
      if (!c) continue;
      if (
        c.cardType === "Attack/Defend" ||
        c.cardType === "Signature Move" ||
        c.cardType === "Stance" ||
        c.cardType === "Utility"
      ) {
        legalCardIds.push(cid);
      }
    }
    if (legalCardIds.length === 0) {
      next = pushLog(next, p.name, "Quick Learner — no playable cards in opponent's discard");
    } else {
      next = setPendingPrompt(next, {
        kind: "quick-learner",
        playerNum: n,
        sigSlotIdx,
        legalDiscard: legalCardIds,
      } as PendingPrompt);
    }
  }

  void cost;
  return next;
}

/** Helper — pushes a utility-play action chain entry. Eligibility is widened
 *  to also match cards listing the BROAD "opponent-plays-utility" trigger. */
function pushActionChainForUtility(
  gs: GameState,
  actorNum: 1 | 2,
  cardId: string,
  instanceId: string,
  cardName: string,
  narrowEvent: ReactionTrigger,
  cardMap: Map<string, Card>,
): GameState {
  // Delegates to pushActionChain so we get the always-open + LIFO + two-pass
  // semantics for free. The window opens regardless of whether the opponent
  // has an eligible reaction card in hand.
  return pushActionChain(
    gs,
    {
      playerNum: actorNum,
      triggerEvent: narrowEvent,
      cardId,
      instanceId,
      description: `played Utility ${cardName}`,
    },
    narrowEvent,
    cardMap,
  );
}

/**
 * Push an Action Chain entry when an opponent activates a counter, so the
 * non-actor can play a Reaction utility (e.g. "Saw it Coming") in response.
 * Mirrors pushActionChainForUtility but uses the "opponent-activates-counter"
 * trigger event and a counter-flavored description.
 */
function pushActionChainForCounter(
  gs: GameState,
  actorNum: 1 | 2,
  cardId: string,
  instanceId: string,
  cardName: string,
  cardMap: Map<string, Card>,
): GameState {
  // Delegates to pushActionChain for always-open + LIFO + two-pass semantics.
  const narrowEvent: ReactionTrigger = "opponent-activates-counter";
  return pushActionChain(
    gs,
    {
      playerNum: actorNum,
      triggerEvent: narrowEvent,
      cardId,
      instanceId,
      description: `activated Counter ${cardName}`,
    },
    narrowEvent,
    cardMap,
  );
}

/**
 * Negate the most-recent action chain entry made by the opponent of `n`.
 * Used by "Saw it Coming" and similar negation reactions.
 *
 * Behavior:
 *  - Refunds the printed stamina cost of the negated card to its owner
 *    (utilities pay stamina; counters typically don't but staminaCostOf
 *    returns 0 for them so the refund is a no-op there).
 *  - Logs the negation and emits a chain.event trace.
 *  - Closes the open action chain reactor (the negation card was the
 *    reaction; nothing further can chain off the negated entry).
 *  - LEAVES the negated card where it currently sits (discard, utilityInPlay,
 *    or hand) per design: "Card stays where it was."
 *
 * Note: this does NOT revert state mutations the negated card already made
 * (buffs queued, focus minted, etc.). True effect-revert would require an
 * `appliedSnapshot` on the chain entry — left as future work.
 */
function negateLastOpponentChainEntry(
  gs: GameState,
  reactorNum: 1 | 2,
  cardMap: Map<string, Card>,
): GameState {
  const chain = gs.actionChain;
  if (!chain || chain.entries.length === 0) {
    return pushLog(gs, "system", "Negate — no opposing action to negate.");
  }
  // Find most-recent entry made by the OPPONENT of the reactor.
  const opponentNum = opp(reactorNum);
  let targetIdx = -1;
  for (let i = chain.entries.length - 1; i >= 0; i--) {
    if (chain.entries[i].playerNum === opponentNum) {
      targetIdx = i;
      break;
    }
  }
  if (targetIdx < 0) {
    return pushLog(gs, "system", "Negate — no opposing action found in chain.");
  }
  const target = chain.entries[targetIdx];
  const targetCard = target.cardId ? cardMap.get(target.cardId) : undefined;
  // Attack-play entries store the actual stamina paid in appliedSnapshot.
  // Fallback to staminaCostOf(card) for non-attack entries (utilities, etc).
  const snapshot = target.appliedSnapshot;
  const isAttackPlay = snapshot?.kind === "attack-play";
  const snapshotRefund = typeof snapshot?.refundCost === "number" ? (snapshot.refundCost as number) : null;
  const refund = snapshotRefund ?? (targetCard ? staminaCostOf(targetCard) : 0);

  let next = gs;
  if (refund > 0) {
    next = updatePlayer(next, opponentNum, (p) => ({
      ...p,
      stamina: Math.min(p.maxStamina, p.stamina + refund),
    }));
    trace.emit({
      category: "stamina",
      label: "refund",
      summary: `P${opponentNum} +${refund} stamina (Negate refund)`,
      round: gs.round,
      activePlayer: reactorNum,
      details: { playerNum: opponentNum, refund, source: "Saw it Coming", negatedCard: targetCard?.name },
    });
  }

  // Attack-play negate: remove the combat slot, decrement attacksThisRound,
  // and restore priority/subPhase so the attacker can act again (or pass).
  if (isAttackPlay) {
    const slotId = snapshot?.slotId as string | undefined;
    const attackerNum = (snapshot?.attackerNum as 1 | 2 | undefined) ?? opponentNum;
    if (slotId) {
      const oldSlots = next.combatSlots ?? [];
      const newSlots = oldSlots.filter((s) => s.id !== slotId);
      const slotWasFirst = oldSlots.length === 1;
      next = {
        ...next,
        combatSlots: newSlots,
        attacksThisRound: Math.max(0, (next.attacksThisRound ?? 0) - 1),
        // Hand priority back to the attacker so they can attack again, play
        // another card, or pass into the recover step.
        priority: attackerNum,
        subPhase: "aggressor-pre-attack",
        defenderPassed: false,
      };
      next = updatePlayer(next, attackerNum, (p) => ({
        ...p,
        attacksThisRound: Math.max(0, (p.attacksThisRound ?? 0) - 1),
      }));
      trace.emit({
        category: "combat.slot.resolve",
        label: "negated",
        summary: `slot ${slotId} removed (negated)`,
        round: gs.round,
        activePlayer: reactorNum,
        details: {
          slotId,
          attackerNum,
          slotWasFirst,
          reason: "saw-it-coming-negate",
        },
      });
      trace.emit({
        category: "priority.shift",
        label: "to-attacker-after-negate",
        summary: `priority → P${attackerNum} (attack negated, attacker may act again)`,
        round: gs.round,
        activePlayer: attackerNum,
        details: { to: attackerNum, reason: "attack-negated" },
      });
    }
  }

  trace.emit({
    category: "chain.event",
    label: "negate",
    summary: `Negate — ${target.description}`,
    round: gs.round,
    activePlayer: reactorNum,
    cardName: targetCard?.name,
    details: {
      reactorNum,
      negatedEntryId: target.id,
      negatedDescription: target.description,
      negatedCardName: targetCard?.name,
      refund,
      isAttackPlay,
      appliedSnapshot: snapshot,
    },
  });

  next = pushLog(
    next,
    "system",
    `Saw it Coming — ${gs.players[opponentNum].name}'s ${targetCard?.name ?? target.description} negated${refund > 0 ? ` (+${refund} stamina refunded)` : ""}.`,
  );

  // Close the action chain — the negation reaction concludes the chain;
  // nothing further can react off the negated entry.
  next = setActionChain(next, null);
  return next;
}

// ─── combat resolution (ro / Ea) ────────────────────────────────────────────

/**
 * Resolve the most-recent unresolved combat slot. Auto-fired after both sides
 * pass in the reaction window. (ro)
 */
export function resolveCombat(gs: GameState, cardMap: Map<string, Card>): GameState {
  const slots = (gs.combatSlots ?? []).slice();
  let idx = -1;
  for (let i = slots.length - 1; i >= 0; i--) {
    if (!slots[i].resolved) {
      idx = i;
      break;
    }
  }
  if (idx === -1) return gs;
  const slot = slots[idx];
  const attackerNum = slot.attackerNum;
  const defenderNum = opp(attackerNum);
  const attacker = gs.players[attackerNum];
  const defender = gs.players[defenderNum];

  // Synthetic attacks (Reflect) don't have a real card in cardMap. Build a
  // stub card so the rest of resolveCombat treats them like any other plain
  // attack — it has no keywords, just the synthetic power.
  const attCard = slot.synthetic
    ? syntheticCardFor(slot)
    : cardMap.get(slot.attackCard.cardId);
  if (!attCard) return gs;
  const blockCard = slot.blockCard;
  const blkCard = blockCard ? cardMap.get(blockCard.cardId) : null;

  const mode = (gs.adModes?.get(slot.attackCard.id) ?? "attack") as AdMode;
  const basePower = instanceValue(attacker, slot.attackCard.id, mode, attCard);
  let rawBlkBlock = blockCard && blkCard ? instanceValue(defender, blockCard.id, "defend", blkCard) : 0;
  // Phase 3 — Doubled Block: sum the second defend's block value when present.
  // The doubleDefendCard was placed on the slot by attachDoubleDefend (called
  // from the doubledBlock hook). Both block values are summed before the
  // Piercing / damage calculation proceeds.
  if (slot.doubleDefendCard) {
    const dblCard = cardMap.get(slot.doubleDefendCard.cardId);
    if (dblCard) {
      const dblBlock = instanceValue(defender, slot.doubleDefendCard.id, "defend", dblCard);
      rawBlkBlock += dblBlock;
    }
  }

  // Piercing: only bypasses armor block, NOT a played defend card or
  // equipment-used-as-defend. canArmorBlock already prevents the defender
  // from choosing armor against a Piercing attack, so when blockType is
  // anything other than "armor" we honor the defender's full block value.
  // (Old rules/damage.ts zeroed ALL block; user clarified that's wrong —
  // Hardened Hands/Channel Focus shouldn't ignore a played defend's block.)
  const slotGranted = (slot as { attackGrantedKeywords?: string[] }).attackGrantedKeywords ?? [];
  const attHasPiercing =
    hasKeyword(attCard.attackKeywords, "Piercing") ||
    (attCard.keywords ?? []).some((k) => k.toLowerCase() === "piercing") ||
    slotGranted.some((k) => k.toLowerCase() === "piercing");
  const piercingShouldZero = attHasPiercing && gs.combatPopup?.blockType === "armor";
  const blkBlock = piercingShouldZero ? 0 : rawBlkBlock;

  // Damage multiplier (post-block): legacy effect kind that scales the FINAL
  // damage after block subtraction. No active card uses this anymore as of
  // the Crushing Strength rework, but keep the path for future cards.
  let dmgMultiplier = 1;
  let consumedMultiplierIds: string[] = [];
  for (const b of attacker.pendingBuffs) {
    if (b.effect.kind !== "damage-multiplier") continue;
    const triggerOk =
      b.trigger === "next-attack" ||
      b.trigger === "next-action" ||
      (b.trigger === "next-quick-attack" && cardIsQuick(attCard, slotGranted));
    if (!triggerOk) continue;
    const f = b.effect.factor ?? 1;
    if (f > dmgMultiplier) dmgMultiplier = f;
    consumedMultiplierIds.push(b.id);
  }

  // Base-power multiplier (pre-block, pre-other-buffs): Crushing Strength.
  // Applied to the printed/base power BEFORE any flat power bonus (e.g.
  // Focus +2 from firePendingBuffs) or block subtraction. So Focus + Crushing
  // Strength on a 3-power attack = (3 × 2) + 2 = 8, not (3 + 2) × 2 = 10.
  let basePowerFactor = 1;
  let consumedBaseFactorIds: string[] = [];
  for (const b of attacker.pendingBuffs) {
    if (b.effect.kind !== "base-power-multiplier") continue;
    const triggerOk =
      b.trigger === "next-attack" ||
      b.trigger === "next-action" ||
      (b.trigger === "next-quick-attack" && cardIsQuick(attCard, slotGranted));
    if (!triggerOk) continue;
    const f = b.effect.factor ?? 1;
    if (f > basePowerFactor) basePowerFactor = f;
    consumedBaseFactorIds.push(b.id);
  }
  const scaledBasePower = basePower * basePowerFactor;

  // Quick-power-bonus: Ignite Blade's +1 power on Quick attacks is now baked
  // into adValueOverrides at play time (see playAttackDefend / playEquip /
  // playSig), so it's already included in `basePower` via instanceValue. We
  // still detect the buff here purely for the log message and the focus
  // grant below. Synthetic (Reflect) attacks have no override but also can't
  // be Quick (no keywords), so they correctly skip this branch.
  let quickBonus = 0;
  const attIsQuick = cardIsQuick(attCard, slotGranted);
  if (attIsQuick) {
    for (const b of attacker.pendingBuffs) {
      if (b.effect.kind !== "quick-power-bonus") continue;
      quickBonus += b.effect.amount ?? 0;
    }
  }

  // basePower already includes quickBonus (baked at play time) — do NOT add
  // it again here or it would double-count.
  const attPower = basePower;
  const dmgPreMult = Math.max(0, attPower - blkBlock);
  let dmg = dmgPreMult * dmgMultiplier;

  // Soulreaver: weapon always deals 1 damage even when fully blocked. Detect
  // by canonicalName of the attack card (covers signature-move and equipment
  // weapon attack modes — both surface the same canonical card here).
  let soulreaverMinDamage = false;
  if (!slot.synthetic && dmg === 0 && canonicalName(attCard.name) === "Soulreaver") {
    dmg = 1;
    soulreaverMinDamage = true;
  }

  trace.emit({
    category: "damage",
    label: "resolve",
    summary: `${attCard?.name ?? "attack"} → ${dmg} dmg (PWR ${attPower} - BLK ${blkBlock} ×${dmgMultiplier}${soulreaverMinDamage ? ", Soulreaver min 1" : ""})`,
    round: gs.round,
    activePlayer: gs.priority,
    details: {
      slotId: slot.id,
      attacker: slot.attackerNum,
      defender: opp(slot.attackerNum),
      attackCard: attCard?.name,
      attackIsQuick: attIsQuick,
      attackGrantedKeywords: slotGranted,
      basePower,
      basePowerFactor,
      scaledBasePower,
      quickBonusAlreadyBaked: quickBonus,
      blockTotal: blkBlock,
      dmgMultiplier,
      dmgPreMult,
      finalDamage: dmg,
      soulreaverMinDamage,
      consumedBaseFactorIds,
    },
  });

  trace.emit({
    category: "combat.slot.resolve",
    label: "resolve",
    summary: `slot ${slot.id} resolved — P${slot.attackerNum}→P${opp(slot.attackerNum)} ${dmg} dmg${blkBlock >= attPower ? " (full block)" : ""}`,
    round: gs.round,
    activePlayer: gs.priority,
    details: {
      slotId: slot.id,
      attackerNum: slot.attackerNum,
      defenderNum: opp(slot.attackerNum),
      attackCard: attCard?.name,
      defendCard: blkCard?.name,
      attPower,
      blkBlock,
      finalDamage: dmg,
      fullBlock: blkBlock >= attPower,
      synthetic: !!slot.synthetic,
      reflected: slot.synthetic?.kind === "reflect",
      source: "resolveCombat",
    },
  });

  slots[idx] = { ...slot, resolved: true, damageTaken: dmg };

  let next: GameState = { ...gs, combatSlots: slots };

  // Drop consumed multiplier buffs from attacker's pendingBuffs. Quick-power-
  // bonus buffs are NOT consumed — they persist for the rest of the round.
  if (consumedMultiplierIds.length > 0) {
    const dropIds = new Set(consumedMultiplierIds);
    next = updatePlayer(next, attackerNum, (p) => ({
      ...p,
      pendingBuffs: p.pendingBuffs.filter((b) => !dropIds.has(b.id)),
    }));
    if (dmgMultiplier > 1) {
      next = pushLog(next, attacker.name, `× Damage multiplier ×${dmgMultiplier} consumed`);
    }
  }
  if (quickBonus > 0) {
    next = pushLog(next, attacker.name, `⚡ Quick attack +${quickBonus} power (Ignite Blade)`);
  }

  if (dmg > 0) {
    next = updatePlayer(next, defenderNum, (p) => ({
      ...p,
      health: Math.max(0, p.health - dmg),
      tookDamageThisRound: true,
      totalHealthLost: p.totalHealthLost + dmg,
    }));
    // Track per-attack damage on the ATTACKER so cards like Bloodied Edge
    // can list “your attacks that dealt damage this round”. Synthetic
    // (Reflect) attacks are excluded — they're not a card the player threw,
    // and we don't want them showing up in the picker. The `slotId` here is
    // the CombatSlot.id so the UI can render distinct buttons even when the
    // same attack card is played twice.
    if (!slot.synthetic) {
      next = updatePlayer(next, attackerNum, (p) => ({
        ...p,
        attacksDealtDamageThisRound: (p.attacksDealtDamageThisRound ?? 0) + 1,
        damageDealtByAttackThisRound: [
          ...(p.damageDealtByAttackThisRound ?? []),
          {
            slotId: slot.id,
            attackCardId: slot.attackCard.cardId,
            attackCardName: attCard.name,
            damage: dmg,
          },
        ],
      }));
    }
    if (soulreaverMinDamage) {
      next = pushLog(
        next,
        attacker.name,
        `⚔ ${attCard.name} (PWR ${attPower}) vs 🛡 ${blkCard?.name ?? "—"} (BLK ${blkBlock}) — fully blocked but Soulreaver deals 1 damage anyway`,
      );
    } else {
      next = pushLog(
        next,
        attacker.name,
        `⚔ ${attCard.name} (PWR ${attPower}${dmgMultiplier > 1 ? ` ×${dmgMultiplier}` : ""}) vs 🛡 ${blkCard?.name ?? "—"} (BLK ${blkBlock}) → ${dmg} damage to ${defender.name}`,
      );
    }
  } else {
    next = pushLog(next, attacker.name, "⚔ vs 🛡 — fully blocked, no damage");
  }

  // Ignite Blade — +1 focus when a Quick attack deals damage. Gated to
  // attackers who have the Ignite Blade buff active in pendingBuffs.
  if (dmg > 0 && attIsQuick) {
    const igniteActive = attacker.pendingBuffs.some(
      (b) => b.sourceCardName === "Ignite Blade" && b.effect.kind === "quick-power-bonus",
    );
    if (igniteActive) {
      next = grantFocus(next, attackerNum, 1, cardMap);
      next = pushLog(next, attacker.name, "🔥 Ignite Blade — +1 focus (Quick attack dealt damage)");
    }
  }

  // Note: `fullBlock` is based on the printed pre-min calculation. Soulreaver's
  // 1-damage minimum doesn't change full-block keyword effects (Stun on full
  // block, etc.) — the block was still mechanically full.
  const fullBlock = blockCard !== null && dmgPreMult === 0;
  const isHeavy = (attCard.attackDefendFields?.weight ?? "").toLowerCase() === "heavy";

  // ── Keyword effects on combat resolution ─────────────────────────────────
  // Read printed + granted keywords for both sides.
  const blkSlotGranted = (slot as { blockGrantedKeywords?: string[] }).blockGrantedKeywords ?? [];
  const hasAttKw = (kw: string): boolean => {
    const printed =
      hasKeyword(attCard.attackKeywords, kw) ||
      (attCard.keywords ?? []).some((k) => k.toLowerCase() === kw.toLowerCase());
    if (printed && !isAttackKeywordSilenced(slot, kw)) return true;
    // Slot-granted keywords from this player's own buffs/counters are not
    // silenced by attack-side silence effects.
    return slotGranted.some((k) => k.toLowerCase().startsWith(kw.toLowerCase()));
  };
  const hasDefKw = (kw: string): boolean => {
    if (!blkCard) return false;
    return (
      hasKeyword(blkCard.defendKeywords, kw) ||
      (blkCard.keywords ?? []).some((k) => k.toLowerCase() === kw.toLowerCase()) ||
      blkSlotGranted.some((k) => k.toLowerCase().startsWith(kw.toLowerCase()))
    );
  };

  // Track if the defender becomes the new aggressor due to a counter-keyword
  // (Reflect, defender-side Stun on full block, defender-side Disarm/Displace
  // on full block). Set to true to force aggressor flip below.
  let flipAggressorToDefender = false;

  // Stun semantics (unified rule): a stunned player's NEXT chance to play
  // an attack OR a defend is skipped. While stunned they remain in the
  // defender role. Armor-block and take-damage are always available.
  //
  //   • Scenario 2 (here): attacker had Stun, defender played a block but
  //     took partial damage. Defender already used their action this
  //     exchange and remains defender. Set `stunned: true` so their next
  //     defend is skipped — only armor-block / take-damage on next attack.
  //   • Scenario 3 (here): defender had Stun and full-blocked. Attacker is
  //     stunned and the role flips so the former defender is the new
  //     aggressor. Stunned player (now defender) will have their next
  //     defend skipped on the upcoming attack.
  //   • Scenario 1 is handled in `takeDamage` (no block path).
  const attHasStun = hasAttKw("Stun");
  const defHasStun = hasDefKw("Stun");
  if (attHasStun && dmg > 0) {
    // Set both `stunned` (existing behavior) and `wasStunnedThisRound`
    // (Phase 3 — sticky per-round flag for counter triggers like Crimson
    // Tempest's stun-response). The sticky flag clears at round-start.
    next = updatePlayer(next, defenderNum, (pl) => ({ ...pl, stunned: true, wasStunnedThisRound: true }));
    trace.emit({
      category: "status.stun.apply",
      label: "attack-keyword-stun",
      summary: `P${defenderNum} stunned (attacker Stun + damage)`,
      round: gs.round,
      activePlayer: attackerNum,
      details: { playerNum: defenderNum, source: "resolveCombat:stun-keyword", attackCard: attCard?.name, dmg },
    });
    next = pushLog(
      next,
      attacker.name,
      `💫 ${defender.name} is stunned — next defend skipped (armor or take-damage only)`,
    );
    flipAggressorToDefender = false; // attacker keeps aggression and priority
  }
  if (defHasStun && fullBlock) {
    // Scenario 3: defender full-blocks with Stun. The would-be next action
    // for the original aggressor is to keep aggression and play another
    // attack — stun consumes that by flipping the role instead. Forcing
    // the role-flip IS the stun's consumption, so we do NOT flag `stunned`
    // on the (now-defender) original aggressor; they should be free to
    // play a normal defend on the upcoming attack.
    next = pushLog(
      next,
      defender.name,
      `💫 ${attacker.name} is stunned — ${defender.name} becomes aggressor`,
    );
    flipAggressorToDefender = true;
  }

  // Disarm X: on attack-deals-damage OR defend-full-blocks.
  const attHasDisarm = hasAttKw("Disarm");
  const defHasDisarm = hasDefKw("Disarm");
  const applyDisarm = (targetNum: 1 | 2, count: number, sourceName: string, targetName: string): GameState => {
    let s = next;
    s = updatePlayer(s, targetNum, (pl) => {
      const slotsArr = pl.equipSlots.slice();
      let applied = 0;
      for (let i = 0; i < slotsArr.length && applied < count; i++) {
        const sl = slotsArr[i];
        if (sl.cardId && !sl.tapped && !sl.disarmed) {
          slotsArr[i] = { ...sl, disarmed: true };
          applied++;
        }
      }
      for (let i = 0; i < slotsArr.length && applied < count; i++) {
        if (slotsArr[i].cardId) {
          slotsArr[i] = {
            ...slotsArr[i],
            disarmTokens: (slotsArr[i].disarmTokens ?? 0) + 1,
          };
          applied++;
        }
      }
      return { ...pl, equipSlots: slotsArr };
    });
    s = pushLog(s, sourceName, `⚔ Disarm ×${count} applied to ${targetName}`);
    return s;
  };
  // Helper: extract numeric value from a granted-keyword string like
  // "Disarm 2" — used when the source of a Disarm is a slot grant rather
  // than a printed keyword (e.g. counter rules that grant Disarm N).
  const grantedKwAmount = (list: string[] | undefined, head: string): number => {
    if (!list) return 0;
    const target = head.toLowerCase();
    for (const k of list) {
      const lower = k.toLowerCase();
      if (lower.startsWith(target)) {
        const m = lower.match(/(\d+)/);
        if (m) return Math.max(0, parseInt(m[1], 10));
        return 1;
      }
    }
    return 0;
  };
  if (attHasDisarm && dmg > 0) {
    const grantedCount = grantedKwAmount(slotGranted, "disarm");
    const count = Math.max(grantedCount, disarmCount(attCard), 1);
    next = applyDisarm(defenderNum, count, attacker.name, defender.name);
  }
  if (defHasDisarm && fullBlock && blkCard) {
    const grantedCount = grantedKwAmount(blkSlotGranted, "disarm");
    const count = Math.max(grantedCount, disarmCount(blkCard), 1);
    next = applyDisarm(attackerNum, count, defender.name, attacker.name);
    flipAggressorToDefender = true;
  }

  // Serrated: on damage, +1 wear counter on opponent armor. Source can be
  // printed, slot-granted, or a fired buff with serrated:true (Clobberation).
  // Wear is only applied if the defender has an active armor card — a wear
  // counter on no armor is meaningless. If wear reaches 2+, the armor breaks
  // (same threshold as Crush).
  let attHasSerrated =
    hasAttKw("Serrated") ||
    attacker.pendingBuffs.some((b) => b.effect.serrated === true);
  if (attHasSerrated && dmg > 0) {
    // Consume any serrated-flagged buffs that match this play.
    next = updatePlayer(next, attackerNum, (p) => ({
      ...p,
      pendingBuffs: p.pendingBuffs.filter((b) => b.effect.serrated !== true),
    }));
    if (next.players[defenderNum].armorActive) {
      const wearBeforeSerrated = next.players[defenderNum].armorWearCounters;
      const armorIdBefore = next.players[defenderNum].armorCardId;
      next = updatePlayer(next, defenderNum, (pl) => {
        const w = pl.armorWearCounters + 1;
        if (w >= 2) {
          return {
            ...pl,
            armorWearCounters: 0,
            armorBloodCounters: 0,
            armorActive: false,
            armorCardId: null,
            armorWeight: 0,
          };
        }
        return { ...pl, armorWearCounters: w };
      });
      const broke = !next.players[defenderNum].armorActive;
      trace.emit({
        category: "armor.wear",
        label: "serrated",
        summary: `P${defenderNum} armor wear ${wearBeforeSerrated} → ${broke ? "broken" : wearBeforeSerrated + 1} (Serrated, resolveCombat)`,
        round: gs.round,
        activePlayer: attackerNum,
        details: { playerNum: defenderNum, wearBefore: wearBeforeSerrated, wearAfter: broke ? 0 : wearBeforeSerrated + 1, broke, source: "resolveCombat:serrated", wearAdd: 1 },
      });
      if (broke) {
        trace.emit({
          category: "armor.break",
          label: "serrated",
          summary: `P${defenderNum} armor broke (Serrated)`,
          round: gs.round,
          activePlayer: attackerNum,
          details: { playerNum: defenderNum, armorCardId: armorIdBefore, source: "resolveCombat:serrated" },
        });
      }
      next = pushLog(
        next,
        attacker.name,
        `🪓 Serrated — +1 wear on ${defender.name}'s armor`,
      );
    } else {
      next = pushLog(
        next,
        attacker.name,
        `🪓 Serrated — ${defender.name} has no armor (no effect)`,
      );
    }
  }

  // Displace: attack-deals-damage OR blocked-by-armor → opponent stance discarded.
  // Defend full-blocks → opponent stance disarmed (treated as discard for now
  // until per-stance disarm semantics are designed).
  const attHasDisplace = hasAttKw("Displace");
  const defHasDisplace = hasDefKw("Displace");
  const isArmorBlock = (gs.combatPopup?.blockType === "armor");
  const discardStance = (targetNum: 1 | 2, sourceName: string, targetName: string): GameState => {
    const tgt = next.players[targetNum];
    if (!tgt.stanceCardId) return next;
    const stanceId = tgt.stanceCardId;
    let s = updatePlayer(next, targetNum, (pl) => ({
      ...pl,
      stanceCardId: null,
      discard: [...pl.discard, stanceId],
    }));
    s = pushLog(s, sourceName, `⇳ Displace — ${targetName}'s stance discarded`);
    trace.emit({
      category: "stance.clear",
      label: "displace",
      summary: `P${targetNum} stance discarded by Displace (was ${cardMap.get(stanceId)?.name ?? stanceId})`,
      round: gs.round,
      activePlayer: targetNum,
      details: { playerNum: targetNum, stanceBefore: stanceId, source: sourceName, trigger: "resolveCombat-displace" },
    });
    return s;
  };
  if (attHasDisplace && (dmg > 0 || isArmorBlock)) {
    next = discardStance(defenderNum, attacker.name, defender.name);
  }
  if (defHasDisplace && fullBlock) {
    next = discardStance(attackerNum, defender.name, attacker.name);
    flipAggressorToDefender = true;
  }

  // Reflect N: on full block, defender becomes the new aggressor and a
  // synthetic attack with power = N is generated as if the defender played
  // it from hand. The original attacker is now the defender and gets pre-block
  // priority. We capture the amount(s) here and push the synthetic CombatSlot(s)
  // at the end of this function, after all other keyword effects have applied,
  // so any pending buffs targeting next-attack still fire on the reflect.
  //
  // Stacked Reflect: a single defend can carry multiple Reflect sources —
  // the card's printed Reflect plus any granted Reflect chips from
  // pending-buff `gain-reflect` effects (e.g. counter grants "Reflect 2" to
  // next-defend). Each source queues a separate synthetic attack; they
  // resolve sequentially via the normal LIFO combatSlots pop, with the
  // defender getting a fresh pre-block window for each one.
  const pendingReflects: number[] = [];
  let pendingReflectSourceCardId: string | undefined;
  if (fullBlock && blkCard) {
    // Static Reflect from the card itself.
    const printed = reflectAmount(blkCard);
    if (printed > 0) {
      pendingReflects.push(printed);
      pendingReflectSourceCardId = blkCard.id;
      next = pushLog(
        next,
        defender.name,
        `↩ Reflect ${printed} — ${blkCard.name} fires a synthetic attack at ${attacker.name}`,
      );
    }
  
    // Granted Reflect from blockGrantedKeywords (e.g. counter-granted
    // "Reflect 2" sitting in the slot's grants list). Each grant queues its
    // own synthetic attack — stacked Reflect.
    for (const chip of blkSlotGranted) {
      const m = chip.match(/^Reflect\s+(\d+)/i);
      if (m) {
        const amt = parseInt(m[1], 10);
        if (amt > 0) {
          pendingReflects.push(amt);
          pendingReflectSourceCardId = pendingReflectSourceCardId ?? blkCard.id;
          next = pushLog(
            next,
            defender.name,
            `↩ Reflect ${amt} (granted) — fires a synthetic attack at ${attacker.name}`,
          );
        }
      } else if (/^Reflect$/i.test(chip)) {
        // Bare "Reflect" chip with no number → default to 1.
        pendingReflects.push(1);
        pendingReflectSourceCardId = pendingReflectSourceCardId ?? blkCard.id;
        next = pushLog(
          next,
          defender.name,
          `↩ Reflect 1 (granted) — fires a synthetic attack at ${attacker.name}`,
        );
      }
    }
  }

// Chain - only on attack and after resolution of combat the aggressor remains
// the aggressor for the next ACTION unless they'd be skipped (defender Stun on
// full block, defender Reflect on full block). Defend effects that would force
// the aggressor to be the defender (Stun/Disarm/Displace fullBlock) go above
// this line of code so Chain can override their aggressor flip. The armor and
// take-damage paths read `chainKeepsAggression` directly (see consumer below
// and takeDamage()).
const attackHasChain =
  hasAttKw("Chain") ||
  slotGranted.some((k) => k.toLowerCase() === "chain");
const skipNextAction =
  (defHasStun && fullBlock) || pendingReflects.length > 0;
const chainKeepsAggression = attackHasChain && !skipNextAction;
if (chainKeepsAggression) {
  flipAggressorToDefender = false;
  next = pushLog(
    next,
    attacker.name,
    `⛓ Chain — ${attacker.name} remains aggressor for next action`,
  );
  trace.emit({
    category: "keyword.chain",
    label: "remain aggressor",
    summary: `P${attackerNum} remains aggressor (Chain)`,
    round: next.round,
    activePlayer: attackerNum,
    details: {
      cardId: attCard?.id,
      cardName: attCard?.name,
      blockType: gs.combatPopup?.blockType,
      fullBlock,
      source: "resolveCombat:chain",
    },
  })
}
  // Perry: on full block, the defender's defend card may grant queued effects
  // to its owner's next qualifying action (per slot config or legacy shared).
  if (fullBlock && blkCard) {
    const perryHas =
      hasKeyword(blkCard.defendKeywords, "Perry") ||
      (blkCard.keywords ?? []).some((k) => k.toLowerCase() === "perry");
    const perryCfg = perryPayload(blkCard);
    if (perryHas && perryCfg && perryCfg.effects.length > 0) {
      const perryTarget: EffectTarget =
        perryCfg.appliesTo === "attack"
          ? "my-next-attack"
          : perryCfg.appliesTo === "defend"
            ? "my-next-defend"
            : "my-next-action";
      const perryCtx: EffectCtx = {
        self: next.players[defenderNum],
        opponent: next.players[attackerNum],
        cardName: blkCard.name,
        currentRound: next.round,
      };
      const perryResult: EffectResult = emptyResult();
      for (const row of perryCfg.effects) {
        // Re-target every row to the appliesTo target chosen by the designer
        // (the designer's row.target is treated as a hint for the effect type;
        // Perry's spec is that the granted effect lands on the chosen action).
        applyEffectRow({ ...row, target: perryTarget }, perryCtx, perryResult);
      }
      next = applyResults(next, defenderNum, [perryResult], cardMap);
      next = pushLog(next, defender.name, `⛨ Perry triggered from ${blkCard.name}`);
    }
  }

  // ── Run mechanics with damage context (on_attack_damage, on_defend_full_block) ──
  const attMech = evalMechanics(attCard, "attack", {
    self: next.players[attackerNum],
    opponent: next.players[defenderNum],
    cardName: attCard.name,
    damageDealt: dmg,
    fullBlock,
    currentRound: next.round,
    selfNum: attackerNum,
  });
  next = applyResults(next, attackerNum, [attMech], cardMap);
  if (blkCard) {
    const defMech = evalMechanics(blkCard, "defend", {
      self: next.players[defenderNum],
      opponent: next.players[attackerNum],
      cardName: blkCard.name,
      damageTaken: dmg,
      fullBlock,
      currentRound: next.round,
      selfNum: defenderNum,
    });
    next = applyResults(next, defenderNum, [defMech], cardMap);
  }

  // ── Registry hooks: onDamageDealt / onDamageTaken / onFullBlock ──
  const attHooks = getCardHooks(attCard);
  if (attHooks?.onDamageDealt && dmg > 0) {
    const ctx: CardHookCtx = {
      self: next.players[attackerNum],
      opponent: next.players[defenderNum],
      game: next,
      card: attCard,
      defendCard: blkCard ?? undefined,
      damageDealt: dmg,
      instanceId: slot.attackCard.id,
    };
    const r = attHooks.onDamageDealt(ctx) ?? {};
    trace.emit({
      category: "hook.fire",
      label: "onDamageDealt",
      summary: `${attCard.name} onDamageDealt fired (P${attackerNum} dealt ${dmg})`,
      round: next.round,
      activePlayer: attackerNum,
      details: { card: attCard.name, hook: "onDamageDealt", playerNum: attackerNum, source: "resolveCombat:attack", damageDealt: dmg, result: r as unknown as Record<string, unknown> },
    });
    next = applyResults(next, attackerNum, [r], cardMap);
  }
  // NOTE: Champion onDamageDealt is fired by the unified passive-damage loop
  // a few lines below (via getAnyHooksByName) — don't duplicate it here.
  if (blkCard) {
    const defHooks = getCardHooks(blkCard);
    if (defHooks?.onDamageTaken && dmg > 0) {
      const ctx: CardHookCtx = {
        self: next.players[defenderNum],
        opponent: next.players[attackerNum],
        game: next,
        card: blkCard,
        attackCard: attCard,
        damageTaken: dmg,
        instanceId: slot.blockCard?.id,
      };
      const r = defHooks.onDamageTaken(ctx) ?? {};
      trace.emit({
        category: "hook.fire",
        label: "onDamageTaken",
        summary: `${blkCard.name} onDamageTaken fired (P${defenderNum} took ${dmg})`,
        round: next.round,
        activePlayer: defenderNum,
        details: { card: blkCard.name, hook: "onDamageTaken", playerNum: defenderNum, source: "resolveCombat:defend", damageTaken: dmg, result: r as unknown as Record<string, unknown> },
      });
      next = applyResults(next, defenderNum, [r], cardMap);
    }
    if (defHooks?.onFullBlock && fullBlock) {
      const ctx: CardHookCtx = {
        self: next.players[defenderNum],
        opponent: next.players[attackerNum],
        game: next,
        card: blkCard,
        attackCard: attCard,
        fullBlock: true,
        instanceId: slot.blockCard?.id,
      };
      const r = defHooks.onFullBlock(ctx) ?? {};
      trace.emit({
        category: "hook.fire",
        label: "onFullBlock",
        summary: `${blkCard.name} onFullBlock fired (P${defenderNum})`,
        round: next.round,
        activePlayer: defenderNum,
        details: { card: blkCard.name, hook: "onFullBlock", playerNum: defenderNum, source: "resolveCombat:defend", result: r as unknown as Record<string, unknown> },
      });
      next = applyResults(next, defenderNum, [r], cardMap);
      // Phase 3 — Vengeance Strike: onFullBlock returning vengeanceAttackPrompt
      // queues the prompt. The defender's blockCard is the Vengeance Strike
      // defend side; if they accept, they pay stamina and play the attack side
      // of the SAME card next.
      if (r.vengeanceAttackPrompt && !next.pendingPrompt) {
        const v = r.vengeanceAttackPrompt;
        const blockInstId = slot.blockCard?.id;
        const blockCardId = slot.blockCard?.cardId;
        const canStillAttack = (next.players[defenderNum].attacksThisRound ?? 0) === 0;
        if (blockInstId && blockCardId && canStillAttack) {
          next = setPendingPrompt(next, {
            kind: "vengeance-attack",
            playerNum: defenderNum,
            instanceId: blockInstId,
            cardId: blockCardId,
            staminaCost: v.staminaCost,
            damageReceived: 0,
            sourceCardName: blkCard.name,
          });
        }
      }
    }
  }

  // ── Champion / armor / equipment passive damage hooks ──
  // These pieces aren't "played" each combat — we trigger their onDamageDealt /
  // onDamageTaken hooks here so champion/armor/equipment text fires.
  // NOTE: when the defender used armor-block, blkCard above already resolves
  // to the armor card (see armorBlock copying p.armorCardId into blockCard),
  // so the armor's onDamageTaken hook already fired via the defHooks branch.
  // Skip the armor entry here to avoid double-trigger (e.g. Aegis the Defiant
  // gaining +2 focus when fully blocking 1 dmg from Soulreaver).
  const isArmorBlockDefense = gs.combatPopup?.blockType === "armor";
  // `isChampion` flag lets the loop look up champion-only hooks via
  // getAnyHooksByName (covers both REGISTRY and CHAMPION_REGISTRY) so cards
  // like Crixus, Gaul-Breaker fire their passive damage hooks.
  const passiveCards: { num: 1 | 2; cardId: string | null; on: "onDamageDealt" | "onDamageTaken"; isChampion?: boolean }[] = [
    { num: defenderNum, cardId: defender.championCardId, on: "onDamageTaken", isChampion: true },
    { num: defenderNum, cardId: isArmorBlockDefense ? null : defender.armorCardId, on: "onDamageTaken" },
    { num: attackerNum, cardId: attacker.championCardId, on: "onDamageDealt", isChampion: true },
    // Attacker's armor onDamageDealt (e.g. Bloodforged Plate grants Blood
    // counter when attacker deals damage while wearing it). Gated on
    // armorActive so a broken armor does not fire.
    { num: attackerNum, cardId: attacker.armorActive ? attacker.armorCardId : null, on: "onDamageDealt" },
  ];
  for (const { num, cardId, on, isChampion } of passiveCards) {
    if (!cardId) continue;
    const c = cardMap.get(cardId);
    if (!c) continue;
    const h = (isChampion ? getAnyHooksByName(c.name) : getCardHooks(c))?.[on];
    if (!h) continue;
    if (on === "onDamageTaken" && dmg <= 0) continue;
    if (on === "onDamageDealt" && dmg <= 0) continue;
    const ctx: CardHookCtx = {
      self: next.players[num],
      opponent: next.players[opp(num)],
      game: next,
      card: c,
      attackCard: on === "onDamageTaken" ? attCard : undefined,
      defendCard: on === "onDamageDealt" ? (blkCard ?? undefined) : undefined,
      damageDealt: on === "onDamageDealt" ? dmg : undefined,
      damageTaken: on === "onDamageTaken" ? dmg : undefined,
    };
    const r = h(ctx) ?? {};
    trace.emit({
      category: "hook.fire",
      label: on,
      summary: `${c.name} ${on} fired (passive, P${num})`,
      round: next.round,
      activePlayer: num,
      details: { card: c.name, hook: on, playerNum: num, source: "resolveCombat:passive", damage: dmg, result: r as unknown as Record<string, unknown> },
    });
    next = applyResults(next, num, [r], cardMap);
  }

  // Equipment passive (e.g. Soulreaver heavy-attack focus): trigger on attacker's equipped weapons
  for (const sl of attacker.equipSlots) {
    if (!sl.cardId) continue;
    const c = cardMap.get(sl.cardId);
    if (!c) continue;
    const h = getCardHooks(c)?.onDamageDealt;
    if (!h) continue;
    if (dmg <= 0 && !isHeavy) continue;
    const ctx: CardHookCtx = {
      self: next.players[attackerNum],
      opponent: next.players[defenderNum],
      game: next,
      card: c,
      attackCard: attCard,
      damageDealt: dmg,
    };
    const r = h(ctx) ?? {};
    trace.emit({
      category: "hook.fire",
      label: "onDamageDealt",
      summary: `${c.name} onDamageDealt fired (equipment, P${attackerNum})`,
      round: next.round,
      activePlayer: attackerNum,
      details: { card: c.name, hook: "onDamageDealt", playerNum: attackerNum, source: "resolveCombat:equipment", damageDealt: dmg, result: r as unknown as Record<string, unknown> },
    });
    next = applyResults(next, attackerNum, [r], cardMap);
  }

  // Armor wear (Crush bumps wear by +1). Track wear-delta on the defender so
  // we can emit a wear-event for the Action Chain below.
  const defenderArmorWearBefore = next.players[defenderNum].armorWearCounters;
  const popup = gs.combatPopup;
  if (popup?.blockType === "armor") {
    const hasCrush =
      (attCard.keywords ?? []).some((k) => k.toLowerCase() === "crush") ||
      hasKeyword(attCard.attackKeywords, "Crush");
    const wearAdd = 1 + (hasCrush ? 1 : 0);
    const armorIdBefore = next.players[defenderNum].armorCardId;
    next = updatePlayer(next, defenderNum, (pl) => {
      const w = pl.armorWearCounters + wearAdd;
      if (w >= 2) {
        return { ...pl, armorWearCounters: 0, armorBloodCounters: 0, armorActive: false, armorCardId: null, armorWeight: 0 };
      }
      return { ...pl, armorWearCounters: w };
    });
    const brokeNow = !next.players[defenderNum].armorActive;
    trace.emit({
      category: "armor.wear",
      label: hasCrush ? "armor-block+crush" : "armor-block",
      summary: `P${defenderNum} armor wear ${defenderArmorWearBefore} → ${brokeNow ? "broken" : defenderArmorWearBefore + wearAdd} (armor block${hasCrush ? " + Crush" : ""})`,
      round: gs.round,
      activePlayer: attackerNum,
      details: { playerNum: defenderNum, wearBefore: defenderArmorWearBefore, wearAfter: brokeNow ? 0 : defenderArmorWearBefore + wearAdd, broke: brokeNow, wearAdd, hasCrush, source: "resolveCombat:armor-block" },
    });
    if (brokeNow) {
      trace.emit({
        category: "armor.break",
        label: hasCrush ? "armor-block+crush" : "armor-block",
        summary: `P${defenderNum} armor broke (armor block${hasCrush ? " + Crush" : ""})`,
        round: gs.round,
        activePlayer: attackerNum,
        details: { playerNum: defenderNum, armorCardId: armorIdBefore, source: "resolveCombat:armor-block" },
      });
    }
  }
  // (Serrated also bumps wear above; combine-checked here against snapshot.)
  const defenderArmorWearAfter = next.players[defenderNum].armorWearCounters;
  const armorBrokeThisCombat =
    !next.players[defenderNum].armorActive && gs.players[defenderNum].armorActive;
  const defenderGainedWear =
    armorBrokeThisCombat || defenderArmorWearAfter > defenderArmorWearBefore;

  // ── Action Chain emits (Phase 2): damage / full-block / armor-wear ──
  // Emit AFTER all keyword effects and hooks have applied, so reactions see
  // the post-resolution state. Each emit checks eligibility independently;
  // if multiple events open windows, the LAST eligible reactor wins.
  if (dmg > 0) {
    next = emitActionChainEvent(
      next,
      {
        actorNum: attackerNum,
        reactorNum: attackerNum,
        event: "my-attack-dealt-damage",
        description: `${attCard.name} dealt ${dmg} damage`,
        cardId: attCard.id,
        instanceId: slot.attackCard.id,
      },
      cardMap,
    );
    next = emitActionChainEvent(
      next,
      {
        actorNum: attackerNum,
        reactorNum: defenderNum,
        event: "self-received-damage",
        description: `received ${dmg} damage from ${attCard.name}`,
        cardId: attCard.id,
        instanceId: slot.attackCard.id,
      },
      cardMap,
    );
  }
  if (fullBlock && blkCard) {
    next = emitActionChainEvent(
      next,
      {
        actorNum: defenderNum,
        reactorNum: defenderNum,
        event: "my-defend-full-blocked",
        description: `${blkCard.name} fully blocked ${attCard.name}`,
        cardId: blkCard.id,
        instanceId: blockCard?.id ?? "",
      },
      cardMap,
    );
  }
  if (defenderGainedWear) {
    next = emitActionChainEvent(
      next,
      {
        actorNum: attackerNum,
        reactorNum: defenderNum,
        event: "self-armor-gains-wear",
        description: `armor gained wear`,
      },
      cardMap,
    );
    next = emitActionChainEvent(
      next,
      {
        actorNum: attackerNum,
        reactorNum: attackerNum,
        event: "opponent-armor-gains-wear",
        description: `${defender.name}'s armor gained wear`,
      },
      cardMap,
    );
  }

  // Track resolved instance ids
  const resolved = new Set(next.resolvedInstanceIds ?? new Set<string>());
  resolved.add(slot.attackCard.id);
  if (blockCard) resolved.add(blockCard.id);
  next = { ...next, resolvedInstanceIds: resolved };

  // Game over check
  if (next.players[defenderNum].health <= 0) {
    next = { ...next, winner: attackerNum, status: "finished" };
    next = pushLog(next, "system", `${attacker.name} wins!`);
    return next;
  }

  // Continue priority
  next = { ...next, combatPopup: null, freeReactionWindow: false };

  // If Reflect fired, push the synthetic reflect-attack(s) BEFORE continuing.
  // pushReflectAttack itself sets subPhase to "defender-pre-block" and gives
  // priority to the original attacker, so we skip the standard continueAfter*
  // branch in that case. Reflect takes precedence over the other defender-side
  // aggressor-flip keywords (Stun/Disarm/Displace) because the synthetic attack
  // creates a fresh combat exchange that needs an immediate pre-block window.
  //
  // Stacked Reflect: each call appends a new combatSlot. resolveCombat picks
  // unresolved slots LIFO, so the LAST queued reflect resolves first — fine,
  // since stacked reflects from the same defend are independent and equivalent
  // by spec. Pending buffs (e.g. power-bonus) only fire on the first push;
  // subsequent pushes see an empty pendingBuffs list, which matches the spec
  // (the buff gets consumed by the first reflect, not duplicated across all).
  if (pendingReflects.length > 0) {
    for (const amt of pendingReflects) {
      next = pushReflectAttack(
        next,
        defenderNum,
        amt,
        pendingReflectSourceCardId,
        cardMap,
      );
    }
    return next;
  }

  // If a synthetic (Reflect) attack is still queued behind this resolution
  // (stacked Reflect, previous attack just resolved via block), promote it to
  // the active combat exchange with a fresh pre-block window for the new
  // defender. This must come BEFORE the standard continueAfter* branch so
  // the queued reflect doesn't get bypassed.
  const queued = continueWithQueuedAttack(next);
  if (queued) return queued;

  if (popup?.blockType === "armor") {
    // Armor block normally flips aggression to the defender. Chain overrides
    // that — attacker keeps aggression for the next action.
    if (chainKeepsAggression) {
      next = continueAfterBlock(next);
    } else {
      next = continueAfterArmor(next);
    }
  } else if (flipAggressorToDefender) {
    // Defender-side Stun-Disarm-Displace flips aggressor to the defender.
    next = continueAfterTakeDamage(next);
  } else {
    next = continueAfterBlock(next);
  }
  return next;
}

/**
 * Build a stub Card object for a synthetic combat slot. Used by `resolveCombat`
 * and the popup renderer when `slot.synthetic` is set so they don't have to
 * branch everywhere on cardMap miss.
 *
 * The stub has empty keywords (reflect attacks are plain) and the synthetic
 * power baked into attackDefendFields so `instanceValue` returns the right
 * value when no override is set. In practice we ALSO set adValueOverrides at
 * push time so any pending power-bonus buffs are reflected, but the stub
 * still serves as the fallback / display.
 */
export function syntheticCardFor(slot: CombatSlot): Card | null {
  if (!slot.synthetic) return null;
  return {
    id: "__synthetic_reflect__",
    set: "",
    name: slot.synthetic.name,
    rarity: "",
    status: "Finalized",
    cardType: "Attack/Defend",
    keywords: [],
    createdAt: "",
    attackDefendFields: {
      powerValue: String(slot.synthetic.power),
      blockValue: "0",
      weight: "",
      staminaCost: "0",
    } as Card["attackDefendFields"],
    attackKeywords: [],
    defendKeywords: [],
  } as Card;
}

/**
 * Push a synthetic Reflect attack onto combatSlots.
 *
 * The reflector becomes the new aggressor; the original attacker becomes the
 * new defender and gets pre-block priority. The attack carries `synthetic`
 * metadata (name, power, sourceCardId) so the renderer can show it without
 * a real card in cardMap, and so `resolveCombat` knows to use the synthetic
 * power instead of looking up cardMap.
 *
 * Pending buffs on the reflector that target next-attack are fired here the
 * same way `playAD` would fire them — so a Crushing Strength or other queued
 * buff lands on the reflect attack as the user requested.
 *
 * Reflect attacks have no keywords on their own. If the new defender plays
 * a defend with Reflect on this synthetic attack, that block's full-block
 * triggers another reflect naturally via the same code path — chains work
 * recursively without special handling.
 */
export function pushReflectAttack(
  gs: GameState,
  reflectorNum: 1 | 2,
  power: number,
  sourceCardId: string | undefined,
  cardMap: Map<string, Card>,
): GameState {
  const newAttackerNum = reflectorNum;
  const newDefenderNum = opp(reflectorNum);
  const reflector = gs.players[reflectorNum];
  const newDefender = gs.players[newDefenderNum];

  // Synthetic ZoneCard: real instance id (so adValueOverrides + Action Chain
  // emits work normally), but cardId is a non-existent stub so any naive
  // cardMap.get(...) miss is detected by callers as "synthetic".
  const instanceId = uid("refl_");
  const syntheticZone: ZoneCard = {
    id: instanceId,
    cardId: "__synthetic_reflect__",
  };

  // Fire any pending buffs targeting next-attack so they land on the reflect.
  // We use mode="attack", no keywords (reflect attacks are plain), weight "".
  const { fired, remaining } = firePendingBuffs(
    reflector.pendingBuffs,
    "attack",
    [],
    "",
    gs.round,
  );
  const grantedKw = keywordGrantsFromFired(fired);

  // Power-bonus buffs targeting next-attack add to the synthetic attack's
  // power. We bake the result into adValueOverrides[instanceId] so
  // instanceValue() returns the bumped value (matches playAD's pattern).
  const powerMod = fired
    .filter((b) => b.effect.kind === "power-bonus")
    .reduce((s, b) => s + (b.effect.amount ?? 0), 0);

  // Drop the fired buffs from the reflector's pendingBuffs. damage-multiplier
  // and quick-power-bonus buffs are NOT consumed here (firePendingBuffs only
  // sees them as "matching" by trigger; they actually consume in resolveCombat).
  // To match playAD semantics where they DO get consumed at play-time when
  // they match, we keep the same behavior: drop everything firePendingBuffs
  // returned as fired.
  let next: GameState = gs;
  const finalPower = Math.max(0, power + powerMod);
  next = updatePlayer(next, newAttackerNum, (p) => {
    const ovr = new Map(p.adValueOverrides);
    ovr.set(instanceId, finalPower);
    return {
      ...p,
      pendingBuffs: remaining,
      adValueOverrides: ovr,
    };
  });
  // cardMap is intentionally unused here — reserved for future buff effects
  // that need to dispatch through applyResults during reflect creation.
  void cardMap;

  const slot: CombatSlot = {
    id: uid("cs_"),
    attackerNum: newAttackerNum,
    attackCard: syntheticZone,
    blockCard: null,
    resolved: false,
    damageTaken: 0,
    attackGrantedKeywords: grantedKw.length ? grantedKw : undefined,
    synthetic: {
      kind: "reflect",
      name: "Reflect Attack",
      power,
      sourceCardId,
    },
  };

  trace.emit({
    category: "combat.slot.add",
    label: "reflect",
    summary: `slot ${slot.id} added (P${newAttackerNum} synthetic reflect attack)`,
    round: next.round,
    activePlayer: newAttackerNum,
    details: {
      slotId: slot.id,
      attacker: newAttackerNum,
      defender: newDefenderNum,
      power,
      sourceCardId,
      source: "pushReflectAttack",
      synthetic: "reflect",
    },
  });
  next = {
    ...next,
    combatSlots: [...(next.combatSlots ?? []), slot],
    aggressor: newAttackerNum,
    priority: newDefenderNum,
    subPhase: "defender-pre-block",
    aggressorPassed: false,
    defenderPassed: false,
    mustAttackWarning: false,
    attacksThisRound: (next.attacksThisRound ?? 0) + 1,
    reactionChain: null,
    freeReactionWindow: false,
  };

  next = pushLog(
    next,
    reflector.name,
    `↩ Reflect Attack (PWR ${power}) — ${newDefender.name} must defend or take damage`,
  );
  return next;
}

/** Take damage with no block. (Ea) */
export function takeDamage(gs: GameState, n: 1 | 2, cardMap: Map<string, Card>): GameState {
  // Phase 2: choosing to take damage is an implicit pass on the attack action
  // chain — the defender has declined to negate. Close the chain if open.
  if (gs.actionChain && gs.actionChain.entries.length > 0) {
    const topEntry = gs.actionChain.entries[gs.actionChain.entries.length - 1];
    if ((topEntry.appliedSnapshot as { kind?: string } | undefined)?.kind === "attack-play") {
      trace.emit({
        category: "chain.close",
        label: "take-damage-commit",
        summary: `Action Chain closed (P${n} chose to take damage)`,
        round: gs.round,
        activePlayer: n,
        details: { defender: n, reason: "take-damage", chainLength: gs.actionChain.entries.length },
      });
      gs = setActionChain(gs, null);
    }
  }
  const slots = (gs.combatSlots ?? []).slice();
  let idx = -1;
  for (let i = slots.length - 1; i >= 0; i--) {
    if (!slots[i].resolved && slots[i].attackerNum === opp(n)) {
      idx = i;
      break;
    }
  }
  if (idx === -1) return pushLog(gs, "system", "No pending attack to take damage from");
  // Stun consumed: a stunned defender forced to take-damage (because they
  // couldn't play a defend) has spent their stun. Clear before evaluating
  // any new stun from the incoming attack. Track the pre-existing stun so
  // we can route the post-resolution flow correctly: a forced take-damage
  // should flip aggression to the defender (they become the new aggressor)
  // unless the incoming attack itself stuns them again (Scenario 1, no flip).
  const wasPreStunned = gs.players[n].stunned;
  if (wasPreStunned) {
    gs = updatePlayer(gs, n, (pl) => ({ ...pl, stunned: false }));
    trace.emit({
      category: "status.stun.consume",
      label: "forced-take-damage",
      summary: `P${n} stun cleared (forced take-damage consumed it)`,
      round: gs.round,
      activePlayer: n,
      details: { playerNum: n, source: "takeDamage:forced-take-damage" },
    });
    gs = pushLog(gs, gs.players[n].name, "Stun cleared (forced take-damage consumed it)");
  }
  const slot = slots[idx];
  const attacker = gs.players[slot.attackerNum];
  // Synthetic (Reflect) attacks don't have a real card in cardMap.
  const attCard = slot.synthetic
    ? syntheticCardFor(slot)
    : cardMap.get(slot.attackCard.cardId);
  if (!attCard) return gs;
  const mode = (gs.adModes?.get(slot.attackCard.id) ?? "attack") as AdMode;
  const basePower = instanceValue(attacker, slot.attackCard.id, mode, attCard);

  // Quick-power-bonus on the unblocked path: Ignite Blade's +1 is baked into
  // adValueOverrides at play time, so it's already in `basePower`. We still
  // detect the buff here for the log message and the focus grant below.
  const tdSlotGranted = slot.attackGrantedKeywords ?? [];
  const tdAttIsQuick = cardIsQuick(attCard, tdSlotGranted);
  let tdQuickBonus = 0;
  if (tdAttIsQuick) {
    for (const b of attacker.pendingBuffs) {
      if (b.effect.kind !== "quick-power-bonus") continue;
      tdQuickBonus += b.effect.amount ?? 0;
    }
  }
  // basePower already includes tdQuickBonus (baked at play time).
  const dmg = Math.max(0, basePower);
  trace.emit({
    category: "damage",
    label: "take-unblocked",
    summary: `${attCard.name} → ${dmg} dmg to P${n} (unblocked)`,
    round: gs.round,
    activePlayer: gs.priority,
    details: {
      slotId: slot.id,
      attacker: slot.attackerNum,
      defender: n,
      attackCard: attCard.name,
      attackIsQuick: tdAttIsQuick,
      attackGrantedKeywords: tdSlotGranted,
      basePower,
      quickBonusAlreadyBaked: tdQuickBonus,
      finalDamage: dmg,
      wasPreStunned,
    },
  });
  trace.emit({
    category: "combat.slot.resolve",
    label: "take-unblocked",
    summary: `slot ${slot.id} resolved — P${slot.attackerNum}→P${n} ${dmg} dmg (unblocked)`,
    round: gs.round,
    activePlayer: gs.priority,
    details: {
      slotId: slot.id,
      attackerNum: slot.attackerNum,
      defenderNum: n,
      attackCard: attCard.name,
      attPower: basePower,
      blkBlock: 0,
      finalDamage: dmg,
      fullBlock: false,
      synthetic: !!slot.synthetic,
      reflected: slot.synthetic?.kind === "reflect",
      wasPreStunned,
      source: "takeDamage",
    },
  });
  slots[idx] = { ...slot, resolved: true, damageTaken: dmg };

  let next: GameState = { ...gs, combatSlots: slots };
  next = updatePlayer(next, n, (p) => ({
    ...p,
    health: Math.max(0, p.health - dmg),
    tookDamageThisRound: true,
    totalHealthLost: p.totalHealthLost + dmg,
  }));
  // Mirror resolveCombat: track per-attack damage on the ATTACKER so cards
  // like Bloodied Edge can list “your attacks that dealt damage this round”
  // on the unblocked/take-damage path too. Synthetic (Reflect) attacks are
  // excluded — they're not a card the player threw.
  if (dmg > 0 && !slot.synthetic) {
    next = updatePlayer(next, slot.attackerNum, (p) => ({
      ...p,
      attacksDealtDamageThisRound: (p.attacksDealtDamageThisRound ?? 0) + 1,
      damageDealtByAttackThisRound: [
        ...(p.damageDealtByAttackThisRound ?? []),
        {
          slotId: slot.id,
          attackCardId: slot.attackCard.cardId,
          attackCardName: attCard.name,
          damage: dmg,
        },
      ],
    }));
  }
  if (tdQuickBonus > 0) {
    next = pushLog(next, attacker.name, `⚡ Quick attack +${tdQuickBonus} power (Ignite Blade)`);
  }
  next = pushLog(
    next,
    attacker.name,
    `⚔ ${attCard.name} (PWR ${dmg}) → ${dmg} damage to ${gs.players[n].name} (no block)`,
  );

  // Ignite Blade — +1 focus when a Quick attack deals damage on the
  // unblocked path. Gated to attackers who have the Ignite Blade buff
  // active in pendingBuffs (mirrors the resolveCombat branch).
  if (dmg > 0 && tdAttIsQuick) {
    const igniteActive = attacker.pendingBuffs.some(
      (b) => b.sourceCardName === "Ignite Blade" && b.effect.kind === "quick-power-bonus",
    );
    if (igniteActive) {
      next = grantFocus(next, slot.attackerNum, 1, cardMap);
      next = pushLog(next, attacker.name, "🔥 Ignite Blade — +1 focus (Quick attack dealt damage)");
    }
  }

  // ── Attack-side keyword effects on take-damage path ──
  // When defender chooses to eat damage (no block), the attacker's on-damage
  // keywords still apply: Stun, Disarm, Serrated, Displace. This mirrors
  // the equivalent block in resolveCombat so the chosen-take-damage path
  // and the partial-block-damage path behave identically per spec.
  const attackerNum = slot.attackerNum;
  const defenderNum = n;
  const defender = next.players[defenderNum];
  const slotGranted = tdSlotGranted;
  const hasAttKw = (kw: string): boolean => {
    const printed =
      hasKeyword(attCard.attackKeywords, kw) ||
      (attCard.keywords ?? []).some((k) => k.toLowerCase() === kw.toLowerCase());
    if (printed && !isAttackKeywordSilenced(slot, kw)) return true;
    // Slot-granted keywords from this player's own buffs/counters are not
    // silenced by attack-side silence effects.
    return slotGranted.some((k) => k.toLowerCase().startsWith(kw.toLowerCase()));
  };

  if (dmg > 0) {
    // Stun on take-damage path (Scenario 1): defender did NOT play a block.
    // Under normal flow they'd flip to aggressor and counter-attack. Stun
    // consumes that would-be-attack chance by suppressing the role flip
    // (handled at the bottom of takeDamage via continueAfterBlock instead
    // of continueAfterTakeDamage). We do NOT set `stunned: true` here
    // because the stun-effect is already consumed by the no-flip; defender
    // remains defender and CAN play a normal defend on the next attack.
    if (hasAttKw("Stun")) {
      next = pushLog(
        next,
        attacker.name,
        `💫 ${defender.name} is stunned — would-be counter-attack skipped, ${attacker.name} stays aggressor`,
      );
    }
    // Disarm X on damage → apply X disarm to defender's equip slots.
    if (hasAttKw("Disarm")) {
      const count = Math.max(1, disarmCount(attCard));
      next = updatePlayer(next, defenderNum, (pl) => {
        const slotsArr = pl.equipSlots.slice();
        let applied = 0;
        for (let i = 0; i < slotsArr.length && applied < count; i++) {
          const sl = slotsArr[i];
          if (sl.cardId && !sl.tapped && !sl.disarmed) {
            slotsArr[i] = { ...sl, disarmed: true };
            applied++;
          }
        }
        for (let i = 0; i < slotsArr.length && applied < count; i++) {
          if (slotsArr[i].cardId) {
            slotsArr[i] = {
              ...slotsArr[i],
              disarmTokens: (slotsArr[i].disarmTokens ?? 0) + 1,
            };
            applied++;
          }
        }
        return { ...pl, equipSlots: slotsArr };
      });
      next = pushLog(
        next,
        attacker.name,
        `⚔ Disarm ×${count} applied to ${defender.name}`,
      );
    }
    // Serrated on damage → +1 wear on defender's active armor (break at 2+).
    const attHasSerrated =
      hasAttKw("Serrated") ||
      attacker.pendingBuffs.some((b) => b.effect.serrated === true);
    if (attHasSerrated) {
      next = updatePlayer(next, attackerNum, (p) => ({
        ...p,
        pendingBuffs: p.pendingBuffs.filter((b) => b.effect.serrated !== true),
      }));
      if (next.players[defenderNum].armorActive) {
        const wearBeforeSerratedTd = next.players[defenderNum].armorWearCounters;
        const armorIdBeforeTd = next.players[defenderNum].armorCardId;
        next = updatePlayer(next, defenderNum, (pl) => {
          const w = pl.armorWearCounters + 1;
          if (w >= 2) {
            return {
              ...pl,
              armorWearCounters: 0,
              armorBloodCounters: 0,
              armorActive: false,
              armorCardId: null,
              armorWeight: 0,
            };
          }
          return { ...pl, armorWearCounters: w };
        });
        const brokeNowTd = !next.players[defenderNum].armorActive;
        trace.emit({
          category: "armor.wear",
          label: "serrated",
          summary: `P${defenderNum} armor wear ${wearBeforeSerratedTd} → ${brokeNowTd ? "broken" : wearBeforeSerratedTd + 1} (Serrated, takeDamage)`,
          round: gs.round,
          activePlayer: attackerNum,
          details: { playerNum: defenderNum, wearBefore: wearBeforeSerratedTd, wearAfter: brokeNowTd ? 0 : wearBeforeSerratedTd + 1, broke: brokeNowTd, wearAdd: 1, source: "takeDamage:serrated" },
        });
        if (brokeNowTd) {
          trace.emit({
            category: "armor.break",
            label: "serrated",
            summary: `P${defenderNum} armor broke (Serrated, takeDamage)`,
            round: gs.round,
            activePlayer: attackerNum,
            details: { playerNum: defenderNum, armorCardId: armorIdBeforeTd, source: "takeDamage:serrated" },
          });
        }
        next = pushLog(
          next,
          attacker.name,
          `🪓 Serrated — +1 wear on ${defender.name}'s armor`,
        );
      }
    }
    // Displace on damage → discard defender's stance.
    if (hasAttKw("Displace")) {
      const tgt = next.players[defenderNum];
      if (tgt.stanceCardId) {
        const stanceId = tgt.stanceCardId;
        next = updatePlayer(next, defenderNum, (pl) => ({
          ...pl,
          stanceCardId: null,
          discard: [...pl.discard, stanceId],
        }));
        next = pushLog(
          next,
          attacker.name,
          `⇳ Displace — ${defender.name}'s stance discarded`,
        );
        trace.emit({
          category: "stance.clear",
          label: "displace-takeDamage",
          summary: `P${defenderNum} stance discarded by Displace on damage (was ${cardMap.get(stanceId)?.name ?? stanceId})`,
          round: gs.round,
          activePlayer: defenderNum,
          details: { playerNum: defenderNum, stanceBefore: stanceId, source: attacker.name, trigger: "takeDamage-displace" },
        });
      }
    }
  }

  // Defender's champion / armor onDamageTaken hooks (Spartacus, Aegis, etc.).
  // Champion is looked up via getAnyHooksByName so champion-only cards
  // (Crixus, Gaul-Breaker, etc.) are reachable; armor uses the regular
  // registry as before.
  if (dmg > 0) {
    const passive: { cardId: string | null; isChampion: boolean }[] = [
      { cardId: next.players[n].championCardId, isChampion: true },
      { cardId: next.players[n].armorCardId, isChampion: false },
    ];
    for (const { cardId, isChampion } of passive) {
      if (!cardId) continue;
      const c = cardMap.get(cardId);
      if (!c) continue;
      const h = (isChampion ? getAnyHooksByName(c.name) : getCardHooks(c))?.onDamageTaken;
      if (!h) continue;
      const ctx: CardHookCtx = {
        self: next.players[n],
        opponent: next.players[opp(n)],
        game: next,
        card: c,
        attackCard: attCard,
        damageTaken: dmg,
      };
      const r = h(ctx) ?? {};
      trace.emit({
        category: "hook.fire",
        label: "onDamageTaken",
        summary: `${c.name} onDamageTaken fired (passive, P${n})`,
        round: next.round,
        activePlayer: n,
        details: { card: c.name, hook: "onDamageTaken", playerNum: n, source: "takeDamage:passive", damageTaken: dmg, result: r as unknown as Record<string, unknown> },
      });
      next = applyResults(next, n, [r], cardMap);
    }
  }

  // Run attack card's data-driven mechanics + onDamageDealt hook on the
  // take-damage (no-block) path. Mirrors what resolveCombat does on the
  // blocked path so cards like "This is Sparta" (attack-deals-damage:
  // +2 power my-next-attack) and Hardened Steel (attack-deals-damage:
  // +1 stamina) fire even when the defender chose to eat the damage.
  // Synthetic (Reflect) attacks have no real card and are skipped.
  if (!slot.synthetic) {
    const attMech = evalMechanics(attCard, "attack", {
      self: next.players[attackerNum],
      opponent: next.players[defenderNum],
      cardName: attCard.name,
      damageDealt: dmg,
      fullBlock: false,
      currentRound: next.round,
      selfNum: attackerNum,
    });
    next = applyResults(next, attackerNum, [attMech], cardMap);
    const attHooks = getCardHooks(attCard);
    if (attHooks?.onDamageDealt && dmg > 0) {
      const ctx: CardHookCtx = {
        self: next.players[attackerNum],
        opponent: next.players[defenderNum],
        game: next,
        card: attCard,
        damageDealt: dmg,
      };
      const r = attHooks.onDamageDealt(ctx) ?? {};
      trace.emit({
        category: "hook.fire",
        label: "onDamageDealt",
        summary: `${attCard.name} onDamageDealt fired (P${attackerNum} dealt ${dmg}, unblocked)`,
        round: next.round,
        activePlayer: attackerNum,
        details: { card: attCard.name, hook: "onDamageDealt", playerNum: attackerNum, source: "takeDamage:attack", damageDealt: dmg, result: r as unknown as Record<string, unknown> },
      });
      next = applyResults(next, attackerNum, [r], cardMap);
    }

    // Attacker's champion passive onDamageDealt hook (Crixus, Gaul-Breaker
    // queues +1 power on next attack). Mirrors the resolveCombat passive
    // loop — needed here because takeDamage is the no-block path and
    // doesn't go through that loop. Uses getAnyHooksByName so champion-
    // only cards (CHAMPION_REGISTRY) are reachable.
    if (dmg > 0 && next.players[attackerNum].championCardId) {
      const champId = next.players[attackerNum].championCardId!;
      const champCard = cardMap.get(champId);
      const champHook = champCard ? getAnyHooksByName(champCard.name)?.onDamageDealt : undefined;
      if (champCard && champHook) {
        const ctx: CardHookCtx = {
          self: next.players[attackerNum],
          opponent: next.players[defenderNum],
          game: next,
          card: champCard,
          attackCard: attCard,
          damageDealt: dmg,
        };
        const r = champHook(ctx) ?? {};
        trace.emit({
          category: "hook.fire",
          label: "onDamageDealt",
          summary: `${champCard.name} onDamageDealt fired (champion passive, P${attackerNum}, unblocked)`,
          round: next.round,
          activePlayer: attackerNum,
          details: { card: champCard.name, hook: "onDamageDealt", playerNum: attackerNum, source: "takeDamage:champion", damageDealt: dmg, result: r as unknown as Record<string, unknown> },
        });
        next = applyResults(next, attackerNum, [r], cardMap);
      }
    }

    // Attacker's armor passive onDamageDealt hook (e.g. Bloodforged Plate
    // grants a Blood counter when the wearer deals damage). Mirrors the
    // resolveCombat passive loop — needed here because takeDamage is the
    // no-block path. Armor lives in the regular REGISTRY only, so use
    // getCardHooks (not getAnyHooksByName). Gated on armorActive so a
    // broken armor does not fire.
    if (dmg > 0 && next.players[attackerNum].armorActive && next.players[attackerNum].armorCardId) {
      const armorId = next.players[attackerNum].armorCardId!;
      const armorCard = cardMap.get(armorId);
      const armorHook = armorCard ? getCardHooks(armorCard)?.onDamageDealt : undefined;
      if (armorCard && armorHook) {
        const ctx: CardHookCtx = {
          self: next.players[attackerNum],
          opponent: next.players[defenderNum],
          game: next,
          card: armorCard,
          attackCard: attCard,
          damageDealt: dmg,
        };
        const r = armorHook(ctx) ?? {};
        trace.emit({
          category: "hook.fire",
          label: "onDamageDealt",
          summary: `${armorCard.name} onDamageDealt fired (armor passive, P${attackerNum}, unblocked)`,
          round: next.round,
          activePlayer: attackerNum,
          details: { card: armorCard.name, hook: "onDamageDealt", playerNum: attackerNum, source: "takeDamage:armor", damageDealt: dmg, result: r as unknown as Record<string, unknown> },
        });
        next = applyResults(next, attackerNum, [r], cardMap);
      }
    }

    // Equipment passive (e.g. Soulreaver heavy-attack focus) on the
    // take-damage path. Mirrors the resolveCombat loop so an unblocked
    // heavy attack — whether thrown by Soulreaver itself, an A/D card,
    // or a signature move — still grants the equipped Soulreaver controller
    // their once-per-round focus token.
    if (dmg > 0) {
      const attackerNow = next.players[attackerNum];
      const isHeavyAtt =
        attCard.attackDefendFields?.weight === "Heavy" ||
        attCard.equipmentFields?.attackWeight === "Heavy" ||
        attCard.signatureMoveFields?.attackWeight === "Heavy";
      // Note: when the attack card IS the equipped weapon (e.g. attacking
      // with Soulreaver itself), attHooks.onDamageDealt already fired
      // above. The equipment hook's own dedupe (e.g. soulreaverUsedThisRound)
      // makes the second call a no-op, mirroring resolveCombat's behavior.
      for (const sl of attackerNow.equipSlots) {
        if (!sl.cardId) continue;
        const c = cardMap.get(sl.cardId);
        if (!c) continue;
        const h = getCardHooks(c)?.onDamageDealt;
        if (!h) continue;
        if (dmg <= 0 && !isHeavyAtt) continue;
        const ctx: CardHookCtx = {
          self: next.players[attackerNum],
          opponent: next.players[defenderNum],
          game: next,
          card: c,
          attackCard: attCard,
          damageDealt: dmg,
        };
        const r = h(ctx) ?? {};
        trace.emit({
          category: "hook.fire",
          label: "onDamageDealt",
          summary: `${c.name} onDamageDealt fired (equipment, P${attackerNum}, unblocked)`,
          round: next.round,
          activePlayer: attackerNum,
          details: { card: c.name, hook: "onDamageDealt", playerNum: attackerNum, source: "takeDamage:equipment", damageDealt: dmg, result: r as unknown as Record<string, unknown> },
        });
        next = applyResults(next, attackerNum, [r], cardMap);
      }
    }
  }

  // Phase 3 — onSelfTookDamage: fire for every A/D card in the damaged
  // player's hand. If a card returns `vengeanceAttackPrompt`, queue the
  // vengeance-attack pending prompt (first eligible card wins; only fires
  // when damage >= minDamage and the player hasn't already committed an
  // attack this round).
  if (dmg > 0 && !next.pendingPrompt) {
    const damagedPlayer = next.players[n];
    const canStillAttack = (damagedPlayer.attacksThisRound ?? 0) === 0;
    for (const hi of damagedPlayer.handInstances) {
      if (next.pendingPrompt) break;
      const hCard = cardMap.get(hi.cardId);
      if (!hCard) continue;
      const hHooks = getCardHooks(hCard);
      if (!hHooks?.onSelfTookDamage) continue;
      const ctx: CardHookCtx = {
        self: next.players[n],
        opponent: next.players[opp(n)],
        game: next,
        card: hCard,
        instanceId: hi.id,
        damageTaken: dmg,
      };
      const r = hHooks.onSelfTookDamage(ctx) ?? {};
      if (r.vengeanceAttackPrompt && canStillAttack) {
        const v = r.vengeanceAttackPrompt;
        if (dmg >= v.minDamage) {
          next = setPendingPrompt(next, {
            kind: "vengeance-attack",
            playerNum: n,
            instanceId: v.instanceId,
            cardId: hi.cardId,
            staminaCost: v.staminaCost,
            damageReceived: dmg,
            sourceCardName: hCard.name,
          });
        }
      }
    }
  }

  // ── Action Chain emits (Phase 2): unblocked-damage path ──
  if (dmg > 0) {
    next = emitActionChainEvent(
      next,
      {
        actorNum: slot.attackerNum,
        reactorNum: slot.attackerNum,
        event: "my-attack-dealt-damage",
        description: `${attCard.name} dealt ${dmg} damage`,
        cardId: attCard.id,
        instanceId: slot.attackCard.id,
      },
      cardMap,
    );
    next = emitActionChainEvent(
      next,
      {
        actorNum: slot.attackerNum,
        reactorNum: n,
        event: "self-received-damage",
        description: `received ${dmg} damage from ${attCard.name}`,
        cardId: attCard.id,
        instanceId: slot.attackCard.id,
      },
      cardMap,
    );
  }

  if (next.players[n].health <= 0) {
    next = { ...next, winner: opp(n), status: "finished" };
    next = pushLog(next, "system", `${attacker.name} wins!`);
    return next;
  }

  next = { ...next, combatPopup: null, freeReactionWindow: false };

  // If a synthetic (Reflect) attack is still queued behind this take-damage,
  // promote it to the active combat exchange. Stacked Reflect: defender
  // chose to eat the first reflect, the second reflect now needs its own
  // pre-block window per spec.
  const queued = continueWithQueuedAttack(next);
  if (queued) return queued;

  // Stun-on-damage: attacker keeps aggression and gets priority. Defender's
  // next action is skipped via the stunned flag (handled in confirmPendingPlay).
  // This overrides the normal take-damage "flip aggressor" behavior.
  const stunFired = dmg > 0 && hasAttKw("Stun");
  if (stunFired) {
    next = continueAfterBlock(next);
    return next;
  }

  // Chain on take-damage path: there is no defend card, so the defender has
  // no "would-be skipped" action. Chain unconditionally keeps the attacker
  // as the aggressor for the next action, overriding the normal take-damage
  // role flip. (Spec: aggressor stays unless they'd be skipped.)
  const chainFired = hasAttKw("Chain");
  if (chainFired) {
    next = pushLog(
      next,
      attacker.name,
      `⛓ Chain — ${attacker.name} remains aggressor for next action`,
    );
    trace.emit({
      category: "keyword.chain",
      label: "remain aggressor",
      summary: `P${slot.attackerNum} remains aggressor (Chain, take-damage)`,
      round: next.round,
      activePlayer: slot.attackerNum,
      details: {
        cardId: attCard?.id,
        cardName: attCard?.name,
        source: "takeDamage:chain",
      },
    });
    next = continueAfterBlock(next);
    return next;
  }

  next = continueAfterTakeDamage(next);
  return next;
}

// ─── priority continuation (pf / mf / Sl) ──────────────────────────────────

/**
 * After any combat slot resolves (block, take-damage, armor), check if there
 * are still unresolved combat slots queued. This happens with stacked Reflect:
 * `pushReflectAttack` queues multiple synthetic attacks at once, and they
 * resolve one at a time. After each resolution, we need to set up the next
 * one as a fresh combat exchange with its own pre-block window.
 *
 * Returns null if no unresolved slot remains — caller should fall through to
 * the normal continueAfter* path. Returns updated state if a queued attack
 * needs to take over as the active combat.
 */
function continueWithQueuedAttack(gs: GameState): GameState | null {
  const slots = gs.combatSlots ?? [];
  // Find latest unresolved slot (matches resolveCombat / takeDamage LIFO).
  let idx = -1;
  for (let i = slots.length - 1; i >= 0; i--) {
    if (!slots[i].resolved) {
      idx = i;
      break;
    }
  }
  if (idx === -1) return null;
  const slot = slots[idx];
  // Only synthetic (Reflect) attacks should auto-promote like this. A non-
  // synthetic queued slot would be unusual; we leave that for the normal
  // continueAfter* flow rather than risk hijacking an unrelated state.
  if (!slot.synthetic) return null;
  const newAggressor = slot.attackerNum;
  const newDefender = opp(newAggressor);
  return {
    ...gs,
    aggressor: newAggressor,
    priority: newDefender,
    subPhase: "defender-pre-block",
    aggressorPassed: false,
    defenderPassed: false,
    mustAttackWarning: false,
    reactionChain: null,
    combatPopup: null,
    // Aggressor flip (Reflect auto-promote) — reset streak trackers.
    chainStreak: 0,
    consecutiveCounters: 0,
    freeReactionWindow: false,
  };
}

/** After defender blocked (non-armor): aggressor keeps priority. (pf) */
export function continueAfterBlock(gs: GameState): GameState {
  const aggressor = gs.aggressor ?? 1;
  return {
    ...gs,
    priority: aggressor,
    subPhase: "aggressor-pre-attack",
    aggressorPassed: false,
    defenderPassed: false,
    // Defensive: ensure no leftover reaction chain bleeds into the next attack.
    reactionChain: null,
    freeReactionWindow: false,
  };
}

/** After armor block: defender becomes new aggressor. (mf) */
export function continueAfterArmor(gs: GameState): GameState {
  const aggressor = opp(gs.aggressor ?? 1);
  return {
    ...gs,
    aggressor,
    priority: aggressor,
    subPhase: "aggressor-pre-attack",
    aggressorPassed: false,
    defenderPassed: false,
    reactionChain: null,
    freeReactionWindow: false,
    // Aggressor flip — reset streak trackers tied to the prior aggressor.
    chainStreak: 0,
    consecutiveCounters: 0,
  };
}

/** After take-damage: defender becomes new aggressor. (Sl) */
export function continueAfterTakeDamage(gs: GameState): GameState {
  const aggressor = opp(gs.aggressor ?? 1);
  return {
    ...gs,
    aggressor,
    priority: aggressor,
    subPhase: "aggressor-pre-attack",
    aggressorPassed: false,
    defenderPassed: false,
    reactionChain: null,
    freeReactionWindow: false,
    // Aggressor flip — reset streak trackers tied to the prior aggressor.
    chainStreak: 0,
    consecutiveCounters: 0,
  };
}

// ─── counter (gf / wi / xf / yf) ────────────────────────────────────────────

/** Open the Activate Counter dialog. (wi) */
export function openCounterDialog(gs: GameState, n: 1 | 2, instanceId: string): GameState {
  const inst = gs.players[n].handInstances.find((h) => h.id === instanceId);
  if (!inst) return gs;
  trace.emit({
    category: "counter.open",
    summary: `P${n} opens counter dialog (${inst.cardId})`,
    round: gs.round,
    activePlayer: n,
    details: { player: n, instanceId, cardId: inst.cardId },
  });
  return {
    ...gs,
    counterDialog: { playerNum: n, card: inst, cardDbId: inst.cardId },
  };
}

/** Activate counter: evaluate counterRules, apply effects, and open the
 *  Counter Activated dialog. (gf)
 *
 *  New schema: each rule has a trigger (matched against the opposing card on
 *  the active combat slot) and a list of effects. Only rules whose trigger
 *  matches fire. Effects are dispatched through the shared `applyEffectRow`
 *  for next-* targets, and via inline slot/instance mutation for `this-*`
 *  and `popup-*` targets. */
export function activateCounter(
  gs: GameState,
  n: 1 | 2,
  instanceId: string,
  cardMap: Map<string, Card>,
): GameState {
  const inst = gs.players[n].handInstances.find((h) => h.id === instanceId);
  if (!inst) return gs;
  const card = cardMap.get(inst.cardId);
  if (!card) return gs;
  // Hard guard: a counter copy may only be activated once per round.
  if (gs.players[n].counterActivatedIds.has(instanceId)) {
    trace.emit({
      category: "counter.fizzle",
      label: "already-activated",
      summary: `${card.name} counter blocked — already activated this round`,
      round: gs.round,
      activePlayer: n,
      details: { player: n, instanceId, cardId: inst.cardId, cardName: card.name, reason: "already-activated-this-round" },
    });
    return gs;

  }

  // ───────────────────────────────────────────────────────────────────────
  // Phase B counter activation flow.
  //
  // PUSH HALF (here):
  //   1. Mark the counter as activated this round so it can't double-fire.
  //   2. Move the card into utilityInPlay and counterRefUtilityIds so the UI
  //      shows it on the board immediately.
  //   3. Show the counterActivatedDialog so the activating player gets
  //      feedback that the counter went on the stack (buffSummary is filled
  //      in by the resolver, but the dialog opens now so the click that
  //      triggered activation has an immediate visible result).
  //   4. Reset the reaction window pass count so opponent must explicitly
  //      pass on this new top-of-stack item.
  //   5. Push a `counter` deferred entry. The opponent's reaction window
  //      opens against this counter — they can negate it before the rules
  //      ever fire.
  //   6. If the counter has the Negate keyword, push ANOTHER `negate` stack
  //      entry on top (sibling to the counter). This negate can itself be
  //      negated by a stacked reaction — honouring the user's rule:
  //      "when negate happens it immediately resolves the action chain.
  //       This should not happen because a card that negates could get
  //       negated in its self. So the negate should go on the action chain
  //       stack."
  //
  // RESOLVE HALF (resolveCounterEffects, resolveNegateEntry):
  //   When the counter entry pops off the stack, resolveCounterEffects
  //   evaluates the matching counter rules and applies their effects. The
  //   sibling negate entry (if any) resolves on its own and removes the
  //   most-recent opposing entry below it.
  // ───────────────────────────────────────────────────────────────────────
  let next = gs;
  next = updatePlayer(next, n, (p) => {
    const ids = new Set(p.counterActivatedIds);
    ids.add(instanceId);
    return {
      ...p,
      counterActivatedIds: ids,
      utilityInPlay: [...p.utilityInPlay, { id: inst.id, cardId: inst.cardId }],
      counterRefUtilityIds: new Set([...p.counterRefUtilityIds, inst.id]),
    };
  });

  // Slot snapshot at push — used only for trace logging here. The real
  // resolution happens in resolveCounterEffects when this entry pops.
  const slot = findCounterTargetSlot(next, n);
  const rules = card.attackDefendFields?.counterRules ?? [];
  trace.emit({
    category: "counter.activate",
    label: "push",
    summary: `${card.name} counter pushed onto the action chain by P${n} (${rules.length} rule${rules.length === 1 ? "" : "s"})`,
    round: gs.round,
    activePlayer: n,
    details: {
      player: n,
      cardName: card.name,
      instanceId,
      cardId: inst.cardId,
      ruleCount: rules.length,
      slotId: slot?.id ?? null,
      slotAttacker: slot?.attackerNum ?? null,
      slotAttackCardId: slot?.attackCard?.cardId ?? null,
      slotBlockCardId: slot?.blockCard?.cardId ?? null,
      pushed: true,
    },
  });

  next = pushLog(
    next,
    gs.players[n].name,
    `Counter pushed onto the action chain: ${card.name} — reactions open before it resolves`,
  );

  // Show the dialog now so the activating player sees immediate feedback.
  // buffSummary stays empty here; the actual fired-effects summary lives in
  // the resolver's trace, and the dialog is informational.
  next = {
    ...next,
    counterDialog: null,
    counterActivatedDialog: {
      playerNum: n,
      card: inst,
      cardDbId: inst.cardId,
      buffSummary: [],
    },
  };

  // If the reaction window is open, activating a counter counts as a card play:
  // reset consecutivePasses so the next button click is "Pass" again, not
  // "Resolve". Priority stays with the counter-er so they can choose to pass
  // or chain another action; they explicitly pass to hand priority over.
  if (next.reactionChain) {
    next = {
      ...next,
      reactionChain: { ...next.reactionChain, consecutivePasses: 0, turn: n },
      priority: n,
    };
  }

  // Push the `counter` deferred entry. Note: counters typically cost 0
  // stamina, but we honour staminaCostOf(card) for forward-compat (a future
  // counter card could carry a cost).
  const counterCost = staminaCostOf(card);
  next = pushActionChain(
    next,
    {
      playerNum: n,
      triggerEvent: "opponent-activates-counter",
      cardId: inst.cardId,
      instanceId: inst.id,
      description: `activated Counter ${card.name}`,
      deferredEffect: {
        kind: "counter",
        cardId: inst.cardId,
        instanceId: inst.id,
        refundCost: counterCost,
      },
    },
    "opponent-activates-counter",
    cardMap,
  );

  // Negate keyword on a counter → push a SIBLING `negate` stack entry on top
  // of the just-pushed counter entry. This negate is itself reactable (it
  // can be negated by a stacked reaction). Source-of-truth note from prior
  // design: Reaction utilities, reaction-only abilities, and Negate-keyword
  // counters all share this push path.
  if (card.keywords?.includes("Negate")) {
    next = pushActionChain(
      next,
      {
        playerNum: n,
        triggerEvent: "opponent-activates-counter",
        cardId: inst.cardId,
        instanceId: inst.id,
        description: `Negate (from Counter ${card.name})`,
        deferredEffect: {
          kind: "negate",
          negatingCardId: inst.cardId,
          negatingInstanceId: inst.id,
          // Negate from a Counter has no extra cost — the counter itself
          // already paid. Refund 0 if THIS negate gets negated.
          refundCost: 0,
        },
      },
      "opponent-activates-counter",
      cardMap,
    );
  }

  return next;
}

/* ── helpers for the new counterRule engine ────────────────────────────── */

/** Pick the slot the counter is reacting to. Mirrors CardActionPopup. */
function findCounterTargetSlot(gs: GameState, n: 1 | 2): CombatSlot | null {
  const slots = gs.combatSlots ?? [];
  for (let i = slots.length - 1; i >= 0; i--) {
    if (!slots[i].resolved) return slots[i];
  }
  return null;
}

/** Determine which combat-slot card is "the opposing play" the counter
 *  responds to (attacker's attack if we're defending, defender's block if
 *  we're attacking). */
function opposingZoneOnSlot(
  slot: CombatSlot,
  n: 1 | 2,
): { zone: ZoneCard | null; mode: "attack" | "defend" } {
  if (slot.attackerNum === n) {
    return { zone: slot.blockCard, mode: "defend" };
  }
  return { zone: slot.attackCard, mode: "attack" };
}

/** Identify whether the opposing card on the slot was played as an A/D card,
 *  via an equipment weapon, or via a signature move. Used by
 *  CounterTrigger.cardKinds (e.g. No Time fires only against equipment
 *  weapon attacks). Heuristic: prefer the form that actually has fields
 *  for the play mode. */
function opposingCardSourceKind(
  opCard: Card,
  mode: "attack" | "defend",
): "attack-defend" | "equipment" | "signature" {
  // A/D card always has attackDefendFields with weight; treat as ad first.
  if (opCard.attackDefendFields?.weight) return "attack-defend";
  if (mode === "attack") {
    if (opCard.signatureMoveFields?.attackWeight) return "signature";
    if (opCard.equipmentFields?.attackWeight) return "equipment";
  } else {
    if (opCard.equipmentFields?.defendWeight) return "equipment";
  }
  // Fallback: A/D is most common.
  return "attack-defend";
}

/** Read weight from any opposing card form: A/D, equipment weapon (attack or
 *  defend weight), or signature-move (attack weight). Returns the weight
 *  string lowercased, or empty string when none can be inferred. */
function opposingCardWeight(opCard: Card, mode: "attack" | "defend"): string {
  const adWeight = opCard.attackDefendFields?.weight;
  if (adWeight) return adWeight.toLowerCase();
  const eqWeight =
    mode === "attack"
      ? opCard.equipmentFields?.attackWeight
      : opCard.equipmentFields?.defendWeight;
  if (eqWeight) return eqWeight.toLowerCase();
  const sigWeight = opCard.signatureMoveFields?.attackWeight;
  if (sigWeight) return sigWeight.toLowerCase();
  return "";
}

/** Same trigger evaluator as the popup uses, but reading the engine state. */
function ruleMatchesSlot(
  rule: CounterRule,
  slot: CombatSlot | null,
  n: 1 | 2,
  cardMap: Map<string, Card>,
  gs: GameState,
): boolean {
  const t = rule.trigger;

  // Event-driven trigger: fires off opponent's per-round draw count, not a
  // slot. The Final Straw uses this — it can be activated any time after
  // the opponent has drawn a card this round, regardless of slot state.
  if (t.opponentDrewThisRound) {
    const oppP = gs.players[opp(n)];
    if ((oppP.cardsDrawnThisRound ?? 0) <= 0) return false;
    // When this gate is present, ignore slot-shape gates entirely; the
    // counter is purely event-driven.
    return true;
  }

  // Phase 3 event-driven gate: counter fires if YOU were stunned this round.
  // Mirrors `opponentDrewThisRound` shape — no slot needed; the counter
  // becomes activatable any time after the player got stunned. Used by
  // Crimson Tempest's stun-response counter ("after I get stunned, play
  // this in response on the stack").
  if (t.selfStunnedThisRound) {
    if (!gs.players[n].wasStunnedThisRound) return false;
    return true;
  }

  // Phase 3 event-driven gate: counter fires if YOU took damage this round.
  // Used by Calculate Risk ("counter: when you take damage, draw 2 then
  // discard 1"). Same slot-independent shape.
  if (t.selfTookDamageThisRound) {
    if (!gs.players[n].tookDamageThisRound) return false;
    return true;
  }

  if (!slot) return false;
  const { zone, mode } = opposingZoneOnSlot(slot, n);
  if (!zone) return false;
  const opCard = cardMap.get(zone.cardId);
  if (!opCard) return false;

  if (t.actionPlayed !== "either" && t.actionPlayed !== mode) return false;

  if (t.cardKinds && t.cardKinds.length > 0) {
    const srcKind = opposingCardSourceKind(opCard, mode);
    if (!t.cardKinds.includes(srcKind)) return false;
  }

  if (t.weights.length > 0) {
    // Read weight from whichever card form the opposing card actually is
    // (A/D, equipment weapon, or signature move) so counters fire symmetrically
    // across all three.
    const w = opposingCardWeight(opCard, mode);
    if (!t.weights.some((x) => x.toLowerCase() === w)) return false;
  }
  if (mode === "attack" && t.attackTypes.length > 0) {
    const at = (opCard.attackDefendFields?.attackType ?? "").toLowerCase();
    if (!t.attackTypes.some((x) => x.toLowerCase() === at)) return false;
  }
  if (mode === "defend" && t.defendTypes.length > 0) {
    const dt = (opCard.attackDefendFields?.defendType ?? "").toLowerCase();
    if (!t.defendTypes.some((x) => x.toLowerCase() === dt)) return false;
  }
  if (t.hasKeywords.length > 0) {
    const granted =
      mode === "attack"
        ? (slot.attackGrantedKeywords ?? [])
        : (slot.blockGrantedKeywords ?? []);
    const all = new Set<string>([
      ...((opCard.keywords ?? []).map((k) => k.toLowerCase())),
      ...granted.map((k) => k.toLowerCase()),
    ]);
    if (!t.hasKeywords.some((k) => all.has(k.toLowerCase()))) return false;
  }
  // Live power/block gates: read the post-buff value off the live combat slot
  // (adValueOverrides already baked any fired power-bonus / block-bonus).
  const opOwnerN: 1 | 2 = mode === "attack" ? slot.attackerNum : opp(slot.attackerNum);
  const opOwnerPlayer = gs.players[opOwnerN];
  if (typeof t.minBuffedPower === "number" && t.minBuffedPower > 0 && mode === "attack") {
    const livePower = instanceValue(opOwnerPlayer, zone.id, "attack", opCard);
    if (livePower < t.minBuffedPower) return false;
  }
  if (typeof t.minBuffedBlock === "number" && t.minBuffedBlock > 0 && mode === "defend") {
    const liveBlock = instanceValue(opOwnerPlayer, zone.id, "defend", opCard);
    if (liveBlock < t.minBuffedBlock) return false;
  }
  return true;
}

/** Dispatch a single EffectRow for an activated counter.
 *
 *  - my-next-*  → run through applyEffectRow → applyResults plumbing
 *                 (queues a pendingBuff on the appropriate player).
 *  - this-*     → no-op for counter (the counter card itself isn't on the
 *                 board as an attack/defend; this target only makes sense
 *                 for mechanics on a played AD card). Logged + skipped.
 *  - popup-*    → mutate the LIVE combat slot in place. For buff-self this
 *                 modifies the player's own card on the slot; for
 *                 debuff-opponent it modifies the opposing card on the slot.
 */
function applyCounterEffectRow(
  gs: GameState,
  n: 1 | 2,
  cardName: string,
  row: EffectRow,
  slot: CombatSlot | null,
  cardMap: Map<string, Card>,
  buffSummary: string[],
): GameState {
  // Route popup-* directly — they need slot mutation that lives outside
  // PlayerState. EffectResult cannot represent this.
  if (isPopupTarget(row.target)) {
    if (!slot) return gs;
    return applyPopupEffectToSlot(gs, n, cardName, row, slot, cardMap, buffSummary);
  }
  // this-* targets are meaningless for counters (the counter card is going to
  // utilityInPlay, not the AD zone). Skip — designer should have used popup-*.
  if (isThisTarget(row.target)) return gs;

  // draw effect: route directly through drawCards so the deck/discard/hand
  // and per-round draw counters all stay in sync. buff-self + target=self
  // draws for the activating player; debuff-opponent + target=opponent
  // forces the opponent to draw (rare; supported for symmetry).
  if (row.effect === "draw") {
    const count = row.amount ?? 1;
    if (count > 0) {
      const drawer: 1 | 2 = row.target === "opponent" ? opp(n) : n;
      buffSummary.push(
        drawer === n ? `draw ${count}` : `opponent draws ${count}`,
      );
      return drawCards(gs, drawer, count);
    }
    return gs;
  }

  // Phase 3 — search-deck effect: build a search-deck-by-filter pending
  // prompt using all deck cards as candidates (the UI will display them
  // so the player can pick the matching one). Setting the prompt here means
  // the player must confirm before the counter's other effects proceed.
  if (row.effect === "search-deck") {
    const searcher: 1 | 2 = n;
    const seacherPlayer = gs.players[searcher];
    const filterLabel = row.searchFilter ?? `${cardName} — Search deck`;
    const pickCount = row.searchPickCount ?? 1;
    const shuffleAfter = row.searchShuffleAfter !== false;
    const candidates = seacherPlayer.deckCards.map((cardId) => ({
      instanceId: uid("dk_"),
      cardId,
    }));
    buffSummary.push(`search-deck (${filterLabel})`);
    if (!gs.pendingPrompt) {
      return setPendingPrompt(gs, {
        kind: "search-deck-by-filter",
        playerNum: searcher,
        filterLabel,
        sourceCardName: cardName,
        candidates,
        selectedInstanceIds: [],
        pickCount,
        shuffleAfter,
      });
    }
    return gs;
  }

  // Phase 3 — clear-stun effect: clear `stunned` and `wasStunnedThisRound`
  // flags on self. Used by Crimson Tempest counter to negate a stun played
  // on the stack. Target is always `self` (validated in isValidCombo).
  if (row.effect === "clear-stun") {
    const target: 1 | 2 = n;
    const p = gs.players[target];
    if (!p.stunned && !p.wasStunnedThisRound) return gs;
    buffSummary.push("clear-stun");
    const updated: PlayerState = {
      ...p,
      stunned: false,
      wasStunnedThisRound: false,
    };
    const players = { ...gs.players, [target]: updated } as GameState["players"];
    return pushLog({ ...gs, players }, p.name, `${cardName} — stun negated.`);
  }

  // my-next-* — run the shared effect dispatcher.
  const result: EffectResult = emptyResult();
  const ctx: EffectCtx = {
    self: gs.players[n],
    opponent: gs.players[opp(n)],
    cardName,
    currentRound: gs.round,
  };
  applyEffectRow(row, ctx, result);
  buffSummary.push(summarizeRow(row));
  return applyResults(gs, n, [result], cardMap);
}

/** Apply a popup-* effect to the live combat slot. */
function applyPopupEffectToSlot(
  gs: GameState,
  n: 1 | 2,
  cardName: string,
  row: EffectRow,
  slot: CombatSlot,
  cardMap: Map<string, Card>,
  buffSummary: string[],
): GameState {
  // Decide which side of the slot we're touching:
  //   buff-self  + popup-attack  → my own attack on the slot
  //   buff-self  + popup-defend  → my own defend on the slot
  //   buff-self  + popup-action  → whichever side I own
  //   debuff-opp + popup-attack  → opponent's attack
  //   debuff-opp + popup-defend  → opponent's defend
  //   debuff-opp + popup-action  → opponent's side on the slot
  const targetIsMine = row.direction === "buff-self";
  const myMode: "attack" | "defend" =
    slot.attackerNum === n ? "attack" : "defend";
  const oppMode: "attack" | "defend" = myMode === "attack" ? "defend" : "attack";

  let touchMode: "attack" | "defend";
  if (row.target === "popup-attack") touchMode = "attack";
  else if (row.target === "popup-defend") touchMode = "defend";
  else touchMode = targetIsMine ? myMode : oppMode;

  // Resolve the ZoneCard + owning playerNum we need to touch.
  const ownerN: 1 | 2 = targetIsMine ? n : opp(n);
  const zone: ZoneCard | null =
    touchMode === "attack" ? slot.attackCard : slot.blockCard;
  if (!zone) return gs; // nothing to touch
  const card = cardMap.get(zone.cardId);
  if (!card) return gs;

  let next = gs;
  const sign = targetIsMine ? 1 : -1;
  switch (row.effect) {
    case "power":
    case "block": {
      const mode: AdMode = row.effect === "power" ? "attack" : "defend";
      const delta = (row.amount ?? 0) * sign;
      next = adjustInstanceValue(next, ownerN, zone.id, card, mode, delta);
      buffSummary.push(`${row.effect} ${delta >= 0 ? "+" : ""}${delta} on live ${touchMode}`);
      next = pushLog(
        next,
        gs.players[n].name,
        `${cardName}: ${delta >= 0 ? "+" : ""}${delta} ${row.effect} on live ${touchMode}`,
      );
      break;
    }
    case "add-keyword": {
      // Only valid on buff-self (validation in editor); add to slot's granted list.
      const kw = row.keyword ?? "";
      if (!kw) return next;
      // Bake the keywordAmount into the chip text (e.g. "Reflect 2"). Engine
      // helpers (reflectAmount/disarmCount/keywordGrantsFromFired) tolerate
      // both "Reflect" and "Reflect N" forms, so this gives consistent display.
      const display =
        typeof row.keywordAmount === "number" && row.keywordAmount > 0
          ? `${kw} ${row.keywordAmount}`
          : kw;
      next = addSlotGrantedKeyword(next, slot.id, touchMode, display);
      buffSummary.push(`+${display} on live ${touchMode}`);
      next = pushLog(next, gs.players[n].name, `${cardName}: live ${touchMode} gains ${display}`);
      break;
    }
    case "remove-keyword": {
      // Only valid on debuff-opponent.
      const kw = row.keyword ?? "";
      next = removeSlotGrantedKeyword(next, slot.id, touchMode, kw);
      // The granted-keyword strip handles slot-granted keywords. For printed
      // keywords (e.g. No Time silencing a weapon's Crush/Quick/etc.) we
      // record the silence on the slot itself. The attack-side keyword
      // readers consult isAttackKeywordSilenced(...) before honoring any
      // keyword. Empty keyword = silence ALL printed attack keywords.
      if (touchMode === "attack") {
        next = {
          ...next,
          combatSlots: (next.combatSlots ?? []).map((s) => {
            if (s.id !== slot.id) return s;
            if (!kw) {
              return { ...s, silenceAllAttackKeywords: true };
            }
            const prev = s.silencedAttackKeywords ?? [];
            const lc = kw.toLowerCase();
            if (prev.some((x) => x.toLowerCase() === lc)) return s;
            return { ...s, silencedAttackKeywords: [...prev, kw] };
          }),
        };
      }
      buffSummary.push(kw ? `−${kw} on live ${touchMode}` : `−keywords on live ${touchMode}`);
      next = pushLog(
        next,
        gs.players[n].name,
        kw
          ? `${cardName}: live ${touchMode} loses ${kw}`
          : `${cardName}: live ${touchMode} keywords removed`,
      );
      break;
    }
    case "remove-power-buffs":
    case "remove-block-buffs": {
      // Strip queued pendingBuffs on the targeted player AND revert the live
      // slot card's value back to its printed value (since power-bonus /
      // block-bonus buffs are baked into adValueOverrides at play time).
      const targetPlayer = ownerN;
      const kindToFilter =
        row.effect === "remove-power-buffs" ? "power-bonus" : "block-bonus";
      const valueMode: AdMode =
        row.effect === "remove-power-buffs" ? "attack" : "defend";
      // Only revert if the live touch mode matches (so removing power buffs
      // off an opposing defend on the same slot — a meaningless combo —
      // doesn't accidentally clear the wrong override).
      const shouldRevert = touchMode === valueMode && zone != null;
      next = updatePlayer(next, targetPlayer, (p) => {
        const ovr = new Map(p.adValueOverrides);
        if (shouldRevert) ovr.delete(zone.id);
        return {
          ...p,
          pendingBuffs: p.pendingBuffs.filter((b) => b.effect.kind !== kindToFilter),
          adValueOverrides: ovr,
        };
      });
      buffSummary.push(
        shouldRevert
          ? `reverted live ${touchMode} to printed + cleared ${kindToFilter} buffs`
          : `cleared ${kindToFilter} buffs`,
      );
      next = pushLog(
        next,
        gs.players[n].name,
        shouldRevert
          ? `${cardName}: reverted ${gs.players[targetPlayer].name}'s live ${touchMode} to printed and cleared ${kindToFilter} buffs`
          : `${cardName}: cleared ${kindToFilter} buffs from ${gs.players[targetPlayer].name}`,
      );
      break;
    }
    case "stamina-cost":
      // Already validated out at the editor — stamina cost was paid when the
      // card hit the slot. Treat as no-op.
      break;
  }
  return next;
}

/** Add a granted keyword to a live combat slot. */
function addSlotGrantedKeyword(
  gs: GameState,
  slotId: string,
  side: "attack" | "defend",
  keyword: string,
): GameState {
  const slots = (gs.combatSlots ?? []).slice();
  const idx = slots.findIndex((s) => s.id === slotId);
  if (idx < 0) return gs;
  const s = slots[idx];
  if (side === "attack") {
    const list = Array.from(new Set([...(s.attackGrantedKeywords ?? []), keyword]));
    slots[idx] = { ...s, attackGrantedKeywords: list };
  } else {
    const list = Array.from(new Set([...(s.blockGrantedKeywords ?? []), keyword]));
    slots[idx] = { ...s, blockGrantedKeywords: list };
  }
  return { ...gs, combatSlots: slots };
}

/** Remove a granted keyword from a live combat slot. Empty keyword = clear all. */
function removeSlotGrantedKeyword(
  gs: GameState,
  slotId: string,
  side: "attack" | "defend",
  keyword: string,
): GameState {
  const slots = (gs.combatSlots ?? []).slice();
  const idx = slots.findIndex((s) => s.id === slotId);
  if (idx < 0) return gs;
  const s = slots[idx];
  const filterFn = (list: string[]): string[] => {
    if (!keyword) return [];
    const target = keyword.toLowerCase();
    return list.filter((k) => k.toLowerCase() !== target);
  };
  if (side === "attack") {
    slots[idx] = { ...s, attackGrantedKeywords: filterFn(s.attackGrantedKeywords ?? []) };
  } else {
    slots[idx] = { ...s, blockGrantedKeywords: filterFn(s.blockGrantedKeywords ?? []) };
  }
  return { ...gs, combatSlots: slots };
}

function summarizeRow(row: EffectRow): string {
  const sign = row.direction === "buff-self" ? "+" : "−";
  switch (row.effect) {
    case "power":
      return `${sign}${row.amount ?? 0} power`;
    case "block":
      return `${sign}${row.amount ?? 0} block`;
    case "stamina-cost":
      return row.direction === "buff-self"
        ? `−${row.amount ?? 0} cost`
        : `+${row.amount ?? 0} cost`;
    case "add-keyword": {
      const kw = row.keyword ?? "keyword";
      const amt = row.keywordAmount;
      return typeof amt === "number" && amt > 0 ? `+${kw} ${amt}` : `+${kw}`;
    }
    case "remove-keyword":
      return `−${row.keyword ?? "keyword"}`;
    case "remove-power-buffs":
      return `−power buffs`;
    case "remove-block-buffs":
      return `−block buffs`;
    case "stamina":
      return row.direction === "buff-self"
        ? `+${row.amount ?? 0} stamina`
        : `−${row.amount ?? 0} stamina`;
    case "draw":
      return row.direction === "buff-self"
        ? `draw ${row.amount ?? 0}`
        : `opp draws ${row.amount ?? 0}`;
    case "add-wear":
      return `+${row.amount ?? 0} wear`;
    case "remove-wear":
      return `−${row.amount ?? 0} wear`;
    case "exhaust":
      return `exhaust ${row.amount ?? 0} weapon${(row.amount ?? 0) === 1 ? "" : "s"}`;
    case "ready":
      return `ready ${row.amount ?? 0} weapon${(row.amount ?? 0) === 1 ? "" : "s"}`;
    case "search-deck":
      return `search deck (${row.searchFilter ?? "any"}, pick ${row.searchPickCount ?? 1})`;
    case "clear-stun":
      return `clear stun`;
  }
}

/** Counter Activated → "Keep Card". (xf) */
export function keepCounter(gs: GameState, n: 1 | 2, instanceId: string): GameState {
  trace.emit({
    category: "counter.keep",
    summary: `P${n} kept counter (${instanceId})`,
    round: gs.round,
    activePlayer: n,
    details: { player: n, instanceId },
  });
  return _keepCounterImpl(gs, n, instanceId);
}
function _keepCounterImpl(gs: GameState, n: 1 | 2, instanceId: string): GameState {
  const next = updatePlayer(gs, n, (p) => {
    const ids = new Set(p.counterKeptIds);
    ids.add(instanceId);
    return { ...p, counterKeptIds: ids };
  });
  return { ...next, counterActivatedDialog: null };
}

/** Counter Activated → "Discard & Draw". (yf) */
export function discardAndDrawCounter(gs: GameState, n: 1 | 2, instanceId: string): GameState {
  trace.emit({
    category: "counter.discard",
    summary: `P${n} discarded counter to draw 1 (${instanceId})`,
    round: gs.round,
    activePlayer: n,
    details: { player: n, instanceId },
  });
  return _discardAndDrawCounterImpl(gs, n, instanceId);
}
function _discardAndDrawCounterImpl(gs: GameState, n: 1 | 2, instanceId: string): GameState {
  let next = updatePlayer(gs, n, (p) => {
    const inst = p.handInstances.find((h) => h.id === instanceId);
    if (!inst) return p;
    const cleaned = removeFromHand(p, instanceId);
    return { ...cleaned, discard: [...cleaned.discard, inst.cardId] };
  });
  next = drawCards(next, n, 1);
  return { ...next, counterActivatedDialog: null };
}

// ─── pendingPlay confirm (Cl) ───────────────────────────────────────────────

/** Dispatch a confirmed pendingPlay. (Cl) */
export function confirmPendingPlay(gs: GameState, cardMap: Map<string, Card>): GameState {
  const pp = gs.pendingPlay;
  if (!pp) return gs;
  const n = pp.playerNum;

  // Stunned skip — stunned player's NEXT chance to play an attack OR a
  // defend is skipped, then stun clears. Armor-block, take-damage, and
  // utilities are NOT consumed by stun (per spec). Counter / pass / other
  // pendingPlay types are not gated either.
  const player = gs.players[n];
  const stunConsumingTypes: ReadonlyArray<typeof pp.type> = [
    "ad-attack",
    "ad-defend",
    "equip-attack",
    "equip-defend",
    "sig-attack",
    "sig-defend",
    // Quick Learner copies that resolve to attack/defend behave like normal
    // attacks/defends for stun purposes.
    "qlsig-ad-attack",
    "qlsig-ad-defend",
    "qlsig-equip-attack",
    "qlsig-equip-defend",
  ];
  if (player.stunned && stunConsumingTypes.includes(pp.type)) {
    let next = updatePlayer(gs, n, (p) => ({ ...p, stunned: false }));
    trace.emit({
      category: "status.stun.consume",
      label: "action-skipped",
      summary: `P${n} stun cleared (pending action ${pp.type} skipped)`,
      round: gs.round,
      activePlayer: n,
      details: { playerNum: n, pendingPlayType: pp.type, source: "continueAfterTakeDamage:action-skipped" },
    });
    next = pushLog(next, player.name, "Action skipped — Stun cleared");
    next = { ...next, pendingPlay: null };
    return next;
  }
  let next: GameState = {...gs, pendingPlay: null };

  // Phase 3 — Doubled Block routing: if this player has a pending
  // double-defend window open and is now playing a defend-side card, route
  // it through attachDoubleDefend instead of the normal defend path. The
  // doubleDefendPendingSlotId is cleared by attachDoubleDefend.
  const defendPlayTypes: ReadonlyArray<typeof pp.type> = [
    "ad-defend",
    "equip-defend",
    "sig-defend",
    "qlsig-ad-defend",
    "qlsig-equip-defend",
  ];
  if (
    gs.doubleDefendPendingSlotId &&
    gs.doubleDefendPlayerNum === n &&
    defendPlayTypes.includes(pp.type) &&
    pp.playCard
  ) {
    // Only ad-defend can be attached as the second defend (equip/sig defends
    // pull from different zones — keep them on the normal path for now).
    if (pp.type === "ad-defend") {
      // attachDoubleDefend also needs to consume the card from hand. The
      // existing playAttackDefend handles the hand removal + stamina pay; for
      // this 2nd-defend path we do it inline here before attaching.
      const player = next.players[n];
      const inst = player.handInstances.find((h) => h.id === pp.playCard!.id);
      if (inst) {
        next = updatePlayer(next, n, (p) => ({
          ...p,
          handInstances: p.handInstances.filter((h) => h.id !== pp.playCard!.id),
        }));
      }
      next = attachDoubleDefend(next, n, pp.playCard, cardMap);
      return next;
    }
  }

  // NOTE: do NOT increment player.attacksThisRound here. playAttackDefend
  // (and its equip / sig variants below) already increment it inside the
  // engine call. Doing it here too was the source of a double-count bug
  // where Crixus's 3rd-attack draw never fired because the counter jumped
  // 0 → 2 → 4 → 6 instead of 0 → 1 → 2 → 3.
  switch (pp.type) {
    case "ad-attack":
      if (pp.playCard) next = playAttackDefend(next, n, pp.playCard.id, "attack", cardMap);
      break;
    case "ad-defend":
      if (pp.playCard) next = playAttackDefend(next, n, pp.playCard.id, "defend", cardMap);
      break;
    case "equip-attack":
      if (typeof pp.slotIdx === "number") next = playEquip(next, n, pp.slotIdx, "attack", cardMap);
      break;
    case "equip-defend":
      if (typeof pp.slotIdx === "number") next = playEquip(next, n, pp.slotIdx, "defend", cardMap);
      break;
    case "sig-attack":
      if (typeof pp.slotIdx === "number") next = playSig(next, n, pp.slotIdx, "attack", cardMap);
      break;
    case "sig-defend":
      if (typeof pp.slotIdx === "number") next = playSig(next, n, pp.slotIdx, "defend", cardMap);
      break;
    case "sig-utility":
      if (typeof pp.slotIdx === "number") next = playSig(next, n, pp.slotIdx, null, cardMap);
      break;
    case "qlsig-ad-attack":
      if (typeof pp.slotIdx === "number") next = playQuickLearnerCopy(next, n, pp.slotIdx, "attack", cardMap);
      break;
    case "qlsig-ad-defend":
      if (typeof pp.slotIdx === "number") next = playQuickLearnerCopy(next, n, pp.slotIdx, "defend", cardMap);
      break;
    case "qlsig-utility":
      if (typeof pp.slotIdx === "number") next = playQuickLearnerCopy(next, n, pp.slotIdx, "utility", cardMap);
      break;
    case "qlsig-stance":
      if (typeof pp.slotIdx === "number") next = playQuickLearnerCopy(next, n, pp.slotIdx, "stance", cardMap);
      break;
    case "qlsig-equip-attack":
      if (typeof pp.slotIdx === "number") next = playQuickLearnerCopy(next, n, pp.slotIdx, "attack", cardMap);
      break;
    case "qlsig-equip-defend":
      if (typeof pp.slotIdx === "number") next = playQuickLearnerCopy(next, n, pp.slotIdx, "defend", cardMap);
      break;
    case "play-stance":
      if (pp.playCard) next = playStance(next, n, pp.playCard.id, cardMap);
      break;
    case "play-utility":
      if (pp.playCard) next = playUtility(next, n, pp.playCard.id, cardMap);
      break;
    case "armor-block":
      next = armorBlock(next, n, cardMap);
      break;
    case "take-damage":
      next = takeDamage(next, n, cardMap);
      break;
  }
  // While a reaction window is open, playing a card resets consecutivePasses
  // so the next button click is Pass (not Resolve). Priority stays with the
  // player who just played — they can chain another action or explicitly pass
  // to hand priority over. Excludes actions that create / close the chain.
  if (next.reactionChain && pp.type !== "ad-defend" && pp.type !== "equip-defend" &&
      pp.type !== "sig-defend" && pp.type !== "armor-block" && pp.type !== "take-damage" &&
      pp.type !== "ad-attack" && pp.type !== "sig-attack" && pp.type !== "equip-attack" &&
      // Quick Learner copy plays that resolve to attack/defend should be
      // treated like the underlying attack/defend (close / consume the chain),
      // not like generic plays.
      pp.type !== "qlsig-ad-attack" && pp.type !== "qlsig-ad-defend" &&
      pp.type !== "qlsig-equip-attack" && pp.type !== "qlsig-equip-defend") {
    next = {
      ...next,
      reactionChain: { ...next.reactionChain, consecutivePasses: 0, turn: n },
      priority: n,
    };
  }
  return next;
}

// ─── playability gates (UI helpers) ────────────────────────────────────────

export function canPlayAttack(gs: GameState, n: 1 | 2): boolean {
  return (
    gs.phase === "playing" &&
    gs.subPhase === "aggressor-pre-attack" &&
    gs.priority === n &&
    !gs.players[n].stunned
  );
}

export function canBlock(gs: GameState, n: 1 | 2): boolean {
  return gs.phase === "playing" && gs.subPhase === "defender-pre-block" && gs.priority === n;
}

/**
 * Find the most-recent unresolved combat slot where `n` is the defender.
 * Used by piercing detection on `n`'s armor / block options.
 */
export function getPendingAttackOn(gs: GameState, n: 1 | 2) {
  const slots = gs.combatSlots ?? [];
  for (let i = slots.length - 1; i >= 0; i--) {
    const s = slots[i];
    if (!s.resolved && s.attackerNum !== n) return s;
  }
  return null;
}

/** True if the active pending attack on `n` has the Piercing keyword. */
export function pendingAttackIsPiercing(
  gs: GameState,
  n: 1 | 2,
  cardMap: Map<string, Card>,
): boolean {
  const slot = getPendingAttackOn(gs, n);
  if (!slot) return false;
  const c = cardMap.get(slot.attackCard.cardId);
  if (!c) return false;
  if (hasKeyword(c.attackKeywords, "Piercing")) return true;
  if ((c.keywords ?? []).some((k) => k.toLowerCase() === "piercing")) return true;
  const granted = (slot as { attackGrantedKeywords?: string[] }).attackGrantedKeywords ?? [];
  return granted.some((k) => k.toLowerCase() === "piercing");
}

/** Armor can block iff it's the defender's window AND attack isn't Piercing. */
export function canArmorBlock(
  gs: GameState,
  n: 1 | 2,
  cardMap?: Map<string, Card>,
): boolean {
  if (!canBlock(gs, n) || !gs.players[n].armorActive) return false;
  if (cardMap && pendingAttackIsPiercing(gs, n, cardMap)) return false;
  return true;
}

export function canPlayUtilityFreeWindow(gs: GameState, n: 1 | 2): boolean {
  if (gs.freeReactionWindow) return true;
  return gs.phase === "playing" && gs.subPhase === "aggressor-pre-attack" && gs.priority === n;
}

/**
 * Authoritative legality check for playing a Utility card. Used by both:
 *   - UI (CardActionPopup.evalUtility) to enable/disable the Play button
 *   - Engine (playUtility) to reject illegal plays before any state change
 *
 * Returns { ok: true } when the card can be played right now, or
 * { ok: false, reason } otherwise. Checks (in order):
 *   1. Card is in `n`'s hand
 *   2. Stamina cost
 *   3. Subtype-specific window legality (Item / Maneuver / Reaction)
 *   4. Card-specific preconditions (e.g. Wound-Bind Cord requires <5 health)
 *
 * Reaction-subtype semantics (Engine #4): Reaction utilities are playable
 * any time the controller has priority AND any preconditions are met. They
 * are NOT restricted to reaction windows — the controller can choose to play
 * them in their own pre-attack window or as the action-chain reactor.
 */
export function canPlayUtilityNow(
  gs: GameState,
  n: 1 | 2,
  card: Card,
): { ok: boolean; reason?: string } {
  if (card.cardType !== "Utility") {
    return { ok: false, reason: "Not a utility card." };
  }
  const p = gs.players[n];
  const cost = staminaCostOf(card);
  if (p.stamina < cost) {
    return { ok: false, reason: `Not enough stamina (need ${cost}).` };
  }

  const subtype = (card.utilityFields?.utilityType ?? "").trim();
  const isActionChainReactor = gs.actionChain?.reactor === n;
  const hasPriority = gs.priority === n || isActionChainReactor;

  // Action Chain reactor window: ONLY Reaction-subtype utilities allowed.
  if (isActionChainReactor && subtype !== "Reaction") {
    return {
      ok: false,
      reason: "Action Chain — only Reaction utilities can be played.",
    };
  }

  if (subtype === "Item") {
    if (!hasPriority) return { ok: false, reason: "You don't have priority." };
    if (gs.subPhase !== "aggressor-pre-attack") {
      return { ok: false, reason: "Items play only during your attack window." };
    }
  } else if (subtype === "Reaction") {
    // Engine #4: Reaction-subtype — playable anywhere holder has priority
    // (their own pre-attack window, action chain as reactor, or a free
    // reaction window). Conditions still gate via preconditions below.
    if (!hasPriority && !gs.freeReactionWindow && gs.reactionChain == null) {
      return { ok: false, reason: "You don't have priority." };
    }
  } else if (subtype === "Maneuver") {
    // Maneuver (default / unspecified subtype): same as old gate.
    if (!hasPriority) return { ok: false, reason: "You don't have priority." };
    const inOwnPriorityWindow =
    gs.subPhase === "aggressor-pre-attack" ||
    gs.subPhase === "defender-pre-block";
  if (!inOwnPriorityWindow && !gs.freeReactionWindow) {
    return { ok: false, reason: "Only during your priority window." };
    }
  }

  // ── Card-specific preconditions (mirrors playUtility hardcoded checks) ──
  if (nameIs(card, "Wound-Bind Cord") && p.health >= 5) {
    return { ok: false, reason: "Wound-Bind Cord requires <5 health." };
  }
  if (nameIs(card, "Battle Adrenaline") && p.health > 5) {
    return { ok: false, reason: "Battle Adrenaline requires ≤5 health." };
  }
  if (nameIs(card, "Crushing Strength") && p.stamina < 4) {
    return { ok: false, reason: "Crushing Strength requires 4 stamina." };
  }
  if (nameIs(card, "Scar-Fueled Focus") && !p.tookDamageThisRound) {
    return {
      ok: false,
      reason: "Scar-Fueled Focus requires you to have taken damage this round.",
    };
  }
  if (nameIs(card, "Added Pressure")) {
    const slots = gs.combatSlots ?? [];
    const hasTarget = slots.some(
      (s) => !s.resolved && s.attackerNum === n && s.blockCard != null,
    );
    if (!hasTarget) {
      return {
        ok: false,
        reason: "Added Pressure — only legal after opponent plays a defend.",
      };
    }
  }

  return { ok: true };
}

// ─── pendingPrompt builders (used by SpecialPrompts) ───────────────────────

export function setPendingPrompt(gs: GameState, prompt: PendingPrompt | null): GameState {
  const before = gs.pendingPrompt;
  if (prompt && (!before || before.kind !== prompt.kind)) {
    trace.emit({
      category: "prompt.open",
      label: prompt.kind,
      summary: `prompt opened: ${prompt.kind}`,
      round: gs.round,
      activePlayer: (prompt as { playerNum?: 1 | 2 }).playerNum ?? gs.priority,
      details: { kind: prompt.kind, prompt, previousKind: before?.kind ?? null },
    });
  } else if (!prompt && before) {
    trace.emit({
      category: "prompt.resolve",
      label: before.kind,
      summary: `prompt resolved: ${before.kind}`,
      round: gs.round,
      activePlayer: (before as { playerNum?: 1 | 2 }).playerNum ?? gs.priority,
      details: { kind: before.kind, prompt: before },
    });
  } else if (prompt && before && before.kind === prompt.kind) {
    // Same-kind step transition (e.g. activate → choose)
    trace.emit({
      category: "prompt.open",
      label: `${prompt.kind}-step`,
      summary: `prompt step: ${prompt.kind}`,
      round: gs.round,
      activePlayer: (prompt as { playerNum?: 1 | 2 }).playerNum ?? gs.priority,
      details: { kind: prompt.kind, prompt, previous: before },
    });
  }
  return { ...gs, pendingPrompt: prompt };
}

export function setPendingPlay(gs: GameState, pp: PendingPlay | null): GameState {
  return { ...gs, pendingPlay: pp };
}

// ─── token activation (Expend) ─────────────────────────────────────────────

/**
 * Expend (activate) a token from the player's tokenArea.
 * - Focus token: spend 1 stamina, remove token, queue +2 power on next attack.
 * - Fortify token: spend 1 stamina, remove token, queue +2 block on next defend.
 * - Edge token: spend 1 stamina, remove token, draw 1 card.
 * - Other tokens: no-op (use Wc-style A/D activation via UI).
 */
export function expendToken(
  gs: GameState,
  n: 1 | 2,
  instanceId: string,
  cardMap: Map<string, Card>,
): GameState {
  const player = gs.players[n];
  const inst = player.tokenArea.find((t) => t.id === instanceId);
  if (!inst) return pushLog(gs, "system", "Token not found");
  const card = cardMap.get(inst.cardId);
  if (!card) return pushLog(gs, "system", "Token card not in registry");
  const cost = safeNum(card.tokenFields?.staminaCost ?? 1);
  if (player.stamina < cost) return pushLog(gs, "system", `⚠ Not enough stamina to expend ${card.name}`);

  let next = removeToken(gs, n, instanceId);
  next = updatePlayer(next, n, (p) => ({ ...p, stamina: p.stamina - cost }));

  if (nameIs(card, "Focus")) {
    next = queueBuff(
      next,
      n,
      makeBuff("Focus", "next-attack", { kind: "power-bonus", amount: 2 }, gs.round),
    );
    next = pushLog(next, player.name, "Expended Focus — next attack +2 power");
  } else if (nameIs(card, "Fortify")) {
    next = queueBuff(
      next,
      n,
      makeBuff("Fortify", "next-defend", { kind: "block-bonus", amount: 2 }, gs.round),
    );
    next = pushLog(next, player.name, "Expended Fortify — next defend +2 block");
  } else if (nameIs(card, "Edge")) {
    next = drawCards(next, n, 1);
    next = pushLog(next, player.name, "Expended Edge — drew a card");
  } else {
    next = pushLog(next, player.name, `Expended ${card.name}`);
  }
  // Reset reaction chain pass counter — expending a token counts as an action.
  // Priority stays with the player who acted; they pass explicitly to hand off.
  if (next.reactionChain) {
    next = {
      ...next,
      reactionChain: { ...next.reactionChain, consecutivePasses: 0, turn: n },
      priority: n,
    };
  }
  return next;
}

// ─── prompt resolution helpers ─────────────────────────────────────────────

/**
 * Quick Learner — confirm: replace the Quick Learner sig slot with the picked
 * card from the opponent's discard. The slot becomes playable any time during
 * the round (utility, attack, or defend depending on the picked card's type).
 * `inPlay` and `used` are reset so the player can play it normally from the
 * sig slot UI; the original Quick Learner stamina cost has already been paid.
 */
export function confirmQuickLearner(
  gs: GameState,
  pickedCardDbId: string,
  cardMap: Map<string, Card>,
): GameState {
  const pp = gs.pendingPrompt;
  if (!pp || pp.kind !== "quick-learner") return gs;
  const n = pp.playerNum;
  const slotIdx = pp.sigSlotIdx;
  const card = cardMap.get(pickedCardDbId);
  if (!card) return setPendingPrompt(gs, null);

  let next = updatePlayer(gs, n, (p) => {
    const slots = p.sigMoveSlots.slice();
    if (!slots[slotIdx]) return p;
    const oldCardId = slots[slotIdx].cardId;
    slots[slotIdx] = {
      ...slots[slotIdx],
      cardId: pickedCardDbId,
      inPlay: false,
      used: false,
      instanceId: uid("ql_"),
    };
    // Drop the Quick Learner copy from utilityInPlay / playedCards — the
    // utility-mode sig play queued copies of the original Quick Learner
    // cardId. Filter the most-recent matching entry out of each list.
    const dropOnce = <T extends { cardId: string }>(arr: T[]): T[] => {
      for (let i = arr.length - 1; i >= 0; i--) {
        if (arr[i].cardId === oldCardId) {
          return [...arr.slice(0, i), ...arr.slice(i + 1)];
        }
      }
      return arr;
    };
    return {
      ...p,
      sigMoveSlots: slots,
      utilityInPlay: dropOnce(p.utilityInPlay),
      playedCards: dropOnce(p.playedCards ?? []),
    };
  });
  trace.emit({
    category: "sig.replace",
    label: "quick-learner",
    summary: `P${n} sig slot ${slotIdx} replaced with ${card.name} (Quick Learner)`,
    round: gs.round,
    activePlayer: n,
    details: { playerNum: n, sigSlotIdx: slotIdx, newCardId: pickedCardDbId, newCardName: card.name, source: "quick-learner" },
  });
  next = pushLog(
    next,
    gs.players[n].name,
    `Quick Learner — sig slot replaced with ${card.name} (plays under its normal ${card.cardType} rules)`,
  );
  return setPendingPrompt(next, null);
}

/**
 * Quick Learner copy — play the picked card sitting in the sig slot as if it
 * were a normal play of its actual card type. The user explicitly wants the
 * copy to obey the source card's rules, costs, and play paths (A/D, Utility,
 * Stance, Equipment) — not to be re-skinned as a signature move.
 *
 * Implementation: synthesize a temporary hand instance carrying the picked
 * card's database id, run the matching underlying play function (which
 * already enforces all rules), then mark the sig slot as used so the copy
 * can't be replayed. The synthesized hand instance is consumed inside the
 * underlying play function, which also pushes the cardId to discard —
 * leaving the player with only the sig slot marked used afterward.
 *
 * Note: Quick Learner's own stamina cost was paid when the utility was first
 * played; the picked card pays its own normal cost via the underlying flow.
 */
export function playQuickLearnerCopy(
  gs: GameState,
  n: 1 | 2,
  slotIdx: number,
  mode: "attack" | "defend" | "utility" | "stance",
  cardMap: Map<string, Card>,
): GameState {
  const player = gs.players[n];
  const slot = player.sigMoveSlots[slotIdx];
  if (!slot || slot.used) return pushLog(gs, "system", "Sig slot unavailable");
  const card = cardMap.get(slot.cardId);
  if (!card) return pushLog(gs, "system", "Sig card not in registry");

  // Validate the requested mode against the picked card's actual type.
  const ct = card.cardType;
  const validForType =
    (ct === "Attack/Defend" && (mode === "attack" || mode === "defend")) ||
    (ct === "Utility" && mode === "utility") ||
    (ct === "Stance" && mode === "stance") ||
    (ct === "Equipment" && (mode === "attack" || mode === "defend")) ||
    (ct === "Signature Move");
  if (!validForType) {
    return pushLog(
      gs,
      "system",
      `Quick Learner copy: ${card.name} (${ct}) cannot be played as ${mode}`,
    );
  }

  // If the picked card is itself a Signature Move, fall through to the
  // normal sig-play pipeline (cardType matches what playSig expects).
  if (ct === "Signature Move") {
    return playSig(gs, n, slotIdx, mode === "utility" ? null : (mode as AdMode), cardMap);
  }

  // Synthesize a hand instance for the picked card's database id, then
  // dispatch into the matching play function. The play function consumes
  // the synthesized instance.
  const synthId = uid("qlh_");
  let next: GameState = updatePlayer(gs, n, (p) => ({
    ...p,
    handInstances: [...p.handInstances, { id: synthId, cardId: slot.cardId }],
  }));

  const beforeStamina = next.players[n].stamina;
  const beforeHandLen = next.players[n].handInstances.length;
  if (ct === "Attack/Defend") {
    next = playAttackDefend(next, n, synthId, mode as AdMode, cardMap);
  } else if (ct === "Utility") {
    next = playUtility(next, n, synthId, cardMap);
  } else if (ct === "Stance") {
    next = playStance(next, n, synthId, cardMap);
  } else if (ct === "Equipment") {
    // Equipment normally taps an equipped slot. For a Quick Learner copy of
    // an equipment card we do the closest equivalent: route it through the
    // A/D pipeline using equipment power/block/weight values. To avoid
    // duplicating playEquip wholesale, we treat the synthesized hand instance
    // as an A/D card by reading equipment fields; since playAttackDefend
    // reads attackDefendFields, equipment plays via this path will fall back
    // to whatever data is on the card. Simpler: refuse and explain —
    // equipment in opponent discard is rare and not worth the complexity.
    next = updatePlayer(next, n, (p) => ({
      ...p,
      handInstances: p.handInstances.filter((h) => h.id !== synthId),
    }));
    return pushLog(
      next,
      "system",
      `Quick Learner copy: equipment cards (${card.name}) are not playable from the sig slot. Pick a different card.`,
    );
  } else {
    // Champion / Armor / Token — not playable from sig slot via Quick Learner.
    next = updatePlayer(next, n, (p) => ({
      ...p,
      handInstances: p.handInstances.filter((h) => h.id !== synthId),
    }));
    return pushLog(
      next,
      "system",
      `Quick Learner copy: ${ct} cards cannot be played as a Quick Learner copy.`,
    );
  }

  // Detect whether the underlying play function actually consumed the
  // synthesized hand instance. If it did NOT (e.g. failed legality check
  // and pushed a system log only), do not mark the sig slot as used —
  // and clean up the synthesized instance ourselves.
  const afterPlayer = next.players[n];
  const stillHasSynth = afterPlayer.handInstances.some((h) => h.id === synthId);
  if (stillHasSynth) {
    next = updatePlayer(next, n, (p) => ({
      ...p,
      handInstances: p.handInstances.filter((h) => h.id !== synthId),
    }));
    return next; // legality failure already logged by underlying function
  }

  // Sanity check — if hand length didn't decrease as expected, bail without
  // consuming the slot. (Defensive; should never hit in normal flow.)
  if (afterPlayer.handInstances.length >= beforeHandLen) {
    return next;
  }
  // If the underlying function refunded stamina or did nothing (rare), still
  // proceed — success path is determined by the synth instance being gone.
  void beforeStamina;

  // Mark the sig slot as used so the copy can't be replayed.
  next = updatePlayer(next, n, (p) => {
    const slots = p.sigMoveSlots.slice();
    if (slots[slotIdx]) {
      slots[slotIdx] = { ...slots[slotIdx], used: true, inPlay: false };
    }
    return { ...p, sigMoveSlots: slots };
  });
  next = pushLog(
    next,
    gs.players[n].name,
    `Quick Learner copy played — ${card.name} resolved as ${ct}`,
  );
  return next;
}

/** Zeus' Wrathbrand — confirm: disarm selected opponent weapon slots. */
export function confirmZeusWrathbrand(
  gs: GameState,
  selectedSlots: number[],
  _cardMap: Map<string, Card>,
): GameState {
  const pp = gs.pendingPrompt;
  if (!pp || pp.kind !== "zeus-wrathbrand") return gs;
  const n = pp.playerNum;
  const oppNum = pp.oppNum;
  let next = updatePlayer(gs, oppNum, (p) => {
    const slots = p.equipSlots.slice();
    for (const idx of selectedSlots) {
      if (slots[idx]?.cardId) slots[idx] = { ...slots[idx], disarmed: true };
    }
    return { ...p, equipSlots: slots };
  });
  for (const idx of selectedSlots) {
    const slot = next.players[oppNum].equipSlots[idx];
    if (!slot?.cardId) continue;
    trace.emit({
      category: "equip.detach",
      label: "disarm",
      summary: `P${oppNum} weapon slot ${idx} disarmed (Zeus' Wrathbrand)`,
      round: gs.round,
      activePlayer: n,
      details: { playerNum: oppNum, source: "zeus-wrathbrand", slotIdx: idx, weaponCardId: slot.cardId },
    });
  }
  next = pushLog(
    next,
    gs.players[n].name,
    `Zeus' Wrathbrand — disarmed ${selectedSlots.length} weapon(s)`,
  );
  return setPendingPrompt(next, null);
}

/** Thrusting Release — confirm: exhaust chosen weapon, add (weaponPower - 2)
 *  to this attack's final power via adValueOverrides. Weapon power < 2 is
 *  clamped at 0 (no negative bonus). slotIdx === null aborts the bonus. */
export function confirmThrustingRelease(
  gs: GameState,
  slotIdx: number | null,
  cardMap: Map<string, Card>,
): GameState {
  const pp = gs.pendingPrompt;
  if (!pp || pp.kind !== "thrusting-release") return gs;
  const n = pp.playerNum;
  const adCopyId = pp.adCopyId;
  if (slotIdx === null || slotIdx === undefined) {
    let next = pushLog(gs, gs.players[n].name, "Thrusting Release — declined to exhaust a weapon");
    return setPendingPrompt(next, null);
  }
  const player = gs.players[n];
  const slot = player.equipSlots[slotIdx];
  if (!slot?.cardId || slot.tapped) {
    return setPendingPrompt(gs, null);
  }
  const wepCard = cardMap.get(slot.cardId);
  const weaponPower = safeNum(
    wepCard?.equipmentFields?.powerValue ?? wepCard?.attackDefendFields?.powerValue ?? 0,
  );
  const bonus = Math.max(0, weaponPower - 2);
  // Find the played AD instance copy and figure its current value
  const adEntry = player.attackDefendZone.find((e) => e.id === adCopyId);
  const adCard = adEntry ? cardMap.get(adEntry.cardId) : undefined;
  const baseAtkPower = safeNum(adCard?.attackDefendFields?.powerValue ?? 0);
  const currentOverride = player.adValueOverrides.get(adCopyId);
  const newPower = (currentOverride !== undefined ? currentOverride : baseAtkPower) + bonus;
  trace.emit({
    category: "status.exhaust",
    label: "thrusting-release",
    summary: `P${n} weapon slot ${slotIdx} exhausted (Thrusting Release → +${bonus} power)`,
    round: gs.round,
    activePlayer: n,
    details: { playerNum: n, slotIdx, weaponCardId: slot.cardId, weaponPower, bonus, source: "thrusting-release" },
  });
  let next = updatePlayer(gs, n, (p) => {
    const slots = p.equipSlots.slice();
    slots[slotIdx] = { ...slots[slotIdx], tapped: true };
    const ovr = new Map(p.adValueOverrides);
    ovr.set(adCopyId, Math.max(0, newPower));
    return { ...p, equipSlots: slots, adValueOverrides: ovr };
  });
  next = pushLog(
    next,
    gs.players[n].name,
    `Thrusting Release — exhausted weapon (power ${weaponPower}) for +${bonus} attack power`,
  );
  return setPendingPrompt(next, null);
}

/** confirmAddWeaponBlockBuffToNextDefend
 * Cards using this: Assisting Saber.
 * Player picks a weapon from their equipped slots; the next time they defend, 
 * they gain block equal to that weapon's block value.
 */
export function confirmAddBuffWeaponBlockToNextDefend(
  gs: GameState,
  selectedSlot: number | null,
  cardMap: Map<string, Card>,
): GameState {
  const pp = gs.pendingPrompt;
  if (!pp || pp.kind !== "pick-weapon") return gs;
  const n = pp.playerNum;
  if (selectedSlot === null || selectedSlot === undefined) {
    let next = pushLog(gs, gs.players[n].name, "Weapon Buff — declined to pick a weapon");
    return setPendingPrompt(next, null);
  }
  const player = gs.players[n];
  const slot = player.equipSlots[selectedSlot];
  if (!slot?.cardId || slot.disarmed) {
    return setPendingPrompt(gs, null);
  }
  const wepCard = cardMap.get(slot.cardId);
  const blockBonus = safeNum(wepCard?.equipmentFields?.blockValue ?? 0);
  let next = queueBuff(
    gs,
    n,
    makeBuff("Assisting Saber", "next-defend", { kind: "block-bonus", amount: blockBonus }, gs.round),
  );
  next = pushLog(
    next,
    gs.players[n].name,
    `Assisting Saber — picked ${wepCard?.name ?? "a weapon"} for +${blockBonus} block on next defend`,
  );
  trace.emit({
    category: "status.buff",
    label: "pick-weapon",
    summary: `P${n} picked weapon (block bonus: ${blockBonus})`,
    round: gs.round,
    activePlayer: n,
    details: { playerNum: n, slotIdx: selectedSlot, weaponCardId: slot.cardId, blockBonus, source: "pick-weapon" },
  }); 
  return setPendingPrompt(next, null);
}

/** Loadout weapon picker — apply exhaust/ready to selected slots. */
export function confirmLoadoutWeapons(
  gs: GameState,
  selectedSlots: number[],
  _cardMap: Map<string, Card>,
): GameState {
  const pp = gs.pendingPrompt;
  if (!pp || pp.kind !== "pick-loadout-weapons") return gs;
  const { playerNum, ownerNum, effect, cardName } = pp;
  const tapped = effect === "exhaust";
  let next = updatePlayer(gs, ownerNum, (p) => {
    const slots = p.equipSlots.slice();
    for (const idx of selectedSlots) {
      if (slots[idx]?.cardId) slots[idx] = { ...slots[idx], tapped };
    }
    return { ...p, equipSlots: slots };
  });
  const verb = effect === "exhaust" ? "exhausted" : "readied";
  const ownerLabel = ownerNum === playerNum ? "own" : "opponent's";
  next = pushLog(
    next,
    gs.players[playerNum].name,
    `${cardName} — ${verb} ${selectedSlots.length} ${ownerLabel} weapon(s)`,
  );
  return setPendingPrompt(next, null);
}

/** Fuel The Inferno — confirm: sacrifice N Focus tokens for N stamina. */
export function confirmFuelInferno(
  gs: GameState,
  focusToSpend: number,
  cardMap: Map<string, Card>,
): GameState {
  const pp = gs.pendingPrompt;
  if (!pp || pp.kind !== "fuel-inferno") return gs;
  const n = pp.playerNum;
  const player = gs.players[n];
  const have = focusTokenCount(player, cardMap);
  const spend = Math.max(0, Math.min(focusToSpend, have));
  let next = consumeFocus(gs, n, spend, cardMap);
  next = updatePlayer(next, n, (p) => ({ ...p, stamina: p.stamina + spend }));
  next = pushLog(
    next,
    player.name,
    `Fuel The Inferno — sacrificed ${spend} Focus, gained ${spend} stamina`,
  );
  return setPendingPrompt(next, null);
}

/** Bloodforged Plate — confirm: spend N Blood counters → next attack +N power. */
export function confirmBloodforgedPlate(
  gs: GameState,
  countersToSpend: number,
  _cardMap: Map<string, Card>,
): GameState {
  const pp = gs.pendingPrompt;
  if (!pp || pp.kind !== "bloodforged-activate") return gs;
  const n = pp.playerNum;
  const player = gs.players[n];
  const have = player.armorBloodCounters ?? 0;
  const spend = Math.max(0, Math.min(countersToSpend, have));
  if (spend <= 0) return setPendingPrompt(gs, null);

  let next = updatePlayer(gs, n, (p) => ({
    ...p,
    armorBloodCounters: (p.armorBloodCounters ?? 0) - spend,
  }));

  next = queueBuff(
    next,
    n,
    makeBuff(
      "Bloodforged Plate",
      "next-attack",
      { kind: "power-bonus", amount: spend },
      next.round,
    ),
  );

  next = pushLog(
    next,
    player.name,
    `Bloodforged Plate — spent ${spend} Blood counter${spend === 1 ? "" : "s"}, next attack +${spend} power`,
  );
  return setPendingPrompt(next, null);
}

/**
 * Confirm handler for Bloodied Edge's pick-damaging-attacks prompt.
 *
 * The user has picked up to N (= prompt.maxSelectable, currently 2) of
 * their own damage-dealing combat-slot ids from this round. We sum the
 * damage of those slots from the player's `damageDealtByAttackThisRound`
 * ledger and queue a +power buff on the next attack equal to that sum.
 *
 * Edge cases:
 *   - User skipped / selected fewer than 2: no buff queued, prompt dismissed.
 *   - User selected the same slotId twice: deduped (set semantics) so the
 *     buff can never double-count a single damage entry.
 *   - User selected a slotId not present in the ledger (shouldn't happen,
 *     but defensive): silently ignored.
 */
export function confirmBloodiedEdge(
  gs: GameState,
  selectedSlotIds: string[],
  _cardMap: Map<string, Card>,
): GameState {
  const pp = gs.pendingPrompt;
  if (!pp || pp.kind !== "pick-damaging-attacks") return gs;
  const n = pp.playerNum;
  const player = gs.players[n];
  const ledger = player.damageDealtByAttackThisRound ?? [];

  // Dedupe + clamp to maxSelectable, preserving the user's pick order.
  const seen = new Set<string>();
  const chosen: string[] = [];
  for (const id of selectedSlotIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    chosen.push(id);
    if (chosen.length >= pp.maxSelectable) break;
  }

  // Card text requires EXACTLY two attacks. Fewer than that = no buff,
  // just dismiss the prompt (the attack still resolves normally).
  if (chosen.length < pp.maxSelectable) {
    let dismissed = setPendingPrompt(gs, null);
    dismissed = pushLog(
      dismissed,
      player.name,
      `${pp.sourceCardName} — cancelled (no bonus queued).`,
    );
    return dismissed;
  }

  const totalDamage = chosen.reduce((sum, slotId) => {
    const entry = ledger.find((e) => e.slotId === slotId);
    return sum + (entry?.damage ?? 0);
  }, 0);

  if (totalDamage <= 0) {
    let dismissed = setPendingPrompt(gs, null);
    dismissed = pushLog(
      dismissed,
      player.name,
      `${pp.sourceCardName} — cancelled (selected attacks dealt 0 damage).`,
    );
    return dismissed;
  }

  let next = queueBuff(
    gs,
    n,
    makeBuff(
      pp.sourceCardName,
      "next-attack",
      { kind: "power-bonus", amount: totalDamage },
      gs.round,
    ),
  );
  const labels = chosen
    .map((id) => ledger.find((e) => e.slotId === id))
    .filter((e): e is NonNullable<typeof e> => !!e)
    .map((e) => `${e.attackCardName} (${e.damage})`)
    .join(" + ");
  next = pushLog(
    next,
    player.name,
    `${pp.sourceCardName} — next attack +${totalDamage} power (from ${labels}).`,
  );
  return setPendingPrompt(next, null);
}

// ─── exports for use by UI ─────────────────────────────────────────────────

/** Pick-discard-for-bonus resolver — used by The Final Straw. The hook
 *  queued a prompt with a `bonusPerCard` ratio and the slot it should
 *  affect. The player picks N hand instances to discard; each card grants
 *  `bonusPerCard` power (mode=attack) or block (mode=defend) on the live
 *  slot via adValueOverrides. */
export function confirmPickDiscardForBonus(
  gs: GameState,
  selectedInstanceIds: string[],
  cardMap: Map<string, Card>,
): GameState {
  const pp = gs.pendingPrompt;
  if (!pp || pp.kind !== "pick-discard-for-bonus") return gs;
  const n = pp.playerNum;
  const player = gs.players[n];

  // Dedupe and validate ids exist in the player's hand.
  const handIds = new Set(player.handInstances.map((h) => h.id));
  const seen = new Set<string>();
  const chosen: string[] = [];
  for (const id of selectedInstanceIds) {
    if (seen.has(id) || !handIds.has(id)) continue;
    seen.add(id);
    chosen.push(id);
  }

  // Zero discards = legal choice (no bonus, dismiss prompt cleanly).
  if (chosen.length === 0) {
    let dismissed = setPendingPrompt(gs, null);
    dismissed = pushLog(
      dismissed,
      player.name,
      `${pp.sourceCardName} — no cards discarded (no bonus).`,
    );
    return dismissed;
  }

  const bonusPerCard = Math.max(0, pp.bonusPerCard ?? 1);
  const totalBonus = chosen.length * bonusPerCard;

  // Resolve the AD-zone copy + card for the slot, so we can apply the
  // value override correctly via adjustInstanceValue.
  const zoneEntry = player.attackDefendZone.find((e) => e.id === pp.zoneCardId);
  const card = zoneEntry ? cardMap.get(zoneEntry.cardId) : undefined;

  // 1) Discard the chosen hand cards.
  let next = updatePlayer(gs, n, (p) => {
    const removed = p.handInstances.filter((h) => chosen.includes(h.id));
    const removedCardIds = removed.map((h) => h.cardId);
    const newHandInstances = p.handInstances.filter((h) => !chosen.includes(h.id));
    // Strip the matching cardIds from `hand` (1:1 by first occurrence).
    const newHand = p.hand.slice();
    for (const cid of removedCardIds) {
      const idx = newHand.indexOf(cid);
      if (idx !== -1) newHand.splice(idx, 1);
    }
    return {
      ...p,
      handInstances: newHandInstances,
      hand: newHand,
      discard: [...p.discard, ...removedCardIds],
    };
  });

  // 2) Apply the +bonus to the live slot value (attack=power, defend=block).
  if (card && totalBonus > 0) {
    next = adjustInstanceValue(
      next,
      n,
      pp.zoneCardId,
      card,
      pp.mode === "attack" ? "attack" : "defend",
      totalBonus,
    );
  }

  next = pushLog(
    next,
    player.name,
    `${pp.sourceCardName} — discarded ${chosen.length} card${chosen.length === 1 ? "" : "s"} for +${totalBonus} ${pp.mode === "attack" ? "power" : "block"}.`,
  );
  trace.emit({
    category: "prompt.confirm",
    label: "pick-discard-for-bonus",
    summary: `P${n} discarded ${chosen.length} for +${totalBonus} ${pp.mode}`,
    round: gs.round,
    activePlayer: n,
    details: {
      playerNum: n,
      sourceCardName: pp.sourceCardName,
      discardCount: chosen.length,
      bonusPerCard,
      totalBonus,
      mode: pp.mode,
      slotId: pp.slotId,
      zoneCardId: pp.zoneCardId,
    },
  });
  return setPendingPrompt(next, null);
}

export const _internal = {
  opp,
  shuffle,
  setPlayer,
  updatePlayer,
};

// ─── Active Combat reaction-window helpers ─────────────────────────────────

/**
 * Start a reaction chain after a block has been attached. Aggressor gets
 * priority first per original-zip behavior. Two consecutive Pass clicks
 * (without a card being played in between) will resolve the exchange.
 */
export function startReactionChain(gs: GameState): GameState {
  const aggressor = gs.aggressor ?? 1;
  trace.emit({
    category: "reaction.open",
    summary: `reaction window opened (aggressor=P${aggressor} acts first)`,
    round: gs.round,
    activePlayer: aggressor,
    details: {
      aggressor,
      priorPriority: gs.priority,
      combatSlots: (gs.combatSlots ?? []).length,
      unresolvedSlots: (gs.combatSlots ?? []).filter((s) => !s.resolved).length,
    },
  });
  return {
    ...gs,
    reactionChain: {
      turn: aggressor,
      consecutivePasses: 0,
      aggressorNum: aggressor,
    },
    priority: aggressor,
    subPhase: "aggressor-pre-attack",
  };
}

/**
 * Pass within an Active-Combat reaction window. Increments consecutivePasses;
 * when it reaches 2, the exchange auto-resolves (resolveCombat). Otherwise
 * priority flips to the other player.
 */
export function passReaction(
  gs: GameState,
  cardMap: Map<string, Card>,
): GameState {
  if (gs.winner) return gs;
  const chain = gs.reactionChain;
  if (!chain) return gs;
  const me = gs.priority;
  trace.emit({
    category: "reaction.pass",
    summary: `P${me} passed reaction (${chain.consecutivePasses + 1}/2)`,
    round: gs.round,
    activePlayer: me,
    details: {
      player: me,
      priorConsecutivePasses: chain.consecutivePasses,
      newConsecutivePasses: chain.consecutivePasses + 1,
      willResolveCombat: chain.consecutivePasses + 1 >= 2,
      aggressorNum: chain.aggressorNum,
    },
  });
  const next0 = pushLog(gs, gs.players[me].name, `${gs.players[me].name} passed reaction`);
  const passes = chain.consecutivePasses + 1;
  if (passes >= 2) {
    // Both passed back-to-back → resolve exchange.
    trace.emit({
      category: "reaction.close",
      label: "both-passed",
      summary: `reaction window closed — both passed, resolving combat`,
      round: gs.round,
      details: { reason: "two-consecutive-passes", aggressorNum: chain.aggressorNum },
    });
    let next: GameState = { ...next0, reactionChain: null };
    next = pushLog(next, "system", "✓ Both players passed — combat resolves");
    return resolveCombat(next, cardMap);
  }
  const other: 1 | 2 = me === 1 ? 2 : 1;
  trace.emit({
    category: "priority.shift",
    label: "reaction-flip",
    summary: `priority P${me} → P${other} (reaction window)`,
    round: gs.round,
    activePlayer: other,
    details: { from: me, to: other, reason: "reaction-pass", consecutivePasses: passes },
  });
  return {
    ...next0,
    reactionChain: { ...chain, turn: other, consecutivePasses: passes },
    priority: other,
  };
}

/**
 * Defender's "Take damage instead of blocking" path inside the popup.
 * Same as engineTakeDamage, but clears the reactionChain too.
 */
export function popupTakeDamage(
  gs: GameState,
  defenderNum: 1 | 2,
  cardMap: Map<string, Card>,
): GameState {
  return takeDamage({ ...gs, reactionChain: null }, defenderNum, cardMap);
}

// no-op export to make this a module if treeshaken
export const __PHASE_KEYS__: PlaytestPhase[] = [
  "coin-flip",
  "mulligan-p1",
  "mulligan-p2",
  "preparation",
  "playing",
  "recover",
  "planning-p1",
  "planning-p2",
  "finished",
];

// ─── Phase 3 prompt resolvers ───────────────────────────────────────────────

/**
 * confirmSearchDeckByFilter
 *
 * Generic tutor resolver: moves the selected deck instances into the player's
 * hand, optionally shuffles the remaining deck, clears the pending prompt, and
 * logs. Does NOT assume Chain semantics — any future search-deck-by-filter
 * prompt resolves through here.
 *
 * Called from PlaytestBoard.handleConfirmPrompt when result.kind === "search-deck-by-filter".
 */
export function confirmSearchDeckByFilter(
  gs: GameState,
  selectedInstanceIds: string[],
  cardMap: Map<string, Card>,
): GameState {
  const pp = gs.pendingPrompt;
  if (!pp || pp.kind !== "search-deck-by-filter") return gs;
  const n = pp.playerNum;
  const player = gs.players[n];

  // Validate: must be in candidates list and present in deckCards.
  const candidateSet = new Set(pp.candidates.map((c) => c.instanceId));
  const deckSet = new Set(player.deckCards);
  const seen = new Set<string>();
  const chosen: { instanceId: string; cardId: string }[] = [];
  for (const iid of selectedInstanceIds) {
    if (seen.has(iid) || !candidateSet.has(iid)) continue;
    // deckCards stores cardIds; candidates stores instanceId → cardId mapping
    const entry = pp.candidates.find((c) => c.instanceId === iid);
    if (!entry) continue;
    seen.add(iid);
    chosen.push(entry);
  }

  // Add the chosen cards to hand and remove their cardIds from deck.
  let next = updatePlayer(gs, n, (p) => {
    let deckCards = p.deckCards.slice();
    const newHandInstances = [...p.handInstances];
    const newHand = [...p.hand];
    for (const entry of chosen) {
      // Remove first occurrence of the cardId from deck (deck stores cardIds, not instance ids)
      const idx = deckCards.indexOf(entry.cardId);
      if (idx !== -1) deckCards.splice(idx, 1);
      // Add to hand
      newHand.push(entry.cardId);
      newHandInstances.push({ id: entry.instanceId, cardId: entry.cardId });
    }
    // Optionally shuffle the remaining deck.
    if (pp.shuffleAfter) {
      deckCards = shuffle(deckCards);
    }
    return {
      ...p,
      deckCards,
      hand: newHand,
      handInstances: newHandInstances,
      cardsDrawnThisRound: (p.cardsDrawnThisRound ?? 0) + chosen.length,
    };
  });

  const names = chosen
    .map((e) => cardMap.get(e.cardId)?.name ?? e.cardId)
    .join(", ");
  next = pushLog(
    next,
    player.name,
    `${pp.sourceCardName} — searched deck and added to hand: ${names || "(none)"}${pp.shuffleAfter ? " (deck shuffled)" : ""}.`,
  );
  trace.emit({
    category: "prompt.confirm",
    label: "search-deck-by-filter",
    summary: `P${n} tutored ${chosen.length} card(s) from deck`,
    round: gs.round,
    activePlayer: n,
    details: {
      playerNum: n,
      sourceCardName: pp.sourceCardName,
      chosen,
      shuffleAfter: pp.shuffleAfter,
    },
  });
  return setPendingPrompt(next, null);
}

/**
 * confirmPickHandDiscard
 *
 * Generic discard-from-hand resolver. Discards the selected hand instances,
 * clears the pending prompt, and logs. Drives Calculate Risk's "draw 2 then
 * discard 1" — but generic enough for any future pick-hand-discard effect.
 *
 * Called from PlaytestBoard.handleConfirmPrompt when result.kind === "pick-hand-discard".
 */
export function confirmPickHandDiscard(
  gs: GameState,
  selectedInstanceIds: string[],
  _cardMap: Map<string, Card>,
): GameState {
  const pp = gs.pendingPrompt;
  if (!pp || pp.kind !== "pick-hand-discard") return gs;
  const n = pp.playerNum;
  const player = gs.players[n];

  // Validate: must be in hand.
  const handIds = new Set(player.handInstances.map((h) => h.id));
  const seen = new Set<string>();
  const chosen: string[] = [];
  for (const id of selectedInstanceIds) {
    if (seen.has(id) || !handIds.has(id)) continue;
    seen.add(id);
    chosen.push(id);
  }

  let next = updatePlayer(gs, n, (p) => {
    const removed = p.handInstances.filter((h) => chosen.includes(h.id));
    const removedCardIds = removed.map((h) => h.cardId);
    const newHandInstances = p.handInstances.filter((h) => !chosen.includes(h.id));
    const newHand = p.hand.slice();
    for (const cid of removedCardIds) {
      const idx = newHand.indexOf(cid);
      if (idx !== -1) newHand.splice(idx, 1);
    }
    return {
      ...p,
      handInstances: newHandInstances,
      hand: newHand,
      discard: [...p.discard, ...removedCardIds],
    };
  });

  next = pushLog(
    next,
    player.name,
    `${pp.sourceCardName} — discarded ${chosen.length} card${chosen.length === 1 ? "" : "s"} from hand.`,
  );
  trace.emit({
    category: "prompt.confirm",
    label: "pick-hand-discard",
    summary: `P${n} discarded ${chosen.length} card(s) from hand`,
    round: gs.round,
    activePlayer: n,
    details: {
      playerNum: n,
      sourceCardName: pp.sourceCardName,
      discardCount: chosen.length,
    },
  });
  return setPendingPrompt(next, null);
}

// ─── Phase 3: Vengeance Strike resolver ────────────────────────────────────

/**
 * confirmVengeanceAttack
 *
 * Player accepted the "Pay X stamina to play Vengeance Strike as your attack"
 * prompt. Deducts the stamina cost, routes the card through the normal attack
 * play path (playAttackDefend) so counters + reactions work, then clears the
 * prompt.
 *
 * Called from PlaytestBoard.handleConfirmPrompt when result.kind === "vengeance-attack"
 * with accept === true.
 */
export function confirmVengeanceAttack(
  gs: GameState,
  cardMap: Map<string, Card>,
): GameState {
  const pp = gs.pendingPrompt;
  if (!pp || pp.kind !== "vengeance-attack") return gs;
  const n = pp.playerNum;
  const player = gs.players[n];

  // Validate stamina.
  if (player.stamina < pp.staminaCost) {
    return pushLog(gs, player.name, `${pp.sourceCardName} — not enough stamina (need ${pp.staminaCost}, have ${player.stamina}).`);
  }

  // Deduct stamina cost.
  let next = updatePlayer(gs, n, (p) => ({
    ...p,
    stamina: p.stamina - pp.staminaCost,
  }));
  next = setPendingPrompt(next, null);

  // Route through normal attack-play path. The card must still be in hand.
  const handInst = next.players[n].handInstances.find(
    (h) => h.id === pp.instanceId && h.cardId === pp.cardId,
  );
  if (!handInst) {
    return pushLog(next, player.name, `${pp.sourceCardName} — card no longer in hand (cannot activate vengeance).`);
  }

  next = pushLog(
    next,
    player.name,
    `${pp.sourceCardName} — Vengeance Strike! (paid ${pp.staminaCost} stamina, playing as attack)`,
  );
  trace.emit({
    category: "prompt.confirm",
    label: "vengeance-attack",
    summary: `P${n} activated Vengeance Strike (paid ${pp.staminaCost} stamina)`,
    round: gs.round,
    activePlayer: n,
    details: { playerNum: n, sourceCardName: pp.sourceCardName, staminaCost: pp.staminaCost, instanceId: pp.instanceId },
  });

  // Play as attack via the normal engine path.
  next = playAttackDefend(next, n, pp.instanceId, "attack", cardMap);
  return next;
}

/**
 * dismissVengeanceAttack — player declined the vengeance prompt. Just clears it.
 */
export function dismissVengeanceAttack(gs: GameState): GameState {
  const pp = gs.pendingPrompt;
  if (!pp || pp.kind !== "vengeance-attack") return gs;
  return setPendingPrompt(gs, null);
}

// ─── Phase 3: In-play Item Activation ──────────────────────────────────────

/**
 * activateItemCard
 *
 * Generic "activate this in-play Item" resolver. Looks up the card hook's
 * `onActivate` method and applies the result. Used by Sharpening Mill's
 * "Activate" button in the items panel / CardActionPopup.
 *
 * Guards:
 *  - Card must be present in `player.utilityInPlay`.
 *  - Card must have an `onActivate` hook in the registry.
 *  - The per-round once-flag is enforced by the hook itself (and optionally
 *    pre-checked here so the UI button can be disabled).
 *
 * @param n       Player number activating the item.
 * @param cardId  DB card id of the item to activate.
 * @param instanceId  The utilityInPlay instance id (used as the flagKey seed).
 */
export function activateItemCard(
  gs: GameState,
  n: 1 | 2,
  cardId: string,
  instanceId: string,
  cardMap: Map<string, Card>,
): GameState {
  const card = cardMap.get(cardId);
  if (!card) return gs;

  const hooks = getCardHooks(card);
  if (!hooks?.onActivate) {
    return pushLog(gs, gs.players[n].name, `${card.name} — no activate ability.`);
  }

  const ctx: CardHookCtx = {
    self: gs.players[n],
    opponent: gs.players[opp(n)],
    game: gs,
    card,
    instanceId,
  };
  const r = hooks.onActivate(ctx) ?? {};
  trace.emit({
    category: "hook.fire",
    label: "onActivate",
    summary: `${card.name} onActivate fired (P${n})`,
    round: gs.round,
    activePlayer: n,
    details: { card: card.name, hook: "onActivate", playerNum: n, instanceId, result: r as unknown as Record<string, unknown> },
  });
  return applyResults(gs, n, [r], cardMap);
}
